import React, { useState, useEffect, useMemo, useRef } from "react";
import { 
  Container, Typography, Box, Paper, TextField, Button, 
  Grid, Chip, IconButton, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, MenuItem, Alert,
  Switch, FormControlLabel, Select, FormControl, InputLabel, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, CircularProgress, ListItemText
} from "@mui/material";
import { collection, addDoc, getDocs, Timestamp, query, orderBy, limit, doc, updateDoc, where, deleteField } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "./firebase";
import { apiFetch } from "./utils/apiClient";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ImageIcon from '@mui/icons-material/Image';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import * as XLSX from "xlsx-js-style";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import UploadPreview, { appendPickedFiles } from "./components/UploadPreview";
import { isWallpaper, wallpaperPrintCost } from "./utils/wallpaper";

// 🔒 مدة قفل زر "مطابقة الحسابات" بعد آخر مطابقة تسجّلها بنين
const RECONCILE_LOCK_MS = 12 * 60 * 60 * 1000;

const API_BASE =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5000/api"
    : "https://artikaorder-backend.onrender.com/api";

const MySwal = withReactContent(Swal);

const EXPENSE_CATEGORIES = [
  "أجور عمال",
  "اتصالات عامة",
  "اتصالات عامة - التزام الأستاذ مروان رسام",
  "استئجار مباني وانشاءات",
  "الدائنون / السيد مروان",
  "القرطاسية",
  "اللوازم والمهمات",
  "المخزون",
  "الوقود والزيوت",
  "خدمات مصرفية",
  "دعاية واعلان",
  "رواتب واجور",
  "سلف المنتسبين",
  "صيانة أجهزة كهربائية",
  "صيانة سيارات",
  "ضيافة",
  "طعام",
  "كلفة البضاعة المباعة",
  "كلفة بضاعة مباعة - إطارات",
  "كلفة بضاعة مباعة - طباعة",
  "كلفة بضاعة مباعة - تعبئة وتغليف",
  "كلفة البضاعة المباعة - مواد وسم",
  "كلفة البضاعة المباعة - خشب",
  "مدينون تعبئة وتغليف",
  "مسحوبات شخصية",
  "مصاريف / آلات ومعدات",
  "مصاريف اتصالات",
  "مصاريف إيجار",
  "مصاريف تأسيس",
  "مصاريف ضيافة",
  "مصاريف عرضية",
  "مصاريف قانونية واستشارية",
  "مصاريف قرطاسية",
  "مصاريف كساوي",
  "مصاريف لوازم",
  "مصاريف مدفوعة مقدماً",
  "مصاريف نقل وإيفاد",
  "مصروف عمولات بيع",
  "مصروفات خدمية متنوعة",
  "مكافأة",
  "موجودات غير ملموسة",
  "الموجودات الثابتة / أثاث وأجهزة مكاتب",
  "الموجودات الثابتة / أجهزة كهربائية",
  "الموجودات الثابتة / تبريد وتكييف",
  "الموجودات الثابتة / حاسبات الكترونية",
  "الموجودات الثابتة / لوازم",
  "صيانة الموجودات الثابتة / حاسبات الكترونية",
  "نفقات إيرادية مؤجلة / مصاريف تأسيس اربيل",
  "نفقات إيرادية مؤجلة / مصاريف تأسيس / نفقات إيرادية مؤجلة بغداد",
  "نقل سلع وخدمات",
  "نقل موظفين",
  "نقل لوحات - شركة توصيل",
  "مصاريف أخرى"
];

const INCOME_CATEGORIES = [
  "إيرادات مبيعات",
  "دفعة من عميل",
  "إيرادات أخرى"
];

const ALL_ACCOUNTING_CATEGORIES = Array.from(
  new Set([...INCOME_CATEGORIES, ...EXPENSE_CATEGORIES])
);

const ENTRY_TAB_OPTIONS = ["قبض", "صرف", "حساب آجل", "حساب موظف", "تحويل داخلي"];
const WALLET_OPTIONS = ["baghdad", "erbil", "zain", "fib", "qi"];
const numberToArabicWords = (num) => {
  num = Math.floor(Number(num || 0));

  if (num === 0) return "صفر";

  const ones = [
    "", "واحد", "اثنان", "ثلاثة", "أربعة", "خمسة",
    "ستة", "سبعة", "ثمانية", "تسعة", "عشرة",
    "أحد عشر", "اثنا عشر", "ثلاثة عشر", "أربعة عشر", "خمسة عشر",
    "ستة عشر", "سبعة عشر", "ثمانية عشر", "تسعة عشر"
  ];

  const tens = [
    "", "", "عشرون", "ثلاثون", "أربعون", "خمسون",
    "ستون", "سبعون", "ثمانون", "تسعون"
  ];

  const hundreds = [
    "", "مائة", "مائتان", "ثلاثمائة", "أربعمائة", "خمسمائة",
    "ستمائة", "سبعمائة", "ثمانمائة", "تسعمائة"
  ];

  const convertLessThan1000 = (n) => {
    let words = [];

    const h = Math.floor(n / 100);
    const rest = n % 100;

    if (h) words.push(hundreds[h]);

    if (rest) {
      if (rest < 20) {
        words.push(ones[rest]);
      } else {
        const one = rest % 10;
        const ten = Math.floor(rest / 10);

        if (one) {
          words.push(`${ones[one]} و${tens[ten]}`);
        } else {
          words.push(tens[ten]);
        }
      }
    }

    return words.join(" و ");
  };

  const parts = [];

  const millions = Math.floor(num / 1000000);
  const thousands = Math.floor((num % 1000000) / 1000);
  const rest = num % 1000;

  if (millions) {
    if (millions === 1) parts.push("مليون");
    else if (millions === 2) parts.push("مليونان");
    else parts.push(`${convertLessThan1000(millions)} مليون`);
  }

  if (thousands) {
    if (thousands === 1) parts.push("ألف");
    else if (thousands === 2) parts.push("ألفان");
    else parts.push(`${convertLessThan1000(thousands)} ألف`);
  }

  if (rest) {
    parts.push(convertLessThan1000(rest));
  }

  return parts.join(" و ");
};

const amountToArabicIQD = (amount) => {
  return `${numberToArabicWords(amount)} دينار عراقي فقط لا غير`;
};

const getWalletLabelForVoucher = (walletValue) => {
  if (walletValue === "baghdad") return "كاش";
  if (walletValue === "erbil") return "كاش";
  if (walletValue === "zain") return "زين كاش";
  if (walletValue === "fib") return "FIB";
  if (walletValue === "qi") return "كي كارد";
  return "كاش";
};

const CREDIT_ACCOUNTS = [
  "مطبعة صدى سومر - أبو زينب",
  "إطارات توب فريم",
  "إطارات محمد الخفاجي",
  "مطبعة الوان - حجي محمد",
  "مطبعة",
  "صاج الريف",
  "الريف كافيه اربيل",
  "الريف كافيه بغداد",
  "ايجار سيارة",
  " حسابات اخرى ",
  "حساب دكتور حسين",
  "    عمولة المبيعات",
];
const INVOICE_COST_ACCOUNTS = [
  "مطبعة صدى سومر 2",
  "إطارات توب فريم 2",
];
// حسابات آجلة محددة (بطلب المستخدم) يظهر فيها مجموع عمود "المبلغ" بديالوج
// تفاصيل الحساب، فوق يسار (يم زر الإكسل) - باقي الحسابات الآجلة ما تتأثر
const CREDIT_ACCOUNTS_WITH_AMOUNT_TOTAL = [
  "مطبعة صدى سومر - أبو زينب",
  "إطارات توب فريم",
];
// ✅ اسم العرض فقط — المعرّف الداخلي (invoiceCostAccountName بقاعدة البيانات) يبقى كما هو
const INVOICE_COST_ACCOUNT_LABELS = {
  "مطبعة صدى سومر 2": "كلفة الطباعة",
  "إطارات توب فريم 2": "كلفة الإطارات",
};
const getInvoiceCostAccountLabel = (name) =>
  INVOICE_COST_ACCOUNT_LABELS[name] || name;

// ✅ نفس الدوال الحرفية المستخدمة بتصدير إكسل الفواتير (InvoiceListPage.jsx:
// parseSize/calculateCostBreakdown) - منسوخة هنا بالضبط (بدون أي تعديل على
// InvoiceListPage.jsx) حتى تبقى "حسابات كلفة الفواتير" مطابقة تماماً لإكسل
// الفواتير، وتشمل كل الفواتير منذ أول استخدام للنظام وليس فقط الفواتير
// المعتمدة بعد إضافة ميزة الترحيل التلقائي لسستم الحسابات (كانت السبب
// بظهور بيانات من شهر 6 فقط سابقاً)
const ceilToStepForCost = (value, step) => Math.ceil(value / step) * step;

const parseSizeForCost = (sizeKey) => {
  if (!sizeKey) return { width: 0, height: 0 };
  const cleanSize = sizeKey.replace("×", "x").replace(/\s/g, "");
  const [width, height] = cleanSize.split("x").map(Number);
  return { width: Number(width || 0), height: Number(height || 0) };
};

const calculateCostBreakdownForAccounting = (item) => {
  const { width, height } = parseSizeForCost(item.sizeKey);

  if (width <= 0 || height <= 0) {
    return { printCost: 0, innerWoodCost: 0, frameCost: 0, totalCost: 0 };
  }

  // 🧻 ورق جدران - نفس معادلة إكسل الفواتير (utils/wallpaper.js)
  if (isWallpaper(item)) {
    const wallpaperCost = wallpaperPrintCost(item, width, height);
    return { printCost: wallpaperCost, innerWoodCost: 0, frameCost: 0, totalCost: wallpaperCost };
  }

  const printCost =
    ceilToStepForCost((height / 100 + 0.05) * (width / 100 + 0.05), 0.2) * 25000;

  const baseDim = Math.ceil((height / 100 + width / 100) * 2 + 0.5);

  const innerWoodCost =
    item.frameOption === "إطار داخلي فقط" || item.frameOption === "إطار كامل"
      ? baseDim * 2000
      : 0;

  let frameCost = 0;
  if (item.frameOption === "إطار كامل") {
    if (item.frameType === "L") frameCost = baseDim * 5500;
    if (item.frameType === "Kas") frameCost = baseDim * 7500;
  }

  return {
    printCost,
    innerWoodCost,
    frameCost,
    totalCost: printCost + innerWoodCost + frameCost,
  };
};

// ✅ نفس منطق كشف/إزالة التكرار المستخدم بـ InvoiceListPage.jsx (getDuplicateKey)
// - أحياناً تتولد أكثر من وثيقة "invoices" لنفس الفاتورة (نفس رقم الفاتورة)،
// وبدونه كانت "حسابات كلفة الفواتير" تحسب هذه الفاتورة مرتين فتختلف عن إكسل
// الفواتير المعتمدة (اللي أصلاً يستبعد هذا التكرار)
const normalizeTextForCost = (str) =>
  String(str || "").trim().toLowerCase().replace(/\s+/g, " ");

const getInvoiceDuplicateKeyForCost = (inv) => {
  const number = normalizeTextForCost(inv.invoiceNumber);
  if (number) return `num:${number}`;
  return `fallback:${normalizeTextForCost(inv.customerName)}__${inv.lastFinalTotal ?? ""}__${inv.lastVersionNumber ?? ""}`;
};
const EMPLOYEE_ACCOUNTS = [
  "نرسي",
  "بنين",
  "برهان",
  "عبد الرحمن",
  "محمد",
  "طه",
  "مياسة",
  "أنوار",
];
// ✅ الأوفر تايم صار جزء من "إدارة الموظفين" (LeaveAdmin.jsx) مع باقي
// حركات الموظف (إجازات/غياب) بدل ما يكون هنا منفصل - راجعي LeaveAdmin.jsx
// بداية احتساب "المبيعات الكلية" بصفحة الحسابات - من 15-5-2026 وتستمر لين
// اليوم (تاريخ متحرك)، مو من بداية السستم
const SALES_TOTAL_START_DATE = new Date(2026, 4, 15); // 15 مايس 2026

// ✅ نفس "staticTotals" المستخدمة بزر "الطلبات الكلية" بواجهة المدير (لما
// فلتر السنة = "الكل") - مجموع مبيعات + ماركتنك للأشهر القديمة الثابتة
// (قبل ما تنسجل الطلبات على Firebase)، تُضاف دائماً بغض النظر عن مدى
// التاريخ المختار - نفس السلوك بالضبط بواجهة المدير (AdminDashboard.js،
// staticOldMonths + staticTotals)
const STATIC_LEGACY_TOTAL = 32183000;

// ✅ نفس toDateSafe المستخدمة بواجهة المدير (AdminDashboard.js) - لازم نتعامل
// مع كل الأشكال المحتملة لتاريخ createdAt (Timestamp / {seconds} / {_seconds}
// / Date / نص / رقم)، وإلا تنطرح طلبات كثيرة من الحساب بالغلط وينتج فرق كبير
// عن رقم واجهة المدير
const toDateSafe = (ts) => {
  try {
    if (!ts) return null;
    if (typeof ts.toDate === "function") return ts.toDate();
    if (typeof ts.seconds === "number") return new Date(ts.seconds * 1000);
    if (typeof ts._seconds === "number") return new Date(ts._seconds * 1000);
    if (ts instanceof Date) return ts;
    if (typeof ts === "string" || typeof ts === "number") {
      const d = new Date(ts);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
  } catch {
    return null;
  }
};

const MONTH_NAMES_AR = [
  "كانون الثاني",
  "شباط",
  "آذار",
  "نيسان",
  "أيار",
  "حزيران",
  "تموز",
  "آب",
  "أيلول",
  "تشرين الأول",
  "تشرين الثاني",
  "كانون الأول",
];

const getSafeJsDate = (item) => {
  const d =
    item?.date?.toDate?.() ||
    item?.timestamp?.toDate?.() ||
    item?.createdAt?.toDate?.() ||
    null;

  return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
};

const getMonthKey = (date) => {
  if (!(date instanceof Date)) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const getMonthLabelFromKey = (monthKey) => {
  if (!monthKey) return "-";

  const [year, month] = monthKey.split("-");
  const monthIndex = Number(month) - 1;

  if (monthIndex < 0 || monthIndex > 11) return monthKey;

  return `${MONTH_NAMES_AR[monthIndex]} ${year}`;
};

const MONTHLY_EXPENSE_BOXES = [
  {
    key: "printingCOGS",
    label: "كلفة بضاعة مباعة - طباعة",
    color: "#8e24aa",
    matches: (record) => {
      const category = String(record.accountingCategory || "");
      return category === "كلفة بضاعة مباعة - طباعة";
    },
  },
  {
    key: "tiresCOGS",
    label: "كلفة بضاعة مباعة - إطارات",
    color: "#5e35b1",
    matches: (record) => {
      const category = String(record.accountingCategory || "");
      return category === "كلفة بضاعة مباعة - إطارات";
    },
  },
  {
    key: "materialsCOGS",
    label: "كلفة البضاعة المباعة - مواد وسم",
    color: "#00acc1",
    matches: (record) => {
      const category = String(record.accountingCategory || "");
      return category === "كلفة البضاعة المباعة - مواد وسم";
    },
  },
  {
    key: "woodCOGS",
    label: "كلفة البضاعة المباعة - خشب",
    color: "#6d4c41",
    matches: (record) => {
      const category = String(record.accountingCategory || "");
      return category === "كلفة البضاعة المباعة - خشب";
    },
  },
  {
    key: "taxi",
    label: "تكسيات",
    color: "#ef6c00",
    matches: (record) => {
      const category = String(record.accountingCategory || "");
      return category === "نقل موظفين";
    },
  },
  {
    key: "boardsTransport",
    label: "أجور نقل لوحات",
    color: "#00897b",
    matches: (record) => {
      const category = String(record.accountingCategory || "");
      return category === "نقل سلع وخدمات";
    },
  },
  {
    key: "boardsTransportDeliveryCompany",
    label: "نقل لوحات - شركة توصيل",
    color: "#0277bd",
    matches: (record) => {
      const category = String(record.accountingCategory || "");
      return category === "نقل لوحات - شركة توصيل";
    },
  },
  {
    key: "generalCommunications",
    label: "اتصالات عامة",
    color: "#3949ab",
    matches: (record) => {
      const category = String(record.accountingCategory || "");
      return category === "اتصالات عامة" || category === "مصاريف اتصالات";
    },
  },
];
const getCreditActionLabel = (action) => {
  if (action === "add_invoice") return "إضافة فاتورة آجلة";
  if (action === "payment") return "تسديد حساب آجل";
  if (action === "discount") return "خصم على الحساب";
  return "-";
};

// نسخة حرفية من getOrderRemainingAmount بـaccountingController.js وبـ
// functions/index.js - يجب إبقاؤها متطابقة يدوياً عند أي تعديل هناك (نفس
// نمط باقي الثوابت المكرَّرة بالملف). يحسب المبلغ المتبقي غير المحصَّل من
// طلب واحد (دفع عند الاستلام لسه ما وصل، أو باقي دفعة مقدمة لسه ما انسدد).
const getOrderRemainingAmount = (order) => {
  if (order.remainingCollected) return 0;
  if (order.excludedFromStats) return 0;
  if ((order.saleAccount || "بيع") === "ماركتنك") return 0;
  const method = order.payment?.method;
  const price = Number(order.price || 0);
  if (method === "cash_on_delivery") return price;
  if (method === "deposit" || method === "deposit_cash") {
    return Math.max(price - Number(order.payment?.depositAmount || 0), 0);
  }
  return 0;
};

const PAYMENT_METHOD_LABELS = {
  full_online: "دفع كامل أونلاين",
  full_cash: "دفعة كاملة كاش",
  deposit: "دفعة أولية",
  deposit_cash: "دفعة أولية كاش",
  cash_on_delivery: "دفع عند الاستلام",
};

const StatCard = ({ label, value, suffix = "", color, onClick, subtitle, footerRows }) => (
  <Paper
    onClick={onClick}
    elevation={0}
    sx={{
      p: 2,
      height: "100%",
      borderRadius: 3,
      bgcolor: color,
      color: "#fff",
      cursor: onClick ? "pointer" : "default",
      transition: "transform 0.15s ease, box-shadow 0.15s ease",
      ...(onClick && {
        "&:hover": { transform: "translateY(-3px)", boxShadow: 6 },
      }),
    }}
  >
    <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 600, display: "block" }}>
      {label}
    </Typography>

    <Typography variant="h6" fontWeight="900" sx={{ mt: 0.5 }}>
      {Number(value || 0).toLocaleString()}{suffix}
    </Typography>

    {subtitle && (
      <Typography variant="caption" sx={{ opacity: 0.75, display: "block" }}>
        {subtitle}
      </Typography>
    )}

    {footerRows && footerRows.length > 0 && (
      <Box sx={{ borderTop: "1px dashed rgba(255,255,255,0.35)", mt: 1, pt: 1 }}>
        {footerRows.map((row) => (
          <Box
            key={row.monthKey || row.label}
            sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.4 }}
          >
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {row.label}
            </Typography>
            <Typography variant="caption" fontWeight="bold">
              {Number(row.value || 0).toLocaleString()}
            </Typography>
          </Box>
        ))}
      </Box>
    )}
  </Paper>
);

const SmartAccountingSystem = () => {
  const currentUser = localStorage.getItem("username");
  const isBaneen = currentUser === "Baneen@123";
  const isAdmin = currentUser === "admin";

  // 🔄 مطابقة الحسابات (يومياً بنهاية الدوام)
  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false);
  const [reconcileMode, setReconcileMode] = useState(null); // null | "unmatched"
  const [reconcileReason, setReconcileReason] = useState("");
  const [reconcileSaving, setReconcileSaving] = useState(false);
  // قفل فوري (مو state) - الضغطة الثانية السريعة كانت تشوف reconcileSaving
  // القديمة (false) قبل إعادة الرسم فتنحفظ مطابقتين
  const reconcileSavingRef = useRef(false);

  // 🧾 كلفة الفواتير محسوبة من كل تاريخ الفواتير المعتمدة (وليس فقط
  // الفواتير المرحّلة تلقائياً لسستم الحسابات بعد شهر 6)
  const [invoiceCostHistoryTotals, setInvoiceCostHistoryTotals] = useState({
    "مطبعة صدى سومر 2": 0,
    "إطارات توب فريم 2": 0,
  });
  // سجلات تفصيلية (فاتورة فاتورة) لنفس الحسابين، لعرضها بنافذة التفاصيل
  const [invoiceCostHistoryRecords, setInvoiceCostHistoryRecords] = useState({
    "مطبعة صدى سومر 2": [],
    "إطارات توب فريم 2": [],
  });
  const [invoiceCostHistoryLoading, setInvoiceCostHistoryLoading] = useState(false);

  const [isIncome, setIsIncome] = useState(false);
  const [isInternalTransfer, setIsInternalTransfer] = useState(false); // الميزة الجديدة لـ سويتش التحويل الداخلي
  const [transferTo, setTransferTo] = useState("baghdad"); // الصندوق المستهدف للتحويل
const [isCreditAccountMode, setIsCreditAccountMode] = useState(false);
const [creditAction, setCreditAction] = useState("add_invoice");
// add_invoice | payment | discount

const [creditAccountName, setCreditAccountName] = useState("");
const [isEmployeeAccountMode, setIsEmployeeAccountMode] = useState(false);
const [employeeAccountName, setEmployeeAccountName] = useState("");
  const [processName, setProcessName] = useState("");
  const [currency, setCurrency] = useState("IQD"); 
  const [amount, setAmount] = useState("");
  const [exchangeRate, setExchangeRate] = useState(1500);
  const [manualCategory, setManualCategory] = useState("");
  const [receiptNumber, setReceiptNumber] = useState(""); 
  const [location, setLocation] = useState("بغداد"); 
  const [note, setNote] = useState("");
 const [files, setFiles] = useState([]);
 const [existingInvoiceUrls, setExistingInvoiceUrls] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [records, setRecords] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [orders, setOrders] = useState([]);
  // 🔗 ربط قيد قبض بطلب/طلبات (باقي دفعة مقدمة أو تحصيل شركة توصيل) -
  // متاح فقط لقيد قبض جديد (مو تعديل)، راجع getOrderRemainingAmount أسفل الملف.
  const [showOrderLinking, setShowOrderLinking] = useState(false);
  const [linkedOrderIds, setLinkedOrderIds] = useState([]);
  // ✅ طلبات "المبيعات الكلية" - تنجاب من نفس API الباك اند اللي تستخدمه
  // واجهة المدير (admin-dashboard/base-data) بدل قراءة Firestore مباشرة،
  // لأن قراءة orders مباشرة من العميل تعتمد على صلاحيات Firestore وممكن
  // ترجع جزء بس من الطلبات (لاحظنا فرق كبير عن رقم واجهة المدير الحقيقي)
  const [salesTotalOrders, setSalesTotalOrders] = useState([]);
  const [wallet, setWallet] = useState("baghdad");
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
const [filterFromDate, setFilterFromDate] = useState("");
const [filterToDate, setFilterToDate] = useState("");
// ✅ فلاتر تعدد اختيار (checkbox) - مصفوفة فاضية تعني "الكل" بدون أي استبعاد
const [filterEntryTab, setFilterEntryTab] = useState([]);
const [filterAccountingCategory, setFilterAccountingCategory] = useState([]);
const [filterWallet, setFilterWallet] = useState([]);
const [filterDescriptionSearch, setFilterDescriptionSearch] = useState("");
const [creditDetailsOpen, setCreditDetailsOpen] = useState(false);
const [selectedCreditAccount, setSelectedCreditAccount] = useState("");
const [walletDetailsOpen, setWalletDetailsOpen] = useState(false);
const [selectedWalletKey, setSelectedWalletKey] = useState("");
const [expenseDetailsOpen, setExpenseDetailsOpen] = useState(false);
const [selectedExpenseKey, setSelectedExpenseKey] = useState("");
const [showForBaneen, setShowForBaneen] = useState(true);
const [voucherDialogOpen, setVoucherDialogOpen] = useState(false);
const [voucherRecordId, setVoucherRecordId] = useState(null);
const [voucherType, setVoucherType] = useState("قبض");

const [voucherData, setVoucherData] = useState({
  voucherNumber: 1,
  date: new Date().toISOString().split("T")[0],
  personName: "",
  amount: 0,
  amountWords: amountToArabicIQD(0),
  reason: "",
  paymentMethod: "كاش",
  invoiceNumber: "",
  receivedBy: "",
  notes: "",
});



  useEffect(() => {
    fetchRecords();
    fetchOrders();
    // "حسابات كلفة الفواتير" تظهر فقط للمدير - لا داعي لهذا الاستعلام
    // الإضافي (يمر على كل الفواتير) بجلسة بنين
    if (currentUser !== "Baneen@123") fetchInvoiceCostHistory();
    // "المبيعات الكلية" (admin فقط) و"الباقي المتوقع تحصيله من الزبائن" (كل
    // الحسابات ما عدا بنين، راجع expectedReceivableStats أسفل الملف) يعتمدان
    // نفس مصدر بيانات الطلبات الكامل (admin-dashboard/base-data، Admin SDK
    // بالباك اند) حتى يطابق الرقم تماماً رقم التقرير اليومي بالإيميل
    // (calculateExpectedReceivable بـfunctions/index.js)
    if (currentUser !== "Baneen@123") fetchSalesTotalOrders();
  }, [currentUser]);

  // ✅ نوافذ SweetAlert (تنبيهات البيانات الناقصة، تأكيد الطباعة...) تنرسم بـ z-index أوطى
  // من نوافذ MUI Dialog، فتطلع مخفية وراء نافذة "إدخال جديد" المفتوحة. نرفع z-index حتى تطلع فوقها دائمًا.
  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-swal-zindex-fix", "true");
    style.innerHTML = `.swal2-container { z-index: 20000 !important; }`;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

const fetchRecords = async () => {
  // ✅ كل القيود للأرصدة حتى تكون موحدة عند المدير وبنين
  const allQ = query(
    collection(db, "accounting_pro"),
    orderBy("timestamp", "desc")
  );

  const allSnap = await getDocs(allQ);
  const allData = allSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  setAllRecords(allData);

  // ✅ قيود العرض داخل الجدول فقط - لبنين: أسبوع العمل الحالي (السبت للخميس)
  // بدل حركات اليوم الحالي فقط سابقاً
 if (isBaneen) {
  const { weekStart, weekEnd } = baneenWeekRange;

  const weekQ = query(
    collection(db, "accounting_pro"),
    where("timestamp", ">=", Timestamp.fromDate(weekStart)),
    where("timestamp", "<=", Timestamp.fromDate(weekEnd)),
    orderBy("timestamp", "desc")
  );

  const weekSnap = await getDocs(weekQ);
  setRecords(weekSnap.docs.map(d => ({ id: d.id, ...d.data() })));
} else {
  // ✅ للمدير نعرض كل الحركات، مو آخر 100 فقط
  setRecords(allData);
}
};

  const fetchOrders = async () => {
    const snap = await getDocs(collection(db, "orders"));
    setOrders(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  // 🔗 طلبات لسه فيها مبلغ متبقي غير محصَّل (دفع عند الاستلام لسه ما وصل من
  // شركة التوصيل، أو باقي دفعة مقدمة لسه ما انسدد) - تُعرض كجيك بوكس عند
  // ربط قيد قبض جديد بطلب/طلبات.
  const outstandingOrders = useMemo(
    () => orders.filter((o) => getOrderRemainingAmount(o) > 0),
    [orders]
  );

  const linkedOrdersRemainingSum = useMemo(
    () =>
      linkedOrderIds.reduce((sum, id) => {
        const order = orders.find((o) => o.id === id);
        return sum + (order ? getOrderRemainingAmount(order) : 0);
      }, 0),
    [linkedOrderIds, orders]
  );

  // ✅ نفس مصدر بيانات الطلبات المستخدم بواجهة المدير (Admin SDK بالباك اند
  // يقرأ كل الطلبات بدون قيود صلاحيات Firestore) - نعتمده لحساب "المبيعات
  // الكلية" حتى يطابق الرقم بواجهة المدير تمام
  const fetchSalesTotalOrders = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin-dashboard/base-data`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.details || data.error || "فشل تحميل بيانات المبيعات");
      setSalesTotalOrders(Array.isArray(data.orders) ? data.orders : []);
    } catch (err) {
      console.error("Error fetching sales total orders:", err);
    }
  };

  // 🧾 يحسب كلفة الطباعة/الإطارات من كل الفواتير المعتمدة تاريخياً (نفس
  // منطق شيت "التفاصيل" بتصدير إكسل الفواتير حرفياً) بدل الاعتماد على
  // سجلات accounting_pro المكتوبة فقط عند اعتماد فاتورة من صفحة الفاتورة
  // نفسها - تلك السجلات موجودة فقط لفواتير اعتُمدت بعد إضافة تلك الميزة
  const fetchInvoiceCostHistory = async () => {
    setInvoiceCostHistoryLoading(true);
    try {
      // ✅ تحسين أداء: كانت هذي الدالة تجيب كل الفواتير من فايرستور مباشرة،
      // وبعدين لكل فاتورة على حدة تسوي استعلام منفصل لجلب نسخها (versions) -
      // بعشرات/مئات الفواتير هذا مئات الطلبات المتوازية من المتصفح مباشرة،
      // وهذا كان السبب الرئيسي لبطء فتح صفحة الحسابات (30 ثانية لدقيقة).
      // الإندبوينت الجديد بالباك اند يجيب نفس البيانات بالضبط (نفس شرط
      // lastVersionNumber، نفس اختيار أعلى نسخة معتمدة لكل فاتورة) بقراءتين
      // فقط (Admin SDK)، مع كاش قصير 30 ثانية. منطق الدي-دبليكيشن وحساب
      // الكلفة بالأسفل لم يتغيّر إطلاقاً - فقط طريقة جلب البيانات.
      const res = await apiFetch(`${API_BASE}/accounting/invoice-cost-data`);
      const payload = await res.json();
      if (!res.ok) {
        throw new Error(payload.error || "فشل تحميل بيانات كلفة الفواتير");
      }

      // completeInvoiceList مفلترة مسبقاً بالباك اند (نفس شرط lastVersionNumber
      // القديم). createdAt يرجع كـtoDate() مثل Firestore Timestamp تماماً حتى
      // يبقى استخدامه بالأسفل (inv.createdAt?.toDate?.()) بدون أي تعديل.
      const completeInvoiceList = (payload.invoices || []).map((inv) => ({
        ...inv,
        createdAt:
          inv.createdAtMs != null
            ? { toDate: () => new Date(inv.createdAtMs) }
            : undefined,
      }));

      const perInvoiceApproved = completeInvoiceList.map((inv) => ({
        inv,
        approvedInvoiceData: (payload.approvedByInvoiceId || {})[inv.id] || null,
      }));

      // ✅ الدي-دبليكيشن هنا: عند وجود أكثر من وثيقة بنفس رقم الفاتورة (خلل
      // قديم معروف بربط الطلب بالفاتورة)، لازم نفضّل الوثيقة اللي عندها نسخة
      // معتمدة فعلياً بدل الاعتماد على ترتيب الاستعلام العشوائي - وإلا ممكن
      // ننتخب وثيقة غير معتمدة كممثلة فنخسر فاتورة معتمدة حقيقية من المجموع
      const bestPerKey = new Map();
      perInvoiceApproved.forEach((entry) => {
        const key = getInvoiceDuplicateKeyForCost(entry.inv);
        const existing = bestPerKey.get(key);
        if (!existing) {
          bestPerKey.set(key, entry);
          return;
        }
        if (!existing.approvedInvoiceData && entry.approvedInvoiceData) {
          bestPerKey.set(key, entry);
        }
      });

      const validResults = Array.from(bestPerKey.values())
        .map(({ inv, approvedInvoiceData }) => {
          if (!approvedInvoiceData) return null;
          const items = approvedInvoiceData.items || [];

          const totals = items.reduce(
            (acc, item) => {
              const quantity = Number(item.quantity || 0);
              const { width, height } = parseSizeForCost(item.sizeKey);
              if (width <= 0 || height <= 0 || quantity <= 0) return acc;

              const costs = calculateCostBreakdownForAccounting(item);
              acc.print += costs.printCost * quantity;
              // "كلفة الإطارات" هنا = كلفة الخشب الداخلي + كلفة إطار L/Kas
              // معاً (نفس ما كان يُكتب سابقاً بحقل frameTotal عند الاعتماد)
              acc.frame += (costs.innerWoodCost + costs.frameCost) * quantity;
              return acc;
            },
            { print: 0, frame: 0 }
          );

          if (totals.print <= 0 && totals.frame <= 0) return null;

          // نفس منطق formatInvoiceDate بتصدير الإكسل (InvoiceListPage.jsx)
          const dateStr =
            approvedInvoiceData.date ||
            inv.date ||
            (approvedInvoiceData.createdAt?.toDate?.() || inv.createdAt?.toDate?.() || new Date())
              .toISOString()
              .slice(0, 10);
          const dateTimestamp = Timestamp.fromDate(new Date(dateStr));
          const customerName = approvedInvoiceData.customerName || inv.customerName || "-";
          const invoiceNumber = approvedInvoiceData.invoiceNumber || inv.invoiceNumber || "";

          return { id: inv.id, customerName, invoiceNumber, dateTimestamp, ...totals };
        })
        .filter(Boolean);

      const printTotal = validResults.reduce((sum, r) => sum + r.print, 0);
      const frameTotal = validResults.reduce((sum, r) => sum + r.frame, 0);

      const buildVirtualRecord = (r, amount) => ({
        id: `invoice_cost_history_${r.id}`,
        isInvoiceCostAccount: true,
        processName: r.customerName,
        invoiceNumber: r.invoiceNumber,
        date: r.dateTimestamp,
        amountIQD: Math.round(amount),
      });

      setInvoiceCostHistoryTotals({
        "مطبعة صدى سومر 2": Math.round(printTotal),
        "إطارات توب فريم 2": Math.round(frameTotal),
      });

      setInvoiceCostHistoryRecords({
        "مطبعة صدى سومر 2": validResults
          .filter((r) => r.print > 0)
          .map((r) => buildVirtualRecord(r, r.print)),
        "إطارات توب فريم 2": validResults
          .filter((r) => r.frame > 0)
          .map((r) => buildVirtualRecord(r, r.frame)),
      });
    } catch (err) {
      console.error("Error computing invoice cost history:", err);
    } finally {
      setInvoiceCostHistoryLoading(false);
    }
  };

 const suggestedCategory = useMemo(() => {
  const d = processName.toLowerCase();

  if (isIncome) {
    if (d.includes("دفعة") || d.includes("عميل") || d.includes("زبون")) return "دفعة من عميل";
    if (d.includes("بيع") || d.includes("مبيعات") || d.includes("ايراد") || d.includes("إيراد")) return "إيرادات مبيعات";
    return "إيرادات مبيعات";
  }

  if (d.includes("ببلز") || d.includes("تيب") || d.includes("لاصق شفاف")) {
    return "كلفة بضاعة مباعة - تعبئة وتغليف";
  }

  if (d.includes("بنزين") || d.includes("نفط") || d.includes("دهن") || d.includes("وقود") || d.includes("زيوت")) {
    return "الوقود والزيوت";
  }
// نقل اللوحات / الكيا / نقل السلع والخدمات
if (
  d.includes("نقل لوحات") ||
  d.includes("تكسي نقل لوحات") ||
  d.includes("كيا")
) {
  return "نقل سلع وخدمات";
}

// تكسي عادي = نقل موظفين
if (d.includes("تكسي")) {
  return "نقل موظفين";
}

// نقل عام بدون كلمة لوحات
if (d.includes("نقل")) {
  return "نقل موظفين";
}

if (d.includes("ايفاد") || d.includes("إيفاد")) {
  return "مصاريف نقل وإيفاد";
}
  if (d.includes("راتب") || d.includes("رواتب") || d.includes("اجور") || d.includes("أجور")) {
    return "رواتب واجور";
  }

  if (d.includes("عامل") || d.includes("عمال")) {
    return "أجور عمال";
  }

  if (d.includes("طعام") || d.includes("غداء") || d.includes("اكل") || d.includes("أكل")) {
    return "طعام";
  }

  if (d.includes("ضيافة") || d.includes("قهوة") || d.includes("شاي")) {
    return "ضيافة";
  }

  if (d.includes("ايجار") || d.includes("إيجار") || d.includes("استئجار")) {
    return "مصاريف إيجار";
  }

  if (d.includes("صيانة") && d.includes("سيارة")) {
    return "صيانة سيارات";
  }

  if (d.includes("صيانة") && (d.includes("جهاز") || d.includes("كهرباء") || d.includes("كهربائية"))) {
    return "صيانة أجهزة كهربائية";
  }

 if (
  d.includes("اشتراك") ||
  d.includes("رصيد") ||
  d.includes("تجديد") ||
  d.includes("اتصال") ||
  d.includes("انترنت") ||
  d.includes("هاتف")
) {
  return "اتصالات عامة";
}

  if (d.includes("قرطاسية") || d.includes("ورق") || d.includes("دفتر") || d.includes("اقلام") || d.includes("أقلام")) {
    return "مصاريف قرطاسية";
  }

  if (d.includes("اعلان") || d.includes("إعلان") || d.includes("دعاية")) {
    return "دعاية واعلان";
  }

  if (d.includes("مكافأة") || d.includes("مكافاة")) {
    return "مكافأة";
  }

  if (d.includes("سلفة") || d.includes("سلف")) {
    return "سلف المنتسبين";
  }

  if (d.includes("عمولة") || d.includes("عمولات")) {
    return "مصروف عمولات بيع";
  }

  if (d.includes("مخزون")) {
    return "المخزون";
  }

  if (d.includes("لوازم")) {
    return "مصاريف لوازم";
  }

  if (d.includes("تأسيس") || d.includes("تاسيس")) {
    return "مصاريف تأسيس";
  }

  if (d.includes("قانون") || d.includes("استشار")) {
    return "مصاريف قانونية واستشارية";
  }

  if (d.includes("بنك") || d.includes("مصرف") || d.includes("حوالة")) {
    return "خدمات مصرفية";
  }

  if (d.includes("مسحوبات") || d.includes("شخصية")) {
    return "مسحوبات شخصية";
  }

  return "مصاريف أخرى";
}, [processName, isIncome]);

 useEffect(() => {
  if (!editingId && !isCreditAccountMode && !isEmployeeAccountMode) {
    setManualCategory(suggestedCategory);
  }
}, [suggestedCategory, isIncome, editingId, isCreditAccountMode, isEmployeeAccountMode]);

  const finalAmountIQD = useMemo(() => {
    if (currency === "USD") return Number(amount) * Number(exchangeRate);
    return Number(amount);
  }, [amount, currency, exchangeRate]);
const isDateWithinFilter = (firebaseDate) => {
  if (!filterFromDate && !filterToDate) return true;

  const itemDate =
    firebaseDate?.toDate?.() ||
    (firebaseDate instanceof Date ? firebaseDate : null);

  if (!itemDate) return true;

  const itemOnlyDate = new Date(itemDate);
  itemOnlyDate.setHours(0, 0, 0, 0);

  if (filterFromDate) {
    const from = new Date(filterFromDate);
    from.setHours(0, 0, 0, 0);

    if (itemOnlyDate < from) return false;
  }

  if (filterToDate) {
    const to = new Date(filterToDate);
    to.setHours(23, 59, 59, 999);

    if (itemOnlyDate > to) return false;
  }

  return true;
};

const isDateFilterActive = !!filterFromDate || !!filterToDate;

const filteredAllRecords = useMemo(() => {
  return allRecords.filter((r) => isDateWithinFilter(r.date || r.timestamp));
}, [allRecords, filterFromDate, filterToDate]);

const filteredOrders = useMemo(() => {
  return orders.filter((o) => isDateWithinFilter(o.createdAt || o.timestamp || o.date));
}, [orders, filterFromDate, filterToDate]);

const displayedRecords = useMemo(() => {
  const source = isDateFilterActive ? filteredAllRecords : records;

  return source.filter(
    (r) =>
      !r.hiddenFromTable &&
      !r.balanceOnly &&
      (!isBaneen || r.showForBaneen !== false)
  );
}, [isDateFilterActive, filteredAllRecords, records, isBaneen]);
  const walletStats = useMemo(() => {
    const stats = { zain: 0, fib: 0, qi: 0, baghdad: 0, erbil: 0 };
    
  filteredOrders.forEach(o => {
  if (!o.payment) return;

  // ✅ إذا الطلب إله قيد حسابات تلقائي، لا نحسبه هنا حتى لا يصير تكرار
  if (o.accountingEntryCreated || o.accountingEntryId) return;

  const amt =
    o.payment?.method === "deposit"
      ? Number(o.payment?.depositAmount || 0)
      : Number(o.price || 0);

  if (o.payment?.provider === "Zain Cash") stats.zain += amt;
  if (o.payment?.provider === "FIB") stats.fib += amt;
  if (o.payment?.provider === "Qi Card") stats.qi += amt;
});

  filteredAllRecords.forEach(r => {
  if (r.reportOnly) return;

  const amt = Number(r.amountIQD || 0);
      
      // إذا كان القيد تحويل داخلي، نخصم من المصدر ونزيد في المستهدف مباشرة دون النظر لكونه قبض أو صرف عادي
      if (r.isInternalTransfer) {
        if (r.wallet) stats[r.wallet] -= amt; // الصندوق الذي خرج منه المال ينقص
        if (r.transferTo) stats[r.transferTo] += amt; // الصندوق الذي استلم المال يزيد
      } else {
        // العمليات الطبيعية القديمة
        const factor = r.type === "قبض" ? 1 : -1;
        if (r.wallet === "zain") stats.zain += (amt * factor);
        else if (r.wallet === "fib") stats.fib += (amt * factor);
        else if (r.wallet === "qi") stats.qi += (amt * factor);
        else if (r.wallet === "baghdad") stats.baghdad += (amt * factor);
        else if (r.wallet === "erbil") stats.erbil += (amt * factor);
      }
    });

    const cashGeneral = stats.baghdad + stats.erbil; 
    const cardBalance = stats.zain + stats.qi + stats.fib; 
    const totalBox = cashGeneral + cardBalance; 

    return { ...stats, cashGeneral, cardBalance, totalBox };
  }, [filteredOrders, filteredAllRecords]);
const displayedRecordsWithBalances = useMemo(() => {
const getSafeRecordTime = (record) => {
  const d =
    record?.timestamp?.toDate?.() ||
    record?.date?.toDate?.() ||
    null;

  return d ? d.getTime() : 0;
};

  const source = isDateFilterActive ? filteredAllRecords : allRecords;

  const stats = {
    zain: 0,
    fib: 0,
    qi: 0,
    baghdad: 0,
    erbil: 0,
  };

  // نفس منطق الصناديق: دفعات الطلبات تدخل كبداية
  filteredOrders.forEach((o) => {
    if (!o.payment) return;
    if (o.accountingEntryCreated || o.accountingEntryId) return;

    const amt =
      o.payment?.method === "deposit"
        ? Number(o.payment?.depositAmount || 0)
        : Number(o.price || 0);

    if (o.payment?.provider === "Zain Cash") stats.zain += amt;
    if (o.payment?.provider === "FIB") stats.fib += amt;
    if (o.payment?.provider === "Qi Card") stats.qi += amt;
  });

  // نرتب من الأقدم للأحدث حتى نحسب الرصيد بعد كل حركة
  const sortedAsc = [...source]
    .filter((r) => !r.reportOnly)
    .sort((a, b) => getSafeRecordTime(a) - getSafeRecordTime(b));

  const balanceMap = {};

  sortedAsc.forEach((r) => {
    const amt = Number(r.amountIQD || 0);

    if (r.isInternalTransfer) {
      if (r.wallet && stats[r.wallet] !== undefined) {
        stats[r.wallet] -= amt;
      }

      if (r.transferTo && stats[r.transferTo] !== undefined) {
        stats[r.transferTo] += amt;
      }
    } else {
      const factor = r.type === "قبض" ? 1 : -1;

      if (r.wallet && stats[r.wallet] !== undefined) {
        stats[r.wallet] += amt * factor;
      }
    }

    const cashGeneral = stats.baghdad + stats.erbil;
    const cardBalance = stats.zain + stats.qi + stats.fib;
    const totalBox = cashGeneral + cardBalance;

    balanceMap[r.id] = {
      walletBalanceAfter:
        r.wallet && stats[r.wallet] !== undefined ? stats[r.wallet] : null,
      totalBoxAfter: totalBox,
    };
  });

  return displayedRecords.map((r) => ({
    ...r,
    walletBalanceAfter: balanceMap[r.id]?.walletBalanceAfter ?? null,
    totalBoxAfter: balanceMap[r.id]?.totalBoxAfter ?? null,
  }));
}, [
  allRecords,
  filteredAllRecords,
  filteredOrders,
  displayedRecords,
  isDateFilterActive,
]);

// ✅ فلتر التبويب (نوع القيد) فوق سجل القيود - لا يؤثر على حساب الأرصدة، فقط على ما يُعرض
const matchesEntryTab = (r, tab) => {
  if (tab === "الكل") return true;

  const isCreditRecord = !!r.isCreditAccount || !!r.creditAction;
  const isEmployeeRecord = !!r.isEmployeeAccount;
  const isTransferRecord = !!r.isInternalTransfer;

  if (tab === "قبض") {
    return (
      r.type === "قبض" &&
      !isTransferRecord &&
      !isCreditRecord &&
      !isEmployeeRecord
    );
  }

  if (tab === "صرف") {
    return (
      r.type === "صرف" &&
      !isTransferRecord &&
      !isCreditRecord &&
      !isEmployeeRecord
    );
  }

  if (tab === "حساب آجل") return isCreditRecord;
  if (tab === "حساب موظف") return isEmployeeRecord;
  if (tab === "تحويل داخلي") return isTransferRecord;

  return true;
};

const filteredTableRecords = useMemo(() => {
  return displayedRecordsWithBalances.filter((r) => {
    if (
      filterEntryTab.length > 0 &&
      !filterEntryTab.some((tab) => matchesEntryTab(r, tab))
    )
      return false;
    if (
      filterAccountingCategory.length > 0 &&
      !filterAccountingCategory.includes(r.accountingCategory)
    )
      return false;
    if (filterWallet.length > 0 && !filterWallet.includes(r.wallet)) return false;
    if (
      filterDescriptionSearch.trim() !== "" &&
      !(r.processName || "")
        .toLowerCase()
        .includes(filterDescriptionSearch.trim().toLowerCase())
    )
      return false;
    return true;
  });
}, [
  displayedRecordsWithBalances,
  filterEntryTab,
  filterAccountingCategory,
  filterWallet,
  filterDescriptionSearch,
]);

// 📅 نطاق أسبوع العمل (السبت -> الخميس) - يُستخدم لتحديد مدى "سجل القيود"
// لبنين (بدل حركات اليوم الحالي فقط سابقاً)
const baneenWeekRange = useMemo(() => {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0=Sun .. 6=Sat

  const daysSinceSaturday = (dayOfWeek + 1) % 7;

  const weekStart = new Date(now);
  weekStart.setHours(0, 0, 0, 0);
  weekStart.setDate(weekStart.getDate() - daysSinceSaturday);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 5); // السبت + 5 أيام = الخميس
  weekEnd.setHours(23, 59, 59, 999);

  return { weekStart, weekEnd };
}, []);

// 🔒 قفل زر "مطابقة الحسابات" لمدة 12 ساعة بعد آخر مطابقة سجّلتها بنين -
// نشتق وقت آخر مطابقة من نفس السجلات (isReconciliation:true) بدل حقل
// إعداد منفصل، فيبقى القفل صحيحاً حتى بعد إعادة تحميل الصفحة أو بجلسة جديدة
const lastReconciliationAt = useMemo(() => {
  let latest = null;
  allRecords.forEach((r) => {
    if (!r.isReconciliation) return;
    const t = r.timestamp?.toDate?.() || r.date?.toDate?.() || null;
    if (t && (!latest || t > latest)) latest = t;
  });
  return latest;
}, [allRecords]);

const reconcileUnlockAt = useMemo(() => {
  if (!lastReconciliationAt) return null;
  return new Date(lastReconciliationAt.getTime() + RECONCILE_LOCK_MS);
}, [lastReconciliationAt]);

const isReconcileLocked = !!reconcileUnlockAt && reconcileUnlockAt.getTime() > Date.now();

// 🔒 يمنع بنين (isBaneen فقط - زر "مطابقة الحسابات" أصلاً حصري إلها، الأدمن
// ما عنده هذا الزر إطلاقاً) من إضافة أي حركة جديدة (صرف/قبض/موظف/آجل) إذا
// فاتتها مطابقة يوم عمل سابق - نتجاهل الجمعة (عطلة الشركة) عند حساب "يوم
// العمل السابق". ما نقفل رجعياً لو بنين لسه ما استخدمت زر المطابقة إطلاقاً
// (lastReconciliationAt فاضي) حتى ما يوقف العمل فجأة أول ما تنفعّل الميزة.
const missedReconciliationDay = useMemo(() => {
  if (!lastReconciliationAt) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const prevBusinessDay = new Date(today);
  prevBusinessDay.setDate(prevBusinessDay.getDate() - 1);
  while (prevBusinessDay.getDay() === 5) {
    prevBusinessDay.setDate(prevBusinessDay.getDate() - 1);
  }

  const lastReconcileDay = new Date(lastReconciliationAt);
  lastReconcileDay.setHours(0, 0, 0, 0);

  return lastReconcileDay.getTime() < prevBusinessDay.getTime() ? prevBusinessDay : null;
}, [lastReconciliationAt]);

const isReconciliationMissed = !!missedReconciliationDay;

// ✅ إجمالي الإيرادات/المصاريف - يتأثر بنفس فلتر التبويب/التاريخ/الصندوق أعلاه
const { totalRevenueAll, totalExpenseAll } = useMemo(() => {
  let revenue = 0;
  let expense = 0;

  filteredTableRecords.forEach((r) => {
    const amountValue = Number(r.amountIQD || 0);

    const isActualIncome =
      r.type === "قبض" &&
      !r.isInternalTransfer &&
      !r.reportOnly &&
      !r.balanceOnly &&
      r.affectsWallet !== false &&
      r.creditAction !== "add_invoice";

    const isActualExpense =
      r.type === "صرف" &&
      !r.isInternalTransfer &&
      !r.reportOnly &&
      !r.balanceOnly &&
      r.affectsWallet !== false &&
      r.creditAction !== "add_invoice";

    if (isActualIncome) revenue += amountValue;
    if (isActualExpense) expense += amountValue;
  });

  return { totalRevenueAll: revenue, totalExpenseAll: expense };
}, [filteredTableRecords]);

// ✅ المبيعات الكلية (من نظام الطلبات بواجهة المدير) - من 15-5-2026 وتستمر
// لين اليوم (تاريخ متحرك)، بغض النظر عن فلتر التاريخ الخاص بسجل القيود.
// نفس استدعاء بيانات الطلبات المستخدم بواجهة المدير (admin-dashboard/base-data)
// ونفس صيغة مقارنة التاريخ حرفياً (new Date("YYYY-MM-DD") لكل من "من" و"إلى"،
// بدون تصفير للساعة) حتى يطابق رقم زر "الطلبات الكلية" بواجهة المدير تمام
// بالريال - شامل قاعدة "المبيعات" (بدون ماركتنك وبدون excludedFromStats)
// + نفس الأشهر القديمة الثابتة (staticTotals) اللي تنضاف هناك
const totalSalesAllTime = useMemo(() => {
  // ⚠️ لازم نبني نص التاريخ من مكوّنات التاريخ المحلية (لا toISOString) وإلا
  // ينزاح التاريخ يوم كامل بسبب فرق التوقيت (UTC+3) قبل ما يترحول لـ Date
  const toLocalDateOnlyString = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const rangeStart = new Date(toLocalDateOnlyString(SALES_TOTAL_START_DATE));
  const rangeEnd = new Date(toLocalDateOnlyString(new Date()));

  const liveTotal = salesTotalOrders.reduce((sum, o) => {
    if ((o.saleAccount || "بيع") === "ماركتنك") return sum;
    if (o.excludedFromStats) return sum;

    const d = toDateSafe(o.createdAt) || toDateSafe(o.timestamp) || toDateSafe(o.date);

    if (!d || d < rangeStart || d > rangeEnd) return sum;

    return sum + Number(o.price || 0);
  }, 0);

  return liveTotal + STATIC_LEGACY_TOTAL;
}, [salesTotalOrders]);

// ✅ "الباقي المتوقع تحصيله من الزبائن" - نفس getOrderRemainingAmount/
// calculateExpectedReceivable المستخدمة بتقرير الإيميل اليومي
// (functions/index.js) وبنفس قائمة ربط القبض بالطلبات (outstandingOrders
// أعلاه)، لكن محسوبة من salesTotalOrders (Admin SDK، كل الطلبات بدون قيود
// صلاحيات Firestore) بدل orders (قراءة Firestore مباشرة من العميل - موثّق
// أعلاه أنها ممكن ترجع جزء بس من الطلبات) حتى يطابق الرقم رقم الإيميل تماماً
const expectedReceivableStats = useMemo(() => {
  const outstanding = salesTotalOrders.filter((o) => getOrderRemainingAmount(o) > 0);
  return {
    count: outstanding.length,
    total: outstanding.reduce((sum, o) => sum + getOrderRemainingAmount(o), 0),
  };
}, [salesTotalOrders]);

const WALLET_LABELS = {
  totalBox: "الصندوق الكلي",
  cashGeneral: "كاش عام",
  cardBalance: "رصيد البطاقات",
  baghdad: "كاش بغداد",
  erbil: "كاش أربيل",
  qi: "كي كارد",
  zain: "زين كاش",
  fib: "FIB",
};

const getWalletName = (walletValue) => {
  if (walletValue === "baghdad") return "كاش بغداد";
  if (walletValue === "erbil") return "كاش أربيل";
  if (walletValue === "zain") return "زين كاش";
  if (walletValue === "fib") return "FIB";
  if (walletValue === "qi") return "كي كارد";
  return walletValue || "-";
};

const openWalletDetails = (walletKey) => {
  setSelectedWalletKey(walletKey);
  setWalletDetailsOpen(true);
};
const selectedWalletDetailsRows = useMemo(() => {
  if (!selectedWalletKey) return [];

  const getSafeDate = (item) => {
    const d =
      item?.date?.toDate?.() ||
      item?.timestamp?.toDate?.() ||
      item?.createdAt?.toDate?.() ||
      null;

    return d || new Date(0);
  };

  const isWalletIncluded = (walletKey, walletValue) => {
    if (walletKey === "totalBox") {
      return ["baghdad", "erbil", "qi", "zain", "fib"].includes(walletValue);
    }

    if (walletKey === "cashGeneral") {
      return ["baghdad", "erbil"].includes(walletValue);
    }

    if (walletKey === "cardBalance") {
      return ["qi", "zain", "fib"].includes(walletValue);
    }

    return walletValue === walletKey;
  };

  const rows = [];

  // ✅ دفعات الطلبات التي تؤثر على البطاقات
  filteredOrders.forEach((o) => {
    if (!o.payment) return;
    if (o.accountingEntryCreated || o.accountingEntryId) return;

    let orderWallet = "";

    if (o.payment?.provider === "Zain Cash") orderWallet = "zain";
    if (o.payment?.provider === "FIB") orderWallet = "fib";
    if (o.payment?.provider === "Qi Card") orderWallet = "qi";

    if (!orderWallet) return;
    if (!isWalletIncluded(selectedWalletKey, orderWallet)) return;

    const amountValue =
      o.payment?.method === "deposit"
        ? Number(o.payment?.depositAmount || 0)
        : Number(o.price || 0);

    rows.push({
      id: `order-${o.id}`,
      date: getSafeDate(o),
      description: o.customerName || "دفعة طلب",
      category: "دفعة طلب",
      type: "قبض",
      amount: amountValue,
      wallet: orderWallet,
      walletLabel: getWalletName(orderWallet),
      location: o.governorate || "-",
      source: "order",
      fileUrls: [],
    });
  });

  // ✅ قيود الحسابات التي تؤثر على الصناديق
  filteredAllRecords.forEach((r) => {
    if (r.reportOnly) return;
    if (r.balanceOnly) return;
    if (r.hiddenFromTable) return;

    const amountValue = Number(r.amountIQD || 0);

    if (r.isInternalTransfer) {
      // خروج من الصندوق المصدر
      if (r.wallet && isWalletIncluded(selectedWalletKey, r.wallet)) {
        rows.push({
          id: `${r.id}-out`,
          recordId: r.id,
          date: getSafeDate(r),
          description: r.processName || "-",
          category: "تحويل داخلي - خروج",
          type: "صرف",
          amount: -amountValue,
          wallet: r.wallet,
          walletLabel: getWalletName(r.wallet),
          location: r.location || "-",
          source: "accounting",
          fileUrls: r.invoiceUrls?.length ? r.invoiceUrls : r.invoiceUrl ? [r.invoiceUrl] : [],
        });
      }

      // دخول إلى الصندوق الهدف
      if (r.transferTo && isWalletIncluded(selectedWalletKey, r.transferTo)) {
        rows.push({
          id: `${r.id}-in`,
          recordId: r.id,
          date: getSafeDate(r),
          description: r.processName || "-",
          category: "تحويل داخلي - دخول",
          type: "قبض",
          amount: amountValue,
          wallet: r.transferTo,
          walletLabel: getWalletName(r.transferTo),
          location: r.location || "-",
          source: "accounting",
          fileUrls: r.invoiceUrls?.length ? r.invoiceUrls : r.invoiceUrl ? [r.invoiceUrl] : [],
        });
      }

      return;
    }

    if (!r.wallet) return;
    if (!isWalletIncluded(selectedWalletKey, r.wallet)) return;

    const isIncomeRecord = r.type === "قبض";
    const signedAmount = isIncomeRecord ? amountValue : -amountValue;

    rows.push({
      id: r.id,
      recordId: r.id,
      date: getSafeDate(r),
      description: r.processName || "-",
      category: r.creditAction === "payment"
        ? `تسديد حساب آجل - ${r.creditAccountName || r.accountName || ""}`
        : r.accountingCategory || "-",
      type: isIncomeRecord ? "قبض" : "صرف",
      amount: signedAmount,
      wallet: r.wallet,
      walletLabel: getWalletName(r.wallet),
      location: r.location || "-",
      source: "accounting",
      fileUrls: r.invoiceUrls?.length ? r.invoiceUrls : r.invoiceUrl ? [r.invoiceUrl] : [],
    });
  });

  return rows.sort((a, b) => b.date.getTime() - a.date.getTime());
}, [selectedWalletKey, filteredOrders, filteredAllRecords]);


const exportSelectedWalletExcel = () => {
  if (!selectedWalletKey || selectedWalletDetailsRows.length === 0) {
    return MySwal.fire({
      icon: "info",
      title: "لا توجد بيانات",
      text: "لا توجد حركات لهذا الصندوق",
    });
  }

  const rows = selectedWalletDetailsRows.map((r, index) => ({
    "تسلسل": index + 1,
    "الصندوق": WALLET_LABELS[selectedWalletKey] || "-",
    "التاريخ": r.date,
    "الوصف": r.description,
    "التبويب": r.category,
    "نوع الحركة": r.type,
    "المبلغ": Math.abs(Number(r.amount || 0)),
    "إشارة الحركة": Number(r.amount || 0) >= 0 ? "قبض" : "صرف",
    "الموقع": r.location,
    "الصندوق المستخدم": r.walletLabel,
  }));

  const ws = XLSX.utils.json_to_sheet(rows, { cellDates: true });

  ws["!cols"] = [
    { wch: 8 },
    { wch: 18 },
    { wch: 14 },
    { wch: 35 },
    { wch: 28 },
    { wch: 14 },
    { wch: 16 },
    { wch: 14 },
    { wch: 14 },
    { wch: 18 },
  ];

  for (let row = 2; row <= rows.length + 1; row++) {
    if (ws[`C${row}`]) ws[`C${row}`].z = "m/d/yyyy";
    if (ws[`G${row}`]) ws[`G${row}`].z = '"IQD" #,##0';
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "تفاصيل الصندوق");

  XLSX.writeFile(
    wb,
    `Wallet_${(WALLET_LABELS[selectedWalletKey] || selectedWalletKey).replace(/[\\/:*?"<>|]/g, "_")}.xlsx`
  );
};

  const creditAccountStats = useMemo(() => {
  const balances = {};

  CREDIT_ACCOUNTS.forEach((name) => {
    balances[name] = 0;
  });

  allRecords.forEach((r) => {
    if (!r.isCreditAccount && !r.creditAction) return;

    const accountName = r.creditAccountName || r.accountName;
    if (!accountName) return;

    const amountValue = Number(r.amountIQD || 0);

    if (!balances[accountName]) {
      balances[accountName] = 0;
    }

    // إضافة فاتورة = يزيد المطلوب علينا
    if (r.creditAction === "add_invoice") {
      balances[accountName] += amountValue;
    }

    // تسديد حساب أو خصم = ينقص المطلوب علينا
    if (r.creditAction === "payment" || r.creditAction === "discount") {
      balances[accountName] -= amountValue;
    }
  });

  const totalCreditAccounts = Object.values(balances).reduce(
    (sum, value) => sum + Number(value || 0),
    0
  );

  return {
    balances,
    totalCreditAccounts,
  };
}, [allRecords]);

const visibleCreditAccountBalances = useMemo(() => {
  const allowedForBaneen = [
    "إطارات توب فريم",
    "إطارات محمد الخفاجي",
    "مطبعة صدى سومر - أبو زينب",
  ];

  return Object.entries(creditAccountStats.balances).filter(([name]) => {
    if (!isBaneen) return true;
    return allowedForBaneen.includes(name);
  });
}, [creditAccountStats.balances, isBaneen]);
// ✅ الأرصدة تجي الآن من fetchInvoiceCostHistory (كل الفواتير المعتمدة
// تاريخياً، مطابقة لإكسل الفواتير) بدل سجلات accounting_pro المحدودة بفواتير
// اعتُمدت بعد إضافة الترحيل التلقائي فقط
const invoiceCostAccountStats = useMemo(() => {
  const balances = {};
  INVOICE_COST_ACCOUNTS.forEach((name) => {
    balances[name] = invoiceCostHistoryTotals[name] || 0;
  });
  return { balances };
}, [invoiceCostHistoryTotals]);

const employeeAccountStats = useMemo(() => {
  const balances = {};

  EMPLOYEE_ACCOUNTS.forEach((name) => {
    balances[name] = 0;
  });

  allRecords.forEach((r) => {
    if (!r.isEmployeeAccount) return;

    const accountName = r.employeeAccountName;
    if (!accountName) return;

    if (!balances[accountName]) {
      balances[accountName] = 0;
    }

    balances[accountName] += Number(r.amountIQD || 0);
  });

  return {
    balances,
  };
}, [allRecords]);

const monthlyDashboardStats = useMemo(() => {
  const currentMonthKey = getMonthKey(new Date());

  let totalMonthlyExpenses = 0;
  const totalExpensesByMonth = {};

  let totalMonthlyIncome = 0;
  const totalIncomeByMonth = {};

  const expenseMap = {};

  MONTHLY_EXPENSE_BOXES.forEach((item) => {
    expenseMap[item.key] = {
      key: item.key,
      label: item.label,
      color: item.color,
      currentMonthTotal: 0,
      monthlyTotals: {},
    };
  });

  allRecords.forEach((record) => {
    const recordDate = getSafeJsDate(record);
    if (!recordDate) return;

    const monthKey = getMonthKey(recordDate);
    const amountValue = Number(record.amountIQD || 0);

    // ✅ مصروف فعلي فقط
    const isActualExpense =
      record.type === "صرف" &&
      !record.isInternalTransfer &&
      !record.reportOnly &&
      !record.balanceOnly &&
      record.affectsWallet !== false &&
      record.creditAction !== "add_invoice";

    // ✅ وارد فعلي فقط
    const isActualIncome =
      record.type === "قبض" &&
      !record.isInternalTransfer &&
      !record.reportOnly &&
      !record.balanceOnly &&
      record.affectsWallet !== false &&
      record.creditAction !== "add_invoice";

    if (isActualIncome) {
      // ✅ الواردات الشهرية: مجموع كل حركات القبض الفعلية لكل شهر
      totalIncomeByMonth[monthKey] =
        (totalIncomeByMonth[monthKey] || 0) + amountValue;

      if (monthKey === currentMonthKey) {
        totalMonthlyIncome += amountValue;
      }
    }

    if (!isActualExpense) return;

    // ✅ المصاريف الشهرية: مجموع كل حركات الصرف الفعلية لكل شهر بدون استثناء أي تبويب
    totalExpensesByMonth[monthKey] =
      (totalExpensesByMonth[monthKey] || 0) + amountValue;

    if (monthKey === currentMonthKey) {
      totalMonthlyExpenses += amountValue;
    }

    // ✅ كل قيد ينتمي إلى بوكس واحد فقط حتى ما يتكرر بالحساب
    const matchedExpense = MONTHLY_EXPENSE_BOXES.find((item) =>
      item.matches(record)
    );

    if (!matchedExpense) return;

    const bucket = expenseMap[matchedExpense.key];

    bucket.monthlyTotals[monthKey] =
      (bucket.monthlyTotals[monthKey] || 0) + amountValue;

    if (monthKey === currentMonthKey) {
      bucket.currentMonthTotal += amountValue;
    }
  });

  const expenseItems = MONTHLY_EXPENSE_BOXES.map((item) => {
    const monthlyTotals = expenseMap[item.key].monthlyTotals;

    const monthRows = Object.entries(monthlyTotals)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .slice(0, 6)
      .map(([monthKey, value]) => ({
        monthKey,
        label: getMonthLabelFromKey(monthKey),
        value,
      }));

    // ✅ المجموع الكلي لكل الأشهر (مو بس الأشهر المعروضة)
    const allMonthsTotal = Object.values(monthlyTotals).reduce(
      (sum, value) => sum + Number(value || 0),
      0
    );

    return {
      ...expenseMap[item.key],
      monthRows,
      allMonthsTotal,
    };
  });

  // ✅ نسبة كل بوكس من مجموع كل البوكسات (لكل الأشهر)
  const expenseBoxesGrandTotal = expenseItems.reduce(
    (sum, item) => sum + item.allMonthsTotal,
    0
  );
  expenseItems.forEach((item) => {
    item.sharePercent = expenseBoxesGrandTotal
      ? (item.allMonthsTotal / expenseBoxesGrandTotal) * 100
      : 0;
  });

  const totalExpenseMonthRows = Object.entries(totalExpensesByMonth)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .slice(0, 3)
    .map(([monthKey, value]) => ({
      monthKey,
      label: getMonthLabelFromKey(monthKey),
      value,
    }));

  const totalIncomeMonthRows = Object.entries(totalIncomeByMonth)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .slice(0, 3)
    .map(([monthKey, value]) => ({
      monthKey,
      label: getMonthLabelFromKey(monthKey),
      value,
    }));

  return {
    currentMonthKey,
    currentMonthLabel: getMonthLabelFromKey(currentMonthKey),
    totalMonthlyExpenses,
    totalExpenseMonthRows,
    totalMonthlyIncome,
    totalIncomeMonthRows,
    expenseItems,
    expenseBoxesGrandTotal,
  };
}, [allRecords]);

// ✅ المبيعات الشهرية: نفس مبلغ بوكس المبيعات بواجهة المدير (من الطلبات، بدون ماركتنك)
const monthlySalesStats = useMemo(() => {
  const currentMonthKey = getMonthKey(new Date());
  const monthTotals = {};

  const parseOrderDate = (order) => {
    if (!order?.createdAt) return null;
    if (typeof order.createdAt === "string") {
      return new Date(`${order.createdAt}-01T00:00:00`);
    }
    if (order.createdAt?.toDate) return order.createdAt.toDate();
    if (order.createdAt?.seconds) return new Date(order.createdAt.seconds * 1000);
    if (order.createdAt?._seconds) return new Date(order.createdAt._seconds * 1000);
    return null;
  };

  orders.forEach((order) => {
    if (order.excludedFromStats) return;
    if ((order.saleAccount || "بيع") === "ماركتنك") return;

    const orderDate = parseOrderDate(order);
    if (!orderDate || Number.isNaN(orderDate.getTime())) return;

    const monthKey = getMonthKey(orderDate);
    monthTotals[monthKey] = (monthTotals[monthKey] || 0) + Number(order.price || 0);
  });

  const monthRows = Object.entries(monthTotals)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .slice(0, 3)
    .map(([monthKey, value]) => ({
      monthKey,
      label: getMonthLabelFromKey(monthKey),
      value,
    }));

  return {
    currentMonthTotal: monthTotals[currentMonthKey] || 0,
    monthRows,
  };
}, [orders]);
const getRecordAttachments = (record) => {
  if (!record) return [];

  if (record.invoiceUrls && Array.isArray(record.invoiceUrls)) {
    return record.invoiceUrls.filter(Boolean);
  }

  if (record.invoiceUrl) {
    return [record.invoiceUrl];
  }

  return [];
};

const removeExistingAttachment = (urlToRemove) => {
  setExistingInvoiceUrls((prev) =>
    prev.filter((url) => url !== urlToRemove)
  );
};

const getNextVoucherNumber = (type) => {
  const nums = allRecords
    .filter((r) => r.voucher?.type === type)
    .map((r) => Number(r.voucher?.voucherNumber || 0))
    .filter((n) => n > 0);

  if (nums.length === 0) return 1;

  return Math.max(...nums) + 1;
};

const openVoucherDialog = (recordId, recordType, recordPayload) => {
  const nextNumber = getNextVoucherNumber(recordType);

  const voucherAmount =
    Number(recordPayload.amountIQD) ||
    Number(recordPayload.amountOriginal) ||
    0;

  setVoucherRecordId(recordId);
  setVoucherType(recordType);

  setVoucherData({
    voucherNumber: nextNumber,
    date: selectedDate,
    personName: "",
    amount: voucherAmount,
    amountWords: amountToArabicIQD(voucherAmount),
    reason: recordPayload.processName || "",
    paymentMethod: getWalletLabelForVoucher(recordPayload.wallet),
    invoiceNumber:
      recordPayload.receiptNumber === "بدون"
        ? ""
        : recordPayload.receiptNumber || "",
    receivedBy: recordType === "قبض" ? "بنين رافد عيسى" : "",
    notes: "",
  });

  setVoucherDialogOpen(true);
};

const printVoucher = async () => {
  if (!voucherRecordId) return;

  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const voucherDate = new Date(voucherData.date);
  voucherDate.setHours(0, 0, 0, 0);

  if (voucherDate > today) {
    return MySwal.fire({
      icon: "error",
      title: "تاريخ غير صحيح",
      text: "لا يمكن اختيار تاريخ مستقبلي للسند",
    });
  }

  if (!voucherData.personName.trim()) {
    return MySwal.fire({
      icon: "error",
      title: "بيانات ناقصة",
      text: voucherType === "قبض" ? "يرجى إدخال اسم السيد / السادة" : "يرجى إدخال اسم المستفيد",
    });
  }

  if (!voucherData.reason.trim()) {
    return MySwal.fire({
      icon: "error",
      title: "بيانات ناقصة",
      text: voucherType === "قبض" ? "يرجى إدخال سبب القبض" : "يرجى إدخال سبب الصرف",
    });
  }

  if (!voucherData.receivedBy.trim()) {
    return MySwal.fire({
      icon: "error",
      title: "بيانات ناقصة",
      text: "يرجى إدخال اسم المستلم / استلمت بواسطة",
    });
  }

const amountValue = Number(voucherData.amount || 0);
const amountWords = voucherData.amountWords || amountToArabicIQD(amountValue);

  const voucherPayload = {
    type: voucherType,
    voucherNumber: Number(voucherData.voucherNumber || 1),
    date: voucherData.date,
    personName: voucherData.personName,
    amount: amountValue,
    amountWords,
    reason: voucherData.reason,
    paymentMethod: voucherData.paymentMethod,
    invoiceNumber: voucherData.invoiceNumber,
    receivedBy: voucherData.receivedBy,
    notes: voucherData.notes || "",
  };

  // ✅ Phase 07-B4: يستبدل updateDoc المباشر - createdAt/createdBy تُحسَبان
  // بالسيرفر الآن (لم تعودا جزءاً من voucherPayload المحلي، غير مستخدمتين
  // بقالب HTML أدناه أصلاً).
  const voucherRes = await apiFetch(`${API_BASE}/accounting/${voucherRecordId}/voucher`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(voucherPayload),
  });
  if (!voucherRes.ok) {
    return MySwal.fire({
      icon: "error",
      title: "خطأ",
      text: "حدث خطأ أثناء حفظ بيانات السند",
    });
  }

  const title = voucherType === "قبض" ? "سند قبض" : "سند صرف";
  const personLabel = voucherType === "قبض" ? "استلمنا من السيد / السادة" : "اسم المستفيد";
  const reasonLabel = voucherType === "قبض" ? "وذلك عن / سبب القبض" : "سبب الصرف / البيان";

const html = `
<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
<meta charset="UTF-8" />
<title>${title}</title>

<style>
  * {
    box-sizing: border-box;
  }

  body {
    margin: 0;
    padding: 0;
    background: #ffffff;
    color: #000000;
    font-family: Arial, Tahoma, sans-serif;
  }

 .print-page {
  width: 210mm;
  height: 148.5mm;
  padding: 4mm;
  background: #fff;
}

.voucher {
  width: 100%;
  height: 136mm;
  border: 2px solid #000;
  padding: 4mm 5mm 3mm 5mm;
  position: relative;
  overflow: hidden;
}

  .top-row {
    display: grid;
    grid-template-columns: 33% 34% 33%;
    align-items: start;
    height: 23mm;
  }

.logo-area {
  direction: ltr;
  text-align: left;
  padding-top: 1mm;
}

  .logo-main {
    font-size: 34px;
    font-weight: 900;
    letter-spacing: -1px;
    line-height: 0.9;
  }

  .logo-sub {
    font-size: 9px;
    font-weight: bold;
    margin-left: 42px;
    margin-top: -1px;
  }

  .logo-mark {
    display: inline-block;
    width: 28px;
    height: 28px;
    border: 7px solid #000;
    margin-right: 7px;
    vertical-align: middle;
    position: relative;
    top: -2px;
  }

  .logo-mark::after {
    content: "";
    position: absolute;
    width: 9px;
    height: 9px;
    border: 4px solid #000;
    left: 4px;
    top: 4px;
    background: #fff;
  }

.voucher-title {
  text-align: center;
  font-size: 32px;
  font-weight: 900;
  line-height: 1.1;
  padding-top: 7mm;
}

  .title-lines {
    width: 45mm;
    margin: 1.5mm auto 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3mm;
  }

  .title-lines span {
    height: 1px;
    background: #000;
    flex: 1;
  }

  .title-lines b {
    width: 3mm;
    height: 3mm;
    background: #000;
    transform: rotate(45deg);
    display: inline-block;
  }

  .meta-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    font-size: 15px;
    font-weight: bold;
    margin-bottom: 3mm;
    margin-top: -1mm;
  }

  .meta-right {
    text-align: right;
  }

  .meta-left {
    text-align: left;
  }

  .dotted {
    display: inline-block;
    min-width: 38mm;
    border-bottom: 1px dotted #000;
    height: 5mm;
    vertical-align: bottom;
    text-align: center;
  }

  .calendar-icon {
    font-size: 15px;
    margin-right: 3px;
  }

 .field {
  border: 1.5px solid #000;
  height: 8mm;
  margin-bottom: 1.7mm;
  display: flex;
  align-items: center;
  padding: 0 3mm;
  font-size: 13px;
  font-weight: bold;
  text-align: right;
}

.field.large {
  height: 13.5mm;
  align-items: flex-start;
  padding-top: 2mm;
}

  .field-label {
    white-space: nowrap;
    margin-left: 4px;
  }

  .field-value {
    flex: 1;
    text-align: right;
    font-weight: bold;
  }

 .payment-field {
  border: 1.5px solid #000;
  height: 8mm;
  margin-bottom: 1.7mm;
  display: flex;
  align-items: center;
  padding: 0 3mm;
  font-size: 13px;
  font-weight: bold;
  gap: 4mm;
}

.payment-option {
  display: inline-flex;
  align-items: center;
  gap: 1.5mm;
  white-space: nowrap;
}

  .checkbox {
    width: 4mm;
    height: 4mm;
    border: 1.5px solid #000;
    display: inline-block;
    margin: 0 4mm 0 2mm;
    vertical-align: middle;
    position: relative;
    top: 1px;
  }

  .checkbox.checked::after {
    content: "✓";
    position: absolute;
    top: -4px;
    right: 0px;
    font-size: 17px;
    font-weight: bold;
  }
 .bottom-grid {
  display: grid;
  grid-template-columns: 36% 27% 37%;
  gap: 3mm;
  margin-top: 2mm;
  align-items: stretch;
}

.bottom-box {
  border: 1.5px solid #000;
  height: 29mm;
  font-size: 12px;
  font-weight: bold;
  position: relative;
  overflow: hidden;
  background: #fff;
}
  .notes-box {
    padding: 3mm;
    text-align: center;
  }

  .notes-title,
  .stamp-title {
    text-align: center;
    margin-bottom: 3mm;
  }

  .note-line {
    border-bottom: 1px dotted #000;
    height: 6mm;
  }

  .stamp-box {
    padding: 3mm;
    text-align: center;
  }

.stamp-circle {
  width: 18mm;
  height: 18mm;
  border: 1.5px dashed #000;
  border-radius: 50%;
  margin: 1.5mm auto 0 auto;
}

.received-box-title {
  background: #000;
  color: #fff;
  height: 7mm;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
}
.receiver-box {
  display: flex;
  flex-direction: column;
}

.receiver-header {
  background: #000;
  color: #fff;
  text-align: center;
  font-weight: bold;
  padding: 2mm 0;
  font-size: 12px;
}

.receiver-body {
  flex: 1;
  padding: 2mm 3mm 1.5mm 3mm;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  box-sizing: border-box;
}

.receiver-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2mm;
  font-size: 12px;
  line-height: 1.4;
}

.receiver-label {
  white-space: nowrap;
  margin-left: 2mm;
}

.receiver-line {
  flex: 1;
  border-bottom: 1px dotted #000;
  min-height: 5mm;
  display: flex;
  align-items: center;
  padding: 0 2mm;
  box-sizing: border-box;
}

.receiver-signature {
  margin-top: auto;
}
.received-content {
  padding: 2mm 3mm 1mm 3mm;
  font-size: 12px;
}

.received-line {
  display: flex;
  align-items: center;
  height: 6mm;
}

  .received-label {
    width: 18mm;
    white-space: nowrap;
  }

  .received-dots {
    flex: 1;
    border-bottom: 1px dotted #000;
    height: 5mm;
    text-align: center;
    font-size: 12px;
  }

  @media print {
    @page {
      size: A4 portrait;
      margin: 0;
    }

    body {
      margin: 0;
      padding: 0;
    }

    .print-page {
      page-break-after: avoid;
    }
  }
</style>
</head>

<body>
  <div class="print-page">
    <div class="voucher">

      <div class="top-row">
        <div class="logo-area">
          <div class="logo-main">
            <span class="logo-mark"></span>Artika
          </div>
          <div class="logo-sub">smart art. human touch</div>
        </div>

        <div>
          <div class="voucher-title">${title}</div>
          <div class="title-lines">
            <span></span>
            <b></b>
            <span></span>
          </div>
        </div>

        <div></div>
      </div>

      <div class="meta-row">
        <div class="meta-right">
          رقم السند :
          <span class="dotted">${voucherPayload.voucherNumber || ""}</span>
        </div>

        <div class="meta-left">
          <span class="calendar-icon">▣</span>
          التاريخ :
          <span class="dotted">${voucherPayload.date || ""}</span>
        </div>
      </div>

      <div class="field">
        <span class="field-label">
          ${voucherType === "قبض" ? "استلمنا من السيد / السادة :" : "اسم المستفيد :"}
        </span>
        <span class="field-value">${voucherPayload.personName || ""}</span>
      </div>

      <div class="field">
        <span class="field-label">مبلغ وقدره :</span>
        <span class="field-value">${amountValue.toLocaleString("en-US")} د.ع</span>
      </div>

      <div class="field">
        <span class="field-label">المبلغ كتابة :</span>
        <span class="field-value">${amountWords}</span>
      </div>

      <div class="field large">
        <span class="field-label">
          ${voucherType === "قبض" ? "وذلك عن / سبب القبض :" : "سبب الصرف / البيان :"}
        </span>
        <span class="field-value">${voucherPayload.reason || ""}</span>
      </div>

   <div class="payment-field">
  <span class="field-label">طريقة الدفع :</span>

  <span class="payment-option">
    <span class="checkbox ${voucherPayload.paymentMethod === "كاش" ? "checked" : ""}"></span>
    <span>نقداً</span>
  </span>

  <span class="payment-option">
    <span class="checkbox ${voucherPayload.paymentMethod === "كي كارد" ? "checked" : ""}"></span>
    <span>كي كارد</span>
  </span>

  <span class="payment-option">
    <span class="checkbox ${voucherPayload.paymentMethod === "زين كاش" ? "checked" : ""}"></span>
    <span>زين كاش</span>
  </span>

  <span class="payment-option">
    <span class="checkbox ${voucherPayload.paymentMethod === "FIB" ? "checked" : ""}"></span>
    <span>FIB</span>
  </span>
</div>

      <div class="field">
        <span class="field-label">رقم المرجع / رقم الفاتورة :</span>
        <span class="field-value">${voucherPayload.invoiceNumber || ""}</span>
      </div>

      <div class="bottom-grid">
        <div class="bottom-box notes-box">
          <div class="notes-title">ملاحظات</div>
          <div class="note-line">${voucherPayload.notes || ""}</div>
          <div class="note-line"></div>
          <div class="note-line"></div>
          <div class="note-line"></div>
        </div>

        <div class="bottom-box stamp-box">
          <div class="stamp-title">الختم</div>
          <div class="stamp-circle"></div>
        </div>

        <div class="bottom-box">
          <div class="received-box-title">استلمت بواسطة</div>

          <div class="received-content">
            <div class="received-line">
              <span class="received-label">الاسم :</span>
              <span class="received-dots">${voucherPayload.receivedBy || ""}</span>
            </div>

            <div class="received-line">
              <span class="received-label">التاريخ :</span>
              <span class="received-dots">${voucherPayload.date || ""}</span>
            </div>

            <div class="received-line">
              <span class="received-label">التوقيع :</span>
              <span class="received-dots"></span>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>

  <script>
    window.onload = function() {
      window.focus();
      window.print();
    };
  </script>
</body>
</html>
`;

  const printWindow = window.open("", "_blank", "width=1000,height=700");
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();

  setVoucherDialogOpen(false);
  fetchRecords();

  MySwal.fire({
    icon: "success",
    title: "تم إنشاء السند",
    timer: 1200,
    showConfirmButton: false,
  });
};

const getCreditAccountBalanceByName = (accountName, excludeRecordId = null) => {
  return allRecords.reduce((balance, r) => {
    if (excludeRecordId && r.id === excludeRecordId) return balance;

    if (!r.isCreditAccount && !r.creditAction) return balance;

    const name = r.creditAccountName || r.accountName;
    if (name !== accountName) return balance;

    const amountValue = Number(r.amountIQD || 0);

    if (r.creditAction === "add_invoice") {
      return balance + amountValue;
    }

    if (r.creditAction === "payment" || r.creditAction === "discount") {
      return balance - amountValue;
    }

    return balance;
  }, 0);
};

const getRecordDateText = (record) => {
  const d =
    record?.date?.toDate?.() ||
    record?.timestamp?.toDate?.() ||
    null;

  if (!d) return "-";

  return d.toLocaleDateString("en-GB");
};

const getRecordDateValue = (record) => {
  const d =
    record?.date?.toDate?.() ||
    record?.timestamp?.toDate?.() ||
    null;

  return d || new Date();
};

const getCreditRecordLabel = (record) => {
  if (record.creditAction === "add_invoice") return "إضافة فاتورة آجلة";
  if (record.creditAction === "payment") return "تسديد دفعة على الحساب";
  if (record.creditAction === "discount") return "خصم على الحساب";
  return "-";
};

const selectedCreditAccountRecords = useMemo(() => {
  if (!selectedCreditAccount) return [];

  return allRecords
    .filter((r) => {
      if (!r.isCreditAccount && !r.creditAction) return false;

      const name = r.creditAccountName || r.accountName;
      return name === selectedCreditAccount;
    })
    .sort((a, b) => {
      const da = getRecordDateValue(a).getTime();
      const db = getRecordDateValue(b).getTime();
      return db - da;
    });
}, [allRecords, selectedCreditAccount]);

const selectedCreditAccountBalance = useMemo(() => {
  if (!selectedCreditAccount) return 0;
  return creditAccountStats.balances[selectedCreditAccount] || 0;
}, [creditAccountStats, selectedCreditAccount]);
// ✅ من fetchInvoiceCostHistory (كل الفواتير المعتمدة تاريخياً) بدل سجلات
// accounting_pro المحدودة بفواتير اعتُمدت بعد إضافة الترحيل التلقائي فقط
const selectedInvoiceCostAccountRecords = useMemo(() => {
  if (!selectedCreditAccount) return [];

  return [...(invoiceCostHistoryRecords[selectedCreditAccount] || [])].sort((a, b) => {
    const da = getRecordDateValue(a).getTime();
    const db = getRecordDateValue(b).getTime();
    return db - da;
  });
}, [invoiceCostHistoryRecords, selectedCreditAccount]);
const selectedEmployeeAccountRecords = useMemo(() => {
  if (!selectedCreditAccount) return [];

  return allRecords
    .filter(
      (r) =>
        r.isEmployeeAccount &&
        r.employeeAccountName === selectedCreditAccount
    )
    .sort((a, b) => {
      const da = getRecordDateValue(a).getTime();
      const db = getRecordDateValue(b).getTime();
      return db - da;
    });
}, [allRecords, selectedCreditAccount]);

const selectedIsEmployeeAccount = useMemo(() => {
  return EMPLOYEE_ACCOUNTS.includes(selectedCreditAccount);
}, [selectedCreditAccount]);
const selectedIsInvoiceCostAccount = useMemo(() => {
  return INVOICE_COST_ACCOUNTS.includes(selectedCreditAccount);
}, [selectedCreditAccount]);

const selectedDetailsRecords = useMemo(() => {
  if (selectedIsEmployeeAccount) {
    return selectedEmployeeAccountRecords;
  }

  if (selectedIsInvoiceCostAccount) {
    return selectedInvoiceCostAccountRecords;
  }

  return selectedCreditAccountRecords;
}, [
  selectedIsEmployeeAccount,
  selectedEmployeeAccountRecords,
  selectedIsInvoiceCostAccount,
  selectedInvoiceCostAccountRecords,
  selectedCreditAccountRecords,
]);

const selectedDetailsBalance = useMemo(() => {
  if (selectedIsEmployeeAccount) {
    return employeeAccountStats.balances[selectedCreditAccount] || 0;
  }

  if (selectedIsInvoiceCostAccount) {
    return invoiceCostAccountStats.balances[selectedCreditAccount] || 0;
  }

  return selectedCreditAccountBalance || 0;
}, [
  selectedIsEmployeeAccount,
  employeeAccountStats,
  selectedIsInvoiceCostAccount,
  invoiceCostAccountStats,
  selectedCreditAccount,
  selectedCreditAccountBalance,
]);
const showSelectedDetailsAmountTotal = useMemo(() => {
  return (
    !selectedIsEmployeeAccount &&
    !selectedIsInvoiceCostAccount &&
    CREDIT_ACCOUNTS_WITH_AMOUNT_TOTAL.includes(selectedCreditAccount)
  );
}, [selectedIsEmployeeAccount, selectedIsInvoiceCostAccount, selectedCreditAccount]);
const selectedDetailsAmountTotal = useMemo(() => {
  return selectedDetailsRecords
    .filter((r) => r.creditAction === "add_invoice")
    .reduce((sum, r) => sum + Number(r.amountIQD || 0), 0);
}, [selectedDetailsRecords]);
const exportSelectedCreditAccountExcel = () => {
 if (!selectedCreditAccount || selectedDetailsRecords.length === 0) {
    return MySwal.fire({
      icon: "info",
      title: "لا توجد بيانات",
      text: "لا توجد قيود لهذا الحساب",
    });
  }

 const rows = selectedDetailsRecords.map((r, index) => {
    const walletName = getWalletName(r.wallet);

return {
  "تسلسل": index + 1,
  "اسم الحساب": selectedCreditAccount,
"نوع الحركة": r.isEmployeeAccount
  ? "إضافة فاتورة موظف"
  : r.isInvoiceCostAccount
  ? "كلفة فاتورة"
  : getCreditRecordLabel(r),
  "التاريخ": getRecordDateValue(r),
  "الوصف": r.processName || "-",
"التبويب": r.isEmployeeAccount || r.isInvoiceCostAccount ? "" : r.accountingCategory || "-",
  "المبلغ": Number(r.amountIQD || 0),
"الصندوق": r.isEmployeeAccount || r.isInvoiceCostAccount
  ? "-"
  : r.creditAction === "add_invoice" || r.creditAction === "discount"
  ? "بدون صندوق"
  : walletName,
 "الموقع": r.isEmployeeAccount || r.isInvoiceCostAccount ? "-" : r.location || "-",
  "رقم الوصل / الفاتورة": r.invoiceNumber || r.receiptNumber || "-",
  "الملاحظات": r.notes || "-",
};
  });

  const ws = XLSX.utils.json_to_sheet(rows, { cellDates: true });

  ws["!cols"] = [
    { wch: 8 },
    { wch: 28 },
    { wch: 22 },
    { wch: 14 },
    { wch: 35 },
    { wch: 28 },
    { wch: 15 },
    { wch: 15 },
    { wch: 12 },
    { wch: 20 },
    { wch: 30 },
  ];

  for (let row = 2; row <= rows.length + 1; row++) {
    if (ws[`D${row}`]) ws[`D${row}`].z = "m/d/yyyy";
    if (ws[`G${row}`]) ws[`G${row}`].z = '"IQD" #,##0';
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "تفاصيل الحساب الآجل");

  XLSX.writeFile(
    wb,
    `Credit_Account_${selectedCreditAccount.replace(/[\\/:*?"<>|]/g, "_")}.xlsx`
  );
};

const selectedExpenseBox = useMemo(() => {
  return (
    MONTHLY_EXPENSE_BOXES.find((item) => item.key === selectedExpenseKey) ||
    null
  );
}, [selectedExpenseKey]);

const selectedExpenseRecords = useMemo(() => {
  if (!selectedExpenseBox) return [];

  return allRecords
    .filter((record) => {
      const isActualExpense =
        record.type === "صرف" &&
        !record.isInternalTransfer &&
        !record.reportOnly &&
        !record.balanceOnly &&
        record.affectsWallet !== false &&
        record.creditAction !== "add_invoice";

      return isActualExpense && selectedExpenseBox.matches(record);
    })
    .sort((a, b) => {
      const da = getRecordDateValue(a).getTime();
      const db = getRecordDateValue(b).getTime();
      return db - da;
    });
}, [allRecords, selectedExpenseBox]);

const selectedExpenseTotal = useMemo(() => {
  return selectedExpenseRecords.reduce(
    (sum, r) => sum + Number(r.amountIQD || 0),
    0
  );
}, [selectedExpenseRecords]);

const exportSelectedExpenseExcel = () => {
  if (!selectedExpenseBox || selectedExpenseRecords.length === 0) {
    return MySwal.fire({
      icon: "info",
      title: "لا توجد بيانات",
      text: "لا توجد قيود لهذا البند",
    });
  }

  const rows = selectedExpenseRecords.map((r, index) => {
    const walletName = getWalletName(r.wallet);

    return {
      "تسلسل": index + 1,
      "البند": selectedExpenseBox.label,
      "التاريخ": getRecordDateValue(r),
      "الوصف": r.processName || "-",
      "التبويب": r.accountingCategory || "-",
      "المبلغ": Number(r.amountIQD || 0),
      "الصندوق": walletName,
      "الموقع": r.location || "-",
      "رقم الوصل / الفاتورة": r.invoiceNumber || r.receiptNumber || "-",
      "الملاحظات": r.notes || "-",
    };
  });

  const ws = XLSX.utils.json_to_sheet(rows, { cellDates: true });

  ws["!cols"] = [
    { wch: 8 },
    { wch: 22 },
    { wch: 14 },
    { wch: 35 },
    { wch: 28 },
    { wch: 15 },
    { wch: 20 },
    { wch: 15 },
    { wch: 20 },
    { wch: 30 },
  ];

  for (let row = 2; row <= rows.length + 1; row++) {
    if (ws[`C${row}`]) ws[`C${row}`].z = "m/d/yyyy";
    if (ws[`F${row}`]) ws[`F${row}`].z = '"IQD" #,##0';
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "تفاصيل المصروف");

  XLSX.writeFile(
    wb,
    `Monthly_Expense_${selectedExpenseBox.label.replace(/[\\/:*?"<>|]/g, "_")}.xlsx`
  );
};

  const handleSave = async () => {
    if (isBaneen && isReconciliationMissed) {
      MySwal.fire({
        icon: "warning",
        title: "لم تتم مطابقة الصندوق",
        text: "لم تتم مطابقة حساب الصندوق مع الفعلي في يوم العمل السابق. يرجى مطابقة الصندوق أولاً ثم إضافة الحركة الجديدة.",
        confirmButtonText: "مطابقة الصندوق الآن",
        confirmButtonColor: "#d32f2f",
        showCancelButton: true,
        cancelButtonText: "إلغاء",
      }).then((result) => {
        if (result.isConfirmed) openReconcileDialog();
      });
      return;
    }
    if (!processName.trim() || !amount) {
        let missing = [];
        if (!processName.trim()) missing.push("اسم العملية");
        if (!amount) missing.push("المبلغ");
        return MySwal.fire({ icon: 'error', title: 'بيانات ناقصة', text: `يرجى إكمال: ${missing.join(" و ")}` });
    }
    if (isEmployeeAccountMode && !employeeAccountName) {
  return MySwal.fire({
    icon: "error",
    title: "بيانات ناقصة",
    text: "يرجى اختيار اسم الموظف",
  });
}

if (isEmployeeAccountMode && files.length === 0 && existingInvoiceUrls.length === 0) {
  return MySwal.fire({
    icon: "error",
    title: "بيانات ناقصة",
    text: "يرجى رفع ملف أو صورة لهذا القيد",
  });
}
if (isCreditAccountMode && !creditAccountName) {
  return MySwal.fire({
    icon: "error",
    title: "بيانات ناقصة",
    text: "يرجى اختيار اسم الحساب",
  });
}
if (isCreditAccountMode && (creditAction === "payment" || creditAction === "discount")) {
  const currentCreditBalance = getCreditAccountBalanceByName(
    creditAccountName,
    editingId
  );

  if (finalAmountIQD > currentCreditBalance) {
    const isDiscount = creditAction === "discount";
    return MySwal.fire({
      icon: "warning",
      title: "رصيد الحساب غير كافٍ",
      html: `
        لا يمكن ${isDiscount ? "تسجيل خصم" : "تسديد مبلغ"} أكبر من رصيد الحساب الآجل.<br><br>
        <b>اسم الحساب:</b> ${creditAccountName}<br>
        <b>الرصيد الحالي:</b> ${currentCreditBalance.toLocaleString()} د.ع<br>
        <b>مبلغ ${isDiscount ? "الخصم" : "التسديد"}:</b> ${finalAmountIQD.toLocaleString()} د.ع
      `,
      confirmButtonColor: "#6a1b9a",
    });
  }
}
// ⚠️ الخصم لا يملك وصل/فاتورة فعلياً - رقم الوصل والمرفقات اختياريان له فقط
if (isCreditAccountMode && creditAction !== "discount" && !receiptNumber.trim()) {
  return MySwal.fire({
    icon: "error",
    title: "بيانات ناقصة",
    text: "يرجى إدخال رقم الوصل / رقم الفاتورة",
  });
}



if (
  isCreditAccountMode &&
  creditAction !== "discount" &&
  files.length === 0 &&
  existingInvoiceUrls.length === 0
) {
  return MySwal.fire({
    icon: "error",
    title: "بيانات ناقصة",
    text: creditAction === "add_invoice"
      ? "يرجى رفع صورة أو ملف الفاتورة"
      : "يرجى رفع صورة أو ملف وصل التسديد",
  });
}

if (isCreditAccountMode && creditAction === "payment" && !manualCategory) {
  return MySwal.fire({
    icon: "error",
    title: "بيانات ناقصة",
    text: "يرجى اختيار التبويب المحاسبي",
  });
}
    if (!isIncome && !isCreditAccountMode && isInternalTransfer && wallet === transferTo) {
        return MySwal.fire({ icon: 'error', title: 'خطأ في التحويل', text: 'لا يمكن التحويل من وإلى نفس الصندوق!' });
    }

    // 🔴 فحص الرصيد المتوفر قبل الصرف أو التحويل
  // 🔴 فحص الرصيد فقط عند إدخال قيد جديد
// أما التعديل على قيد قديم فلا نمنعه بسبب الرصيد لأنه حركة صارت وانتهت
const shouldCheckWalletBalance =
  !editingId &&
  (
    (!isIncome && !isCreditAccountMode && !isEmployeeAccountMode) ||
    (isCreditAccountMode && creditAction === "payment")
  );

if (shouldCheckWalletBalance) {
  const availableBalance = walletStats[wallet] || 0;

  if (finalAmountIQD > availableBalance) {
    return MySwal.fire({
      icon: "warning",
      title: "رصيد غير كافٍ",
      html: `عذراً، الرصيد الحالي في <b>${wallet}</b> هو <b>${availableBalance.toLocaleString()} IQD</b> فقط.<br>لا يمكن إتمام العملية بمبلغ <b>${finalAmountIQD.toLocaleString()} IQD</b>.`,
      confirmButtonColor: "#d32f2f",
    });
  }
}

    setLoading(true);
    try {
  const oldRecord = editingId
  ? allRecords.find((r) => r.id === editingId)
  : null;

// إذا تعديل: نبدأ من الملفات القديمة المتبقية بعد الحذف
// إذا إدخال جديد: نبدأ بدون ملفات
let invoiceUrls = editingId
  ? [...existingInvoiceUrls]
  : [];

if (files.length > 0) {
  const uploadedUrls = [];

  for (const selectedFile of files) {
    const safeFileName = selectedFile.name.replace(/[^\w.\-ء-ي ]/g, "_");
    const fileRef = ref(
      storage,
      `accounting/${Date.now()}-${safeFileName}`
    );

    await uploadBytes(fileRef, selectedFile);
    const url = await getDownloadURL(fileRef);
    uploadedUrls.push(url);
  }

  // إذا تعديل: يضيف الملفات الجديدة فوق القديمة
  // إذا إدخال جديد: يحفظ الملفات المختارة
  invoiceUrls = editingId ? [...invoiceUrls, ...uploadedUrls] : uploadedUrls;
}

   let dataPayload = {};

if (isEmployeeAccountMode) {
  // ✅ Phase 07-B2c: فرع حساب موظف مُرحَّل للباك اند - آخر فرع متبقٍ
  // بـhandleSave، بهذا handleSave بالكامل صار بلا أي كتابة مباشرة. بنفس
  // أسلوب 07-B2a/07-B2b: لا يمر بكتلة addDoc/updateDoc المشتركة أدناه
  // (أصبحت الآن غير قابلة للوصول من أي فرع - كود ميت مؤكَّد، لم يُحذف).
  // ⚠️ فرع PUT هنا غير قابل للوصول عملياً بالممارسة: handleEdit (أسفل هذا
  // الملف) يفرض isEmployeeAccountMode=false دائماً عند فتح أي سجل للتعديل،
  // فـeditingId لا يمكن أن يكون truthy مع isEmployeeAccountMode=true معاً -
  // نُفِّذه فقط للتناسق مع بقية الأوضاع ودفاعياً بالباك اند.
  const employeePayload = {
    mode: "employee",
    employeeAccountName,
    processName,
    amountOriginal: Number(amount),
    notes: note,
    date: selectedDate,
    invoiceUrl: invoiceUrls[0] || "",
    invoiceUrls,
  };

  if (editingId) {
    const res = await apiFetch(`${API_BASE}/accounting/${editingId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(employeePayload),
    });
    if (!res.ok) {
      throw new Error(await res.text().catch(() => ""));
    }

    cancelEdit();

    await MySwal.fire({
      icon: 'success',
      title: 'تم التحديث',
      timer: 1500,
      showConfirmButton: false
    });

    fetchRecords();
  } else {
    const res = await apiFetch(`${API_BASE}/accounting`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(employeePayload),
    });
    if (!res.ok) {
      throw new Error(await res.text().catch(() => ""));
    }

    cancelEdit();

    await MySwal.fire({
      icon: "success",
      title: "تمت إضافة قيد الموظف",
      text: `تمت إضافة المبلغ على حساب: ${employeeAccountName}`,
      timer: 1800,
      showConfirmButton: false,
    });

    fetchRecords();
  }

  setLoading(false);
  return;
} else if (isCreditAccountMode) {
  // ✅ Phase 07-B2b: فرع حساب آجل (إضافة فاتورة/تسديد) مُرحَّل للباك اند -
  // بنفس أسلوب الفرع العادي (07-B2a): لا يمر بكتلة addDoc/updateDoc
  // المشتركة أدناه (أصبحت غير قابلة للوصول من أي فرع بعد Phase 07-B2c -
  // كود ميت مؤكَّد، لم يُحذف). amountIQD لا تُرسَل - السيرفر يعيد حسابها.
  const creditPayload = {
    mode: "credit",
    creditAction,
    creditAccountName,
    processName,
    amountOriginal: Number(amount),
    currency,
    exchangeRate: currency === "USD" ? Number(exchangeRate) : "-",
    receiptNumber,
    location,
    wallet: creditAction === "payment" ? wallet : null,
    accountingCategory: creditAction === "payment" ? manualCategory : "",
    showForBaneen,
    notes: note,
    date: selectedDate,
    invoiceUrl: invoiceUrls[0] || "",
    invoiceUrls,
  };

  if (editingId) {
    const res = await apiFetch(`${API_BASE}/accounting/${editingId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(creditPayload),
    });
    if (!res.ok) {
      throw new Error(await res.text().catch(() => ""));
    }

    cancelEdit();

    await MySwal.fire({
      icon: 'success',
      title: 'تم التحديث',
      timer: 1500,
      showConfirmButton: false
    });

    fetchRecords();
  } else {
    const res = await apiFetch(`${API_BASE}/accounting`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(creditPayload),
    });
    if (!res.ok) {
      throw new Error(await res.text().catch(() => ""));
    }
    const { id: newRecordId } = await res.json();

    cancelEdit();

    if (creditAction === "add_invoice") {
      await MySwal.fire({
        icon: "success",
        title: "تمت إضافة الفاتورة الآجلة",
        text: `تمت إضافة المبلغ على حساب: ${creditAccountName}`,
        timer: 1800,
        showConfirmButton: false,
      });
    } else if (creditAction === "discount") {
      // خصم ليس صرف فلوس - لا نعرض سؤال طباعة سند صرف إطلاقاً
      await MySwal.fire({
        icon: "success",
        title: "تمت إضافة الخصم",
        text: `تم تسجيل خصم بمبلغ ${finalAmountIQD.toLocaleString()} على حساب: ${creditAccountName}`,
        timer: 1800,
        showConfirmButton: false,
      });
    } else {
      const result = await MySwal.fire({
        icon: "question",
        title: "هل تريد طباعة سند صرف؟",
        text: "تم ترحيل صرف بنجاح",
        showCancelButton: true,
        confirmButtonText: "نعم، طباعة سند صرف",
        cancelButtonText: "لا",
        confirmButtonColor: "#d32f2f",
      });

      if (result.isConfirmed) {
        openVoucherDialog(newRecordId, "صرف", {
          amountIQD: finalAmountIQD,
          amountOriginal: Number(amount),
          processName,
          wallet,
          receiptNumber,
        });
      } else {
        await MySwal.fire({
          icon: 'success',
          title: 'تم الحفظ',
          timer: 1500,
          showConfirmButton: false
        });
      }
    }

    fetchRecords();
  }

  setLoading(false);
  return;
} else {
  // ✅ Phase 07-B2a: النمط العادي (قبض/صرف/تحويل داخلي) مُرحَّل للباك اند -
  // لا يمر بكتلة addDoc/updateDoc المشتركة أدناه (أصبحت غير قابلة للوصول
  // من أي فرع بعد Phase 07-B2c - كود ميت مؤكَّد، لم يُحذف). amountIQD لا
  // تُرسَل - السيرفر يعيد حسابها من amountOriginal+currency+exchangeRate.
  const computedType = isIncome ? "قبض" : "صرف";
  const computedIsInternalTransfer = !isIncome && isInternalTransfer;

  const normalPayload = {
    mode: "normal",
    isIncome,
    isInternalTransfer,
    accountingCategory: manualCategory,
    transferTo: !isIncome && isInternalTransfer ? transferTo : null,
    receiptNumber,
    location,
    amountOriginal: Number(amount),
    currency,
    showForBaneen,
    wallet,
    exchangeRate: currency === "USD" ? Number(exchangeRate) : "-",
    processName,
    notes: note,
    date: selectedDate,
    invoiceUrl: invoiceUrls[0] || "",
    invoiceUrls,
  };

  if (editingId) {
    const res = await apiFetch(`${API_BASE}/accounting/${editingId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(normalPayload),
    });
    if (!res.ok) {
      throw new Error(await res.text().catch(() => ""));
    }

    cancelEdit();

    await MySwal.fire({
      icon: 'success',
      title: 'تم التحديث',
      timer: 1500,
      showConfirmButton: false
    });

    fetchRecords();
  } else {
    const res = await apiFetch(`${API_BASE}/accounting`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...normalPayload,
        linkedOrderIds: showOrderLinking ? linkedOrderIds : [],
      }),
    });
    if (!res.ok) {
      throw new Error(await res.text().catch(() => ""));
    }
    const { id: newRecordId } = await res.json();

    cancelEdit();

    if (!computedIsInternalTransfer) {
      const result = await MySwal.fire({
        icon: "question",
        title: `هل تريد طباعة سند ${computedType}؟`,
        text: `تم ترحيل ${computedType} بنجاح`,
        showCancelButton: true,
        confirmButtonText: `نعم، طباعة سند ${computedType}`,
        cancelButtonText: "لا",
        confirmButtonColor: computedType === "قبض" ? "#2e7d32" : "#d32f2f",
      });

      if (result.isConfirmed) {
        openVoucherDialog(newRecordId, computedType, {
          amountIQD: finalAmountIQD,
          amountOriginal: Number(amount),
          processName,
          wallet,
          receiptNumber: receiptNumber || "بدون",
        });
      } else {
        await MySwal.fire({
          icon: 'success',
          title: 'تم الحفظ',
          timer: 1500,
          showConfirmButton: false
        });
      }
    } else {
      await MySwal.fire({
        icon: 'success',
        title: 'تم ترحيل التحويل الداخلي',
        timer: 1500,
        showConfirmButton: false
      });
    }

    fetchRecords();
    fetchOrders();
  }

  setLoading(false);
  return;
}

if (editingId) {
  const updatePayload = { ...dataPayload };

  // إذا رجعنا الحركة إلى صرف/قبض عادي، نحذف حقول الحساب الآجل القديمة
  if (!isCreditAccountMode) {
    updatePayload.isCreditAccount = deleteField();
    updatePayload.creditAction = deleteField();
    updatePayload.creditAccountName = deleteField();
    updatePayload.accountName = deleteField();
    updatePayload.affectsWallet = deleteField();
    updatePayload.affectsExpense = deleteField();
  }

  // إذا الحركة مو حساب موظف، نحذف حقول حساب الموظف القديمة احتياطًا
  if (!isEmployeeAccountMode) {
    updatePayload.isEmployeeAccount = deleteField();
    updatePayload.employeeAccountAction = deleteField();
    updatePayload.employeeAccountName = deleteField();
  }

  await updateDoc(doc(db, "accounting_pro", editingId), updatePayload);

  cancelEdit();

  await MySwal.fire({
    icon: 'success',
    title: 'تم التحديث',
    timer: 1500,
    showConfirmButton: false
  });

  fetchRecords();
} else {
  const docRef = await addDoc(collection(db, "accounting_pro"), dataPayload);

  // ✅ نسكر نافذة الإدخال قبل ما نطلع أي سؤال/تأكيد (SweetAlert)
  // حتى ما تنرسم نافذة التأكيد وراء نافذة الإدخال وتضل مخفية عن المستخدم
  cancelEdit();

if (dataPayload.isEmployeeAccount) {
  await MySwal.fire({
    icon: "success",
    title: "تمت إضافة قيد الموظف",
    text: `تمت إضافة المبلغ على حساب: ${dataPayload.employeeAccountName}`,
    timer: 1800,
    showConfirmButton: false,
  });
}
else
 if (dataPayload.isCreditAccount && dataPayload.creditAction === "add_invoice") {
  await MySwal.fire({
    icon: "success",
    title: "تمت إضافة الفاتورة الآجلة",
    text: `تمت إضافة المبلغ على حساب: ${dataPayload.creditAccountName}`,
    timer: 1800,
    showConfirmButton: false,
  });
} else if (!dataPayload.isInternalTransfer) {
    const result = await MySwal.fire({
      icon: "question",
      title: `هل تريد طباعة سند ${dataPayload.type}؟`,
      text: `تم ترحيل ${dataPayload.type} بنجاح`,
      showCancelButton: true,
      confirmButtonText: `نعم، طباعة سند ${dataPayload.type}`,
      cancelButtonText: "لا",
      confirmButtonColor: dataPayload.type === "قبض" ? "#2e7d32" : "#d32f2f",
    });

    if (result.isConfirmed) {
      openVoucherDialog(docRef.id, dataPayload.type, dataPayload);
    } else {
      await MySwal.fire({
        icon: 'success',
        title: 'تم الحفظ',
        timer: 1500,
        showConfirmButton: false
      });
    }
  } else {
    await MySwal.fire({
      icon: 'success',
      title: 'تم ترحيل التحويل الداخلي',
      timer: 1500,
      showConfirmButton: false
    });
  }

  fetchRecords();
}
    } catch (e) { MySwal.fire({ icon: 'error', title: 'خطأ', text: e.message }); }
    setLoading(false);
  };

  const handleEdit = (r) => {
    if (r.autoFromOrder) {
      MySwal.fire('غير مسموح', 'هذا قيد تلقائي جاي من طلب (دفعة أولية) ولا يمكن تعديله.', 'warning');
      return;
    }
    setEditingId(r.id);
    setFiles([]);
setExistingInvoiceUrls(getRecordAttachments(r));
    setIsIncome(r.type === "قبض");
    setIsInternalTransfer(!!r.isInternalTransfer);
const recordIsCredit = !!r.isCreditAccount || !!r.creditAction;

setIsEmployeeAccountMode(false);
setEmployeeAccountName("");

setIsCreditAccountMode(recordIsCredit);
setCreditAction(recordIsCredit ? (r.creditAction || "payment") : "payment");
setCreditAccountName(recordIsCredit ? (r.creditAccountName || r.accountName || "") : "");
    if (r.isInternalTransfer && r.transferTo) {
      setTransferTo(r.transferTo);
    }
    setProcessName(r.processName);
    setAmount(r.amountOriginal);
    setCurrency(r.currency);
    setExchangeRate(r.currency === "USD" ? r.exchangeRate : 1500);
    setManualCategory(r.accountingCategory);
    setReceiptNumber(r.receiptNumber === "بدون" ? "" : r.receiptNumber);
    setLocation(r.location);
    setNote(r.notes === "-" ? "" : r.notes);
    setWallet(r.wallet);
    setSelectedDate(r.date?.toDate().toISOString().split('T')[0]);
    setShowForBaneen(r.showForBaneen !== false);
    setFormDialogOpen(true);
  };

 const resetFormFields = () => {
  setProcessName("");
  setAmount("");
  setCurrency("IQD");
  setExchangeRate(1500);
  setReceiptNumber("");
  setLocation("بغداد");
  setNote("");
  setFiles([]);
  setExistingInvoiceUrls([]);
  setWallet("baghdad");
  setTransferTo("baghdad");
  setIsInternalTransfer(false);
setIsCreditAccountMode(false);
setCreditAction("add_invoice");
setCreditAccountName("");
setIsEmployeeAccountMode(false);
setEmployeeAccountName("");
setShowForBaneen(true);
setShowOrderLinking(false);
setLinkedOrderIds([]);
  setSelectedDate(new Date().toISOString().split('T')[0]);
};

const cancelEdit = () => {
  setEditingId(null);
  resetFormFields();
  setFormDialogOpen(false);
};

const openNewEntryDialog = () => {
  setEditingId(null);
  resetFormFields();
  setFormDialogOpen(true);
};

// 💳 قيد صرف جديد من المدير: الصندوق الافتراضي كي كارد (يقدر يغيّره)
useEffect(() => {
  if (isAdmin && formDialogOpen && !editingId && !isIncome) {
    setWallet("qi");
  }
}, [isAdmin, formDialogOpen, editingId, isIncome]);

  const handleDelete = async (r) => {
    const id = typeof r === "string" ? r : r?.id;
    const isAutoFromOrder = typeof r === "object" && !!r?.autoFromOrder;
    if (isAutoFromOrder) {
      MySwal.fire('غير مسموح', 'هذا قيد تلقائي جاي من طلب (دفعة أولية) ولا يمكن حذفه.', 'warning');
      return;
    }
    MySwal.fire({
      title: 'هل أنت متأكد؟',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'نعم، احذف',
      cancelButtonText: 'إلغاء',
      confirmButtonColor: '#d32f2f'
    }).then(async (result) => {
      if (result.isConfirmed) {
        const res = await apiFetch(`${API_BASE}/accounting/${id}`, {
          method: "DELETE",
        });
        if (!res.ok) {
          console.error("خطأ أثناء حذف القيد المحاسبي:", await res.text().catch(() => ""));
          return;
        }
        fetchRecords();
        MySwal.fire('تم!', 'تم الحذف بنجاح.', 'success');
      }
    });
  };

  // 🔄 مطابقة الحسابات: تسجل حركة خاصة تبين بشكل شريط ملون بسجل القيود (أخضر
  // للمطابق، أحمر لغير المطابق مع السبب) - ما تأثر على أي رصيد صندوق
  const openReconcileDialog = () => {
    setReconcileMode(null);
    setReconcileReason("");
    setReconcileDialogOpen(true);
  };

  const saveReconciliation = async (status, reason = "") => {
    if (reconcileSavingRef.current) return;
    reconcileSavingRef.current = true;

    try {
      setReconcileSaving(true);

      // ✅ Phase 07-B4: يستبدل addDoc المباشر - كل الحقول الثابتة (isReconciliation/
      // type/amountIQD:0/wallet:null/date/timestamp/user/createdBy) تُحسَب
      // بالسيرفر الآن من status/reason فقط.
      const reconcileRes = await apiFetch(`${API_BASE}/accounting/reconciliation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, ...(status === "unmatched" ? { reason } : {}) }),
      });
      // 409 = السيرفر لقى مطابقة انحفظت قبل لحظات (ضغطة مكررة) - نعتبرها محفوظة
      if (!reconcileRes.ok && reconcileRes.status !== 409) {
        throw new Error(await reconcileRes.text().catch(() => ""));
      }

      await fetchRecords();
      setReconcileDialogOpen(false);
    } catch (err) {
      console.error("Error saving reconciliation:", err);
      MySwal.fire('خطأ', 'حدث خطأ أثناء حفظ المطابقة', 'error');
    } finally {
      reconcileSavingRef.current = false;
      setReconcileSaving(false);
    }
  };

const handleExportExcel = () => {
const recordsForExcel = (allRecords.length > 0 ? allRecords : displayedRecords).filter(
  (r) =>
    !r.hiddenFromExcel &&
    !r.hiddenFromTable &&
    !r.balanceOnly &&
    r.creditAction !== "add_invoice" &&
    r.creditAction !== "discount"
);
if (recordsForExcel.length === 0) {
  return MySwal.fire("لا توجد بيانات لتصديرها");
}

  const getWalletLabel = (walletValue) => {
    if (walletValue === "baghdad") return "كاش بغداد";
    if (walletValue === "erbil") return "كاش أربيل";
    if (walletValue === "zain") return "زين كاش";
    if (walletValue === "fib") return "FIB";
    if (walletValue === "qi") return "كي كارد";
    return walletValue || "-";
  };

  const getRecordDate = (r) => {
    if (r.date?.toDate) return r.date.toDate();
    if (r.timestamp?.toDate) return r.timestamp.toDate();
    return new Date();
  };


const getOperationCode = (r) => {
  if (r.isInternalTransfer) return 3;
if (r.creditAction === "payment") return 2;
  if (r.type === "قبض") return 1;
  if (r.type === "صرف") return 2;
  return "-";
};

const getOperationName = (r) => {
  if (r.isInternalTransfer) return "تحويل داخلي";
  if (r.creditAction === "add_invoice") return "فاتورة آجلة";
if (r.creditAction === "payment") return "صرف";
  return r.type || "-";
};
const buildExcelBalanceMap = () => {
  const stats = {
    zain: 0,
    fib: 0,
    qi: 0,
    baghdad: 0,
    erbil: 0,
  };

  const ordersForBalance = isDateFilterActive ? filteredOrders : orders;
  const recordsForBalance = isDateFilterActive ? filteredAllRecords : allRecords;

  // نفس منطق الصناديق: دفعات الطلبات تدخل بالحساب إذا ما إلها قيد حسابي
  ordersForBalance.forEach((o) => {
    if (!o.payment) return;
    if (o.accountingEntryCreated || o.accountingEntryId) return;

    const amt =
      o.payment?.method === "deposit"
        ? Number(o.payment?.depositAmount || 0)
        : Number(o.price || 0);

    if (o.payment?.provider === "Zain Cash") stats.zain += amt;
    if (o.payment?.provider === "FIB") stats.fib += amt;
    if (o.payment?.provider === "Qi Card") stats.qi += amt;
  });

  const sortedAsc = [...recordsForBalance]
    .filter((r) => !r.reportOnly)
    .sort((a, b) => getRecordDate(a).getTime() - getRecordDate(b).getTime());

  const balanceMap = {};

  sortedAsc.forEach((r) => {
    const amt = Number(r.amountIQD || 0);

    if (r.isInternalTransfer) {
      if (r.wallet && stats[r.wallet] !== undefined) {
        stats[r.wallet] -= amt;
      }

      if (r.transferTo && stats[r.transferTo] !== undefined) {
        stats[r.transferTo] += amt;
      }
    } else {
      const factor = r.type === "قبض" ? 1 : -1;

      if (r.wallet && stats[r.wallet] !== undefined) {
        stats[r.wallet] += amt * factor;
      }
    }

    const cashGeneral = stats.baghdad + stats.erbil;
    const cardBalance = stats.zain + stats.qi + stats.fib;
    const totalBox = cashGeneral + cardBalance;

    balanceMap[r.id] = {
      walletBalanceAfter:
        r.wallet && stats[r.wallet] !== undefined ? stats[r.wallet] : null,
      totalBoxAfter: totalBox,
    };
  });

  return balanceMap;
};

const excelBalanceMap = buildExcelBalanceMap();
const staticOldMonths = {
  "2025-01": { salesTotal: 695000, marketingTotal: 0 },
  "2025-02": { salesTotal: 2060000, marketingTotal: 0 },
  "2025-03": { salesTotal: 3275000, marketingTotal: 0 },
  "2025-04": { salesTotal: 2050000, marketingTotal: 0 },
  "2025-05": { salesTotal: 450000, marketingTotal: 2894000 },
  "2025-06": { salesTotal: 4425000, marketingTotal: 7307000 },
  "2025-07": { salesTotal: 5464000, marketingTotal: 3563000 },
};

const getOrderDateForExcel = (order) => {
  if (!order?.createdAt) return null;

  if (typeof order.createdAt === "string") {
    return new Date(`${order.createdAt}-01T00:00:00`);
  }

  if (order.createdAt?.toDate) {
    return order.createdAt.toDate();
  }

  if (order.createdAt?.seconds) {
    return new Date(order.createdAt.seconds * 1000);
  }

  return null;
};

const buildManagerMonthlySalesRows = () => {
  const monthlySales = {};

  orders.forEach((order) => {
    if (order.excludedFromStats) return;

    const orderDate = getOrderDateForExcel(order);
    if (!orderDate || Number.isNaN(orderDate.getTime())) return;

    const key = `${orderDate.getFullYear()}-${String(
      orderDate.getMonth() + 1
    ).padStart(2, "0")}`;

    // نفس منطق بوكسات المدير: الماركتنك يظهر منفصل، لذلك لا يدخل بالمبيعات الأساسية
    if ((order.saleAccount || "بيع") === "ماركتنك") return;

    monthlySales[key] = (monthlySales[key] || 0) + Number(order.price || 0);
  });

  Object.entries(staticOldMonths).forEach(([month, value]) => {
    monthlySales[month] =
      (monthlySales[month] || 0) + Number(value.salesTotal || 0);
  });

  return Object.entries(monthlySales)
    .sort(([a], [b]) => new Date(a + "-01") - new Date(b + "-01"))
    .map(([month, total]) => ({
      monthDate: new Date(`${month}-01T00:00:00`),
      total,
    }));
};
const recordDates = recordsForExcel.map(getRecordDate);
  const initialFromDate = filterFromDate
    ? new Date(filterFromDate)
    : new Date(Math.min(...recordDates.map((d) => d.getTime())));

  const initialToDate = filterToDate
    ? new Date(filterToDate)
    : new Date(Math.max(...recordDates.map((d) => d.getTime())));

const data = recordsForExcel.map((r, index) => ({
  "كود العملية": getOperationCode(r),

  "اسم العملية": getOperationName(r),

  "التاريخ": getRecordDate(r),

  "التبويب المحاسبي": r.isInternalTransfer
    ? "تحويل داخلي بين الصناديق"
    : r.accountingCategory || "-",

  "الشرح": r.processName || "-",

  "الموقع": r.location || "-",

  "المبلغ (دينار)": Number(r.amountIQD || 0),

  "نوع الدفع": getWalletLabel(r.wallet),

  "رقم العملية": index + 1,

  "من صندوق": r.isInternalTransfer ? getWalletLabel(r.wallet) : "-",

  "الى صندوق": r.isInternalTransfer ? getWalletLabel(r.transferTo) : "-",

  // ✅ نفس أعمدة الجدول داخل السيستم
  "رصيد الصندوق بعد الحركة":
    excelBalanceMap[r.id]?.walletBalanceAfter === null ||
    excelBalanceMap[r.id]?.walletBalanceAfter === undefined
      ? "-"
      : Number(excelBalanceMap[r.id]?.walletBalanceAfter || 0),

  "رصيد الصندوق الكلي بعد الحركة":
    excelBalanceMap[r.id]?.totalBoxAfter === null ||
    excelBalanceMap[r.id]?.totalBoxAfter === undefined
      ? "-"
      : Number(excelBalanceMap[r.id]?.totalBoxAfter || 0),
}));

  const wb = XLSX.utils.book_new();

  // =========================
  // الشيت الأول: البيانات الحالية
  // =========================
  const ws = XLSX.utils.json_to_sheet(data, { cellDates: true });

ws["!cols"] = [
  { wch: 12 },
  { wch: 14 },
  { wch: 14 },
  { wch: 24 },
  { wch: 45 },
  { wch: 12 },
  { wch: 15 },
  { wch: 15 },
  { wch: 14 },
  { wch: 15 },
  { wch: 15 },

  // ✅ الأعمدة الجديدة
  { wch: 22 },
  { wch: 26 },

  // ✅ عمود مساعد مخفي للفلترة
  { wch: 12, hidden: true },
];

  ws["!autofilter"] = { ref: `A1:M${data.length + 1}` };
// ✅ عنوان العمود المخفي L
ws["N1"] = {
  t: "s",
  v: "فلتر التاريخ",
};
  // تنسيق التاريخ والمبلغ بالشيت الأول
 // تنسيق التاريخ والمبلغ بالشيت الأول
for (let row = 2; row <= data.length + 1; row++) {
  if (ws[`C${row}`]) {
    ws[`C${row}`].z = "m/d/yyyy";
  }

 if (ws[`G${row}`]) {
  ws[`G${row}`].z = '"IQD" #,##0';
}

  // ✅ عمود L مخفي: يعطي تسلسل فقط للقيود الواقعة بين تاريخ من/إلى في شيت كشف يومي
  // ✅ تنسيق رصيد الصندوق ورصيد الصندوق الكلي
  if (ws[`L${row}`] && ws[`L${row}`].t === "n") {
    ws[`L${row}`].z = '"IQD" #,##0';
  }

  if (ws[`M${row}`] && ws[`M${row}`].t === "n") {
    ws[`M${row}`].z = '"IQD" #,##0';
  }

  // ✅ عمود N مخفي: يعطي تسلسل فقط للقيود الواقعة بين تاريخ من/إلى في شيت كشف يومي
  ws[`N${row}`] = {
    t: "n",
    f: `IF(AND(C${row}>='كشف يومي'!$A$3,C${row}<='كشف يومي'!$A$4,B${row}<>"تحويل داخلي"),MAX($N$1:N${row - 1})+1,"")`,
  };
}
// ✅ مهم جدًا: توسيع مدى الشيت حتى Excel يحفظ ويقرأ العمود L المخفي
ws["!ref"] = XLSX.utils.encode_range({
  s: { r: 0, c: 0 },
  e: { r: data.length, c: 13 },
});
  XLSX.utils.book_append_sheet(wb, ws, "البيانات المالية");
// =========================
// شيت مخفي: مبيعات المدير الشهرية
// =========================
const monthlySalesRows = buildManagerMonthlySalesRows();

const wsMonthlySales = XLSX.utils.aoa_to_sheet([
  ["الشهر", "المبيعات الكلية"],
  ...monthlySalesRows.map((row) => [row.monthDate, row.total]),
]);

wsMonthlySales["!cols"] = [{ wch: 15 }, { wch: 18 }];

for (let row = 2; row <= monthlySalesRows.length + 1; row++) {
  if (wsMonthlySales[`A${row}`]) {
    wsMonthlySales[`A${row}`].z = "m/d/yyyy";
  }

  if (wsMonthlySales[`B${row}`]) {
    wsMonthlySales[`B${row}`].z = '#,##0';
  }
}

XLSX.utils.book_append_sheet(wb, wsMonthlySales, "مبيعات المدير");

// إخفاء شيت المبيعات المساعد
wb.Workbook = wb.Workbook || {};
wb.Workbook.Sheets = wb.Workbook.Sheets || [];

const monthlySalesSheetIndex = wb.SheetNames.indexOf("مبيعات المدير");
wb.Workbook.Sheets[monthlySalesSheetIndex] = { Hidden: 1 };
  // =========================
  // الشيت الثاني: لوحة التعريف
  // =========================
  const definitionData = [
    ["لوحة التعريف الخاصة بالحركات"],
    [],
    [],
    [],
    [],
    [],
    ["رقم العملية", "نوع العملية"],
    [1, "قبض"],
    [2, "صرف"],
    [3, "تحويل داخلي"],
 [4, "تسديد دفعة على الحساب"],
[5,""],
    [6, ""],
    [7, ""],
    [8, ""],
    [9, ""],
    [10, ""],
  ];

  const wsDefinition = XLSX.utils.aoa_to_sheet(definitionData);

  wsDefinition["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
  ];

  wsDefinition["!cols"] = [
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
  ];

  wsDefinition["!rows"] = [
    { hpt: 35 },
    { hpt: 25 },
    { hpt: 25 },
    { hpt: 25 },
    { hpt: 25 },
    { hpt: 25 },
    { hpt: 36 },
    { hpt: 32 },
    { hpt: 32 },
    { hpt: 32 },
    { hpt: 32 },
    { hpt: 32 },
    { hpt: 32 },
    { hpt: 32 },
    { hpt: 32 },
    { hpt: 32 },
    { hpt: 32 },
  ];

  const titleStyle = {
    font: { bold: true, sz: 26, color: { rgb: "000000" } },
    alignment: { horizontal: "center", vertical: "center" },
    fill: { fgColor: { rgb: "FFFFFF" } },
  };

  const headerStyle = {
    font: { bold: true, sz: 16, color: { rgb: "FFFFFF" } },
    alignment: { horizontal: "center", vertical: "center" },
    fill: { fgColor: { rgb: "163B63" } },
    border: {
      top: { style: "medium", color: { rgb: "000000" } },
      bottom: { style: "medium", color: { rgb: "000000" } },
      left: { style: "medium", color: { rgb: "000000" } },
      right: { style: "medium", color: { rgb: "000000" } },
    },
  };

  const bodyStyle = {
    font: { bold: true, sz: 14, color: { rgb: "000000" } },
    alignment: { horizontal: "center", vertical: "center" },
    fill: { fgColor: { rgb: "C9C9C9" } },
    border: {
      top: { style: "medium", color: { rgb: "000000" } },
      bottom: { style: "medium", color: { rgb: "000000" } },
      left: { style: "medium", color: { rgb: "000000" } },
      right: { style: "medium", color: { rgb: "000000" } },
    },
  };

  if (wsDefinition["A1"]) wsDefinition["A1"].s = titleStyle;

  ["A7", "B7"].forEach((cell) => {
    if (wsDefinition[cell]) wsDefinition[cell].s = headerStyle;
  });

  for (let row = 8; row <= 17; row++) {
    ["A", "B"].forEach((col) => {
      const cell = `${col}${row}`;
      if (wsDefinition[cell]) wsDefinition[cell].s = bodyStyle;
    });
  }

  XLSX.utils.book_append_sheet(wb, wsDefinition, "لوحة التعريف");

  // =========================
  // الشيت الثالث: كشف يومي بالفلاتر والمعادلات
  // =========================
  const dashboardData = Array.from({ length: 80 }, () =>
    Array.from({ length: 11 }, () => "")
  );

  const wsDashboard = XLSX.utils.aoa_to_sheet(dashboardData);

  wsDashboard["!cols"] = [
    { wch: 12 },
    { wch: 16 },
    { wch: 14 },
    { wch: 24 },
    { wch: 45 },
    { wch: 12 },
    { wch: 16 },
    { wch: 16 },
    { wch: 14 },
    { wch: 16 },
    { wch: 16 },
  ];

  wsDashboard["!rows"] = [
    { hpt: 35 },
    { hpt: 28 },
    { hpt: 45 },
    { hpt: 45 },
    { hpt: 18 },
    { hpt: 32 },
    { hpt: 18 },
    { hpt: 32 },
    { hpt: 18 },
    { hpt: 32 },
    { hpt: 30 },
    { hpt: 30 },
    { hpt: 34 },
  ];

wsDashboard["!merges"] = [
  { s: { r: 0, c: 0 }, e: { r: 0, c: 10 } }, // العنوان
  { s: { r: 1, c: 0 }, e: { r: 1, c: 9 } },  // التاريخ
  { s: { r: 2, c: 0 }, e: { r: 2, c: 9 } },  // من
  { s: { r: 3, c: 0 }, e: { r: 3, c: 9 } },  // الى

  { s: { r: 5, c: 0 }, e: { r: 5, c: 9 } },  // المبيعات الكلية
  { s: { r: 6, c: 0 }, e: { r: 6, c: 9 } },  // النقد المقبوض الكلي
  { s: { r: 7, c: 0 }, e: { r: 7, c: 9 } },  // النقد المصروف الكلي
  { s: { r: 8, c: 0 }, e: { r: 8, c: 9 } },  // النقد المقبوض اليومي
  { s: { r: 9, c: 0 }, e: { r: 9, c: 9 } },  // النقد المصروف اليومي
  { s: { r: 10, c: 0 }, e: { r: 10, c: 9 } }, // الرصيد المتبقي
];
  const thinBorder = {
    top: { style: "thin", color: { rgb: "000000" } },
    bottom: { style: "thin", color: { rgb: "000000" } },
    left: { style: "thin", color: { rgb: "000000" } },
    right: { style: "thin", color: { rgb: "000000" } },
  };

  const dashboardTitleStyle = {
    font: { bold: true, sz: 24, color: { rgb: "000000" } },
    alignment: { horizontal: "center", vertical: "center" },
    fill: { fgColor: { rgb: "D9D9D9" } },
    border: thinBorder,
  };

  const dashboardLabelStyle = {
    font: { bold: true, sz: 16, color: { rgb: "000000" } },
    alignment: { horizontal: "center", vertical: "center" },
    fill: { fgColor: { rgb: "D9D9D9" } },
    border: thinBorder,
  };

  const dashboardValueStyle = {
    font: { bold: true, sz: 16, color: { rgb: "000000" } },
    alignment: { horizontal: "center", vertical: "center" },
    fill: { fgColor: { rgb: "FFFFFF" } },
    border: thinBorder,
    numFmt: "m/d/yyyy",
  };

  const dashboardMoneyStyle = {
    font: { bold: true, sz: 15, color: { rgb: "000000" } },
    alignment: { horizontal: "center", vertical: "center" },
    fill: { fgColor: { rgb: "D9D9D9" } },
    border: thinBorder,
    numFmt: '"IQD" #,##0',
  };

  const dashboardTableHeaderStyle = {
    font: { bold: true, sz: 13, color: { rgb: "000000" } },
    alignment: { horizontal: "center", vertical: "center" },
    fill: { fgColor: { rgb: "D9D9D9" } },
    border: thinBorder,
  };

  const dashboardTableBodyStyle = {
    font: { bold: true, sz: 12, color: { rgb: "000000" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: thinBorder,
  };

  // العنوان
  wsDashboard["A1"] = {
    t: "s",
    v: "البيانات المالية Cash Movement",
    s: dashboardTitleStyle,
  };

  // التاريخ
  wsDashboard["A2"] = {
    t: "s",
    v: "التاريخ",
    s: dashboardLabelStyle,
  };

  wsDashboard["K3"] = {
    t: "s",
    v: "من",
    s: dashboardLabelStyle,
  };

  wsDashboard["A3"] = {
    t: "d",
    v: initialFromDate,
    z: "m/d/yyyy",
    s: dashboardValueStyle,
  };

  wsDashboard["K4"] = {
    t: "s",
    v: "الى",
    s: dashboardLabelStyle,
  };

  wsDashboard["A4"] = {
    t: "d",
    v: initialToDate,
    z: "m/d/yyyy",
    s: dashboardValueStyle,
  };

// ملخص الكشف اليومي حسب المطلوب

wsDashboard["K6"] = {
  t: "s",
  v: "المبيعات الكلية",
  s: dashboardLabelStyle,
};

wsDashboard["A6"] = {
  t: "n",
  f: 'SUMIFS(\'مبيعات المدير\'!$B:$B,\'مبيعات المدير\'!$A:$A,">="&DATE(YEAR($A$3),MONTH($A$3),1),\'مبيعات المدير\'!$A:$A,"<="&EOMONTH($A$4,0))',
  z: '"IQD" #,##0',
  s: dashboardMoneyStyle,
};

wsDashboard["K7"] = {
  t: "s",
  v: "النقد المقبوض الكلي",
  s: dashboardLabelStyle,
};

wsDashboard["A7"] = {
  t: "n",
  f: 'SUMIFS(\'البيانات المالية\'!$G:$G,\'البيانات المالية\'!$B:$B,"قبض")',
  z: '"IQD" #,##0',
  s: dashboardMoneyStyle,
};

wsDashboard["K8"] = {
  t: "s",
  v: "النقد المصروف الكلي",
  s: dashboardLabelStyle,
};

wsDashboard["A8"] = {
  t: "n",
  f: 'SUMIFS(\'البيانات المالية\'!$G:$G,\'البيانات المالية\'!$B:$B,"صرف")',
  z: '"IQD" #,##0',
  s: dashboardMoneyStyle,
};

// ✅ هاي نفس معادلة النقد المقبوض القديمة، بس غيرنا الاسم فقط
wsDashboard["K9"] = {
  t: "s",
  v: "النقد المقبوض اليومي",
  s: dashboardLabelStyle,
};

wsDashboard["A9"] = {
  t: "n",
  f: 'SUMIFS(\'البيانات المالية\'!$G:$G,\'البيانات المالية\'!$B:$B,"قبض",\'البيانات المالية\'!$C:$C,">="&$A$3,\'البيانات المالية\'!$C:$C,"<="&$A$4)',
  z: '"IQD" #,##0',
  s: dashboardMoneyStyle,
};

// ✅ هاي نفس معادلة النقد المصروف القديمة، بس غيرنا الاسم فقط
wsDashboard["K10"] = {
  t: "s",
  v: "النقد المصروف اليومي",
  s: dashboardLabelStyle,
};

wsDashboard["A10"] = {
  t: "n",
  f: 'SUMIFS(\'البيانات المالية\'!$G:$G,\'البيانات المالية\'!$B:$B,"صرف",\'البيانات المالية\'!$C:$C,">="&$A$3,\'البيانات المالية\'!$C:$C,"<="&$A$4)',
  z: '"IQD" #,##0',
  s: dashboardMoneyStyle,
};

// ✅ الرصيد المتبقي = المقبوض الكلي - المصروف الكلي
wsDashboard["K11"] = {
  t: "s",
  v: "الرصيد المتبقي",
  s: dashboardLabelStyle,
};

wsDashboard["A11"] = {
  t: "n",
  f: "A7-A8",
  z: '"IQD" #,##0',
  s: dashboardMoneyStyle,
};

  // هيدر جدول الحركات
  const dashboardHeaders = [
    "كود العملية",
    "اسم العملية",
    "التاريخ",
    "التبويب المحاسبي",
    "الشرح",
    "الموقع",
    "المبلغ",
    "نوع الدفع",
    "رقم العملية",
    "من صندوق",
    "الى صندوق",
  ];

  dashboardHeaders.forEach((header, index) => {
    const cell = XLSX.utils.encode_cell({ r: 12, c: index });
    wsDashboard[cell] = {
      t: "s",
      v: header,
      s: dashboardTableHeaderStyle,
    };
  });




// قيم أولية عند فتح الملف، حسب التاريخ الموجود A3 و A4
// ✅ جدول تفاعلي مربوط بتاريخ من / إلى داخل الإكسل
// يعتمد على العمود المخفي L في شيت البيانات المالية
const tableStartRow = 14;
const tableEndRow = 120;

const artikaColumns = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K"];

// قيم أولية حتى يفتح الجدول مو فارغ من أول مرة
const dashboardRows = data.filter((row) => {
  if (row["اسم العملية"] === "تحويل داخلي") return false;

  const rowDate = row["التاريخ"];

  if (!(rowDate instanceof Date)) return false;

  const onlyDate = new Date(rowDate);
  onlyDate.setHours(0, 0, 0, 0);

  const from = new Date(initialFromDate);
  from.setHours(0, 0, 0, 0);

  const to = new Date(initialToDate);
  to.setHours(23, 59, 59, 999);

  return onlyDate >= from && onlyDate <= to;
});

for (let excelRow = tableStartRow; excelRow <= tableEndRow; excelRow++) {
  const resultIndex = excelRow - tableStartRow + 1;

  artikaColumns.forEach((sourceCol, colIndex) => {
    const cell = XLSX.utils.encode_cell({
      r: excelRow - 1,
      c: colIndex,
    });

const formula = `IFERROR(INDEX('البيانات المالية'!${sourceCol}$2:${sourceCol}$10000,MATCH(ROWS($A$14:A${excelRow}),'البيانات المالية'!$N$2:$N$10000,0)),"")`;
    const cachedRow = dashboardRows[resultIndex - 1];

    const cachedValues = cachedRow
      ? [
          cachedRow["كود العملية"],
          cachedRow["اسم العملية"],
          cachedRow["التاريخ"],
          cachedRow["التبويب المحاسبي"],
          cachedRow["الشرح"],
          cachedRow["الموقع"],
          cachedRow["المبلغ (دينار)"],
          cachedRow["نوع الدفع"],
          cachedRow["رقم العملية"],
          cachedRow["من صندوق"],
          cachedRow["الى صندوق"],
        ]
      : [];

    const cachedValue = cachedValues[colIndex] ?? "";

    wsDashboard[cell] = {
      t:
        cachedValue instanceof Date
          ? "d"
          : typeof cachedValue === "number"
          ? "n"
          : "s",
      v: cachedValue,
      f: formula,
      s: dashboardTableBodyStyle,
    };

    if (colIndex === 2) {
      wsDashboard[cell].z = "m/d/yyyy";
    }
// كود العملية ورقم العملية أرقام عادية
if (colIndex === 0 || colIndex === 8) {
  wsDashboard[cell].z = '0';
}

// المبلغ فقط يظهر بصيغة IQD
if (colIndex === 6) {
  wsDashboard[cell].z = '"IQD" #,##0';
}
  });
}

// ✅ يجبر Excel يحسب المعادلات عند فتح الملف أو تغيير التاريخ
wb.Workbook = wb.Workbook || {};
wb.Workbook.CalcPr = {
  calcMode: "auto",
  fullCalcOnLoad: true,
  forceFullCalc: true,
};

  wsDashboard["!autofilter"] = { ref: "A13:K13" };

  XLSX.utils.book_append_sheet(wb, wsDashboard, "كشف يومي");
// =========================
// الشيت الرابع: الحسابات الآجلة
// =========================
const creditAccountsRows = Object.entries(creditAccountStats.balances).map(
  ([accountName, balance], index) => ({
    "تسلسل": index + 1,
    "اسم الحساب": accountName,
    "الرصيد الحالي": Number(balance || 0),
  })
);

const totalCreditAccountsAmount = Object.values(creditAccountStats.balances).reduce(
  (sum, value) => sum + Number(value || 0),
  0
);

const creditAccountsSheetData = [
  ["تفاصيل الحسابات الآجلة"],
  [],
  ["إجمالي الحسابات الآجلة", totalCreditAccountsAmount],
  [],
  ["تسلسل", "اسم الحساب", "الرصيد الحالي"],
  ...creditAccountsRows.map((row) => [
    row["تسلسل"],
    row["اسم الحساب"],
    row["الرصيد الحالي"],
  ]),
];

const wsCreditAccounts = XLSX.utils.aoa_to_sheet(creditAccountsSheetData);

wsCreditAccounts["!merges"] = [
  { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } },
];

wsCreditAccounts["!cols"] = [
  { wch: 10 },
  { wch: 35 },
  { wch: 18 },
];

const creditTitleStyle = {
  font: { bold: true, sz: 22, color: { rgb: "000000" } },
  alignment: { horizontal: "center", vertical: "center" },
  fill: { fgColor: { rgb: "EADCF8" } },
  border: thinBorder,
};

const creditSummaryLabelStyle = {
  font: { bold: true, sz: 15, color: { rgb: "FFFFFF" } },
  alignment: { horizontal: "center", vertical: "center" },
  fill: { fgColor: { rgb: "6A1B9A" } },
  border: thinBorder,
};

const creditSummaryValueStyle = {
  font: { bold: true, sz: 16, color: { rgb: "6A1B9A" } },
  alignment: { horizontal: "center", vertical: "center" },
  fill: { fgColor: { rgb: "F3E5F5" } },
  border: thinBorder,
  numFmt: '"IQD" #,##0',
};

const creditHeaderStyle = {
  font: { bold: true, sz: 13, color: { rgb: "FFFFFF" } },
  alignment: { horizontal: "center", vertical: "center" },
  fill: { fgColor: { rgb: "6A1B9A" } },
  border: thinBorder,
};

const creditBodyStyle = {
  font: { bold: true, sz: 12, color: { rgb: "000000" } },
  alignment: { horizontal: "center", vertical: "center" },
  border: thinBorder,
};

const creditAmountPositiveStyle = {
  font: { bold: true, sz: 12, color: { rgb: "6A1B9A" } },
  alignment: { horizontal: "center", vertical: "center" },
  fill: { fgColor: { rgb: "F3E5F5" } },
  border: thinBorder,
  numFmt: '"IQD" #,##0',
};

const creditAmountZeroStyle = {
  font: { bold: true, sz: 12, color: { rgb: "666666" } },
  alignment: { horizontal: "center", vertical: "center" },
  fill: { fgColor: { rgb: "F5F5F5" } },
  border: thinBorder,
  numFmt: '"IQD" #,##0',
};

// العنوان
if (wsCreditAccounts["A1"]) wsCreditAccounts["A1"].s = creditTitleStyle;

// الإجمالي
if (wsCreditAccounts["A3"]) wsCreditAccounts["A3"].s = creditSummaryLabelStyle;
if (wsCreditAccounts["B3"]) {
  wsCreditAccounts["B3"].s = creditSummaryValueStyle;
  wsCreditAccounts["B3"].z = '"IQD" #,##0';
}

// الهيدر
["A5", "B5", "C5"].forEach((cell) => {
  if (wsCreditAccounts[cell]) wsCreditAccounts[cell].s = creditHeaderStyle;
});

// جسم الجدول
const firstCreditDataRow = 6;
const lastCreditDataRow = firstCreditDataRow + creditAccountsRows.length - 1;

for (let row = firstCreditDataRow; row <= lastCreditDataRow; row++) {
["A", "B"].forEach((col) => {
  const cell = `${col}${row}`;
  if (wsCreditAccounts[cell]) wsCreditAccounts[cell].s = creditBodyStyle;
});

  const amountCell = `C${row}`;
  const amountValue = Number(wsCreditAccounts[amountCell]?.v || 0);

  if (wsCreditAccounts[amountCell]) {
    wsCreditAccounts[amountCell].s =
      amountValue > 0 ? creditAmountPositiveStyle : creditAmountZeroStyle;
    wsCreditAccounts[amountCell].z = '"IQD" #,##0';
  }
}

wsCreditAccounts["!autofilter"] = {
  ref: `A5:C${lastCreditDataRow}`,
};

XLSX.utils.book_append_sheet(wb, wsCreditAccounts, "الحسابات الآجلة");
  XLSX.writeFile(wb, `Report_${new Date().toLocaleDateString()}.xlsx`);
};

  return (
    <Container maxWidth="xl" sx={{ mt: 4, direction: "rtl" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1.5}>
        <Typography variant="h5" fontWeight={900}>🏛️ نظام ارتيكا المالي الذكي</Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Box display="flex" justifyContent="flex-start" alignItems="stretch" flexWrap="wrap" gap={2}>
          {isAdmin && (
            <Box sx={{ width: 200 }}>
              <StatCard label="المبيعات الكلية" value={totalSalesAllTime} color="#2e7d32" />
            </Box>
          )}
          {isAdmin && (
            <Box sx={{ width: 200 }}>
              <StatCard label="الإيرادات الكلية" value={totalRevenueAll} color="#1565c0" />
            </Box>
          )}
          {isAdmin && (
            <Box sx={{ width: 200 }}>
              <StatCard label="المصاريف الكلية" value={totalExpenseAll} color="#c62828" />
            </Box>
          )}
        </Box>
      </Paper>

      {/* بوكسات الصناديق */}
{!isBaneen && (
  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: "text.secondary" }}>
    💼 الصناديق
  </Typography>
)}
<Grid container spacing={1.5} mb={3}>
  {!isBaneen && (
    <>
      <Grid item xs={6} sm={4} md={1.5}>
        <StatCard
          label="الصندوق الكلي"
          value={walletStats.totalBox}
          color="#1a237e"
          onClick={() => openWalletDetails("totalBox")}
        />
      </Grid>

      <Grid item xs={6} sm={4} md={1.5}>
        <StatCard
          label="كاش عام"
          value={walletStats.cashGeneral}
          color="#283593"
          onClick={() => openWalletDetails("cashGeneral")}
        />
      </Grid>
    </>
  )}

  <Grid item xs={6} sm={4} md={1.5}>
    <StatCard
      label="كاش بغداد"
      value={walletStats.baghdad}
      color="#1565c0"
      onClick={() => openWalletDetails("baghdad")}
    />
  </Grid>

  {!isBaneen && (
    <>
      <Grid item xs={6} sm={4} md={1.5}>
        <StatCard
          label="كاش أربيل"
          value={walletStats.erbil}
          color="#0277bd"
          onClick={() => openWalletDetails("erbil")}
        />
      </Grid>

      <Grid item xs={6} sm={4} md={1.5}>
        <StatCard
          label="رصيد البطاقات"
          value={walletStats.cardBalance}
          color="#00838f"
          onClick={() => openWalletDetails("cardBalance")}
        />
      </Grid>

      <Grid item xs={6} sm={4} md={1.5}>
        <StatCard
          label="كي كارد"
          value={walletStats.qi}
          color="#00695c"
          onClick={() => openWalletDetails("qi")}
        />
      </Grid>

      <Grid item xs={6} sm={4} md={1.5}>
        <StatCard
          label="زين كاش"
          value={walletStats.zain}
          color="#00796b"
          onClick={() => openWalletDetails("zain")}
        />
      </Grid>

      <Grid item xs={6} sm={4} md={1.5}>
        <StatCard
          label="FIB"
          value={walletStats.fib}
          color="#006064"
          onClick={() => openWalletDetails("fib")}
        />
      </Grid>
    </>
  )}
</Grid>

{!isBaneen && (
  <>
    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: "text.secondary" }}>
      📊 ملخص الشهر - {monthlyDashboardStats.currentMonthLabel}
    </Typography>
    <Grid container spacing={1.5} mb={3}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label="المبيعات الشهرية"
          value={monthlySalesStats.currentMonthTotal}
          color="#2e7d32"
          footerRows={monthlySalesStats.monthRows}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label="المصاريف الشهرية"
          value={monthlyDashboardStats.totalMonthlyExpenses}
          color="#c62828"
          footerRows={monthlyDashboardStats.totalExpenseMonthRows}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label="الواردات الشهرية"
          value={monthlyDashboardStats.totalMonthlyIncome}
          color="#1565c0"
          footerRows={monthlyDashboardStats.totalIncomeMonthRows}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label="الحسابات الآجلة"
          value={creditAccountStats.totalCreditAccounts}
          color="#6a1b9a"
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label="الباقي المتوقع تحصيله من الزبائن"
          value={expectedReceivableStats.total}
          color="#ef6c00"
          subtitle={`${expectedReceivableStats.count.toLocaleString()} طلب غير مدفوع`}
        />
      </Grid>
    </Grid>
  </>
)}

{/* تفاصيل الحسابات الآجلة تحت البوكسات */}
<Paper sx={{ p: 2, mb: 4, borderRadius: 3 }}>
  <Typography fontWeight="bold" sx={{ mb: 2 }}>
    🧾 تفاصيل الحسابات الآجلة
  </Typography>

  <Grid container spacing={1.5}>
   {visibleCreditAccountBalances.map(([name, balance]) => (
      <Grid item xs={12} sm={6} md={3} key={name}>
        <Paper
          variant="outlined"
          onClick={() => {
            setSelectedCreditAccount(name);
            setCreditDetailsOpen(true);
          }}
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: balance > 0 ? "#f3e5f5" : "#f5f5f5",
            cursor: "pointer",
            transition: "0.2s",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: 3,
              borderColor: "#6a1b9a",
            },
          }}
        >
          <Typography fontSize={12} fontWeight="bold">
            {name}
          </Typography>

          <Typography
            fontSize={15}
            fontWeight="900"
            sx={{
              color: balance > 0 ? "#6a1b9a" : "text.secondary",
            }}
          >
            {Number(balance || 0).toLocaleString()} د.ع
          </Typography>
        </Paper>
      </Grid>
    ))}
  </Grid>
</Paper>
{!isBaneen && (
  <Paper sx={{ p: 2, mb: 4, borderRadius: 3 }}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2,
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Typography fontWeight="bold">
        📊 تفاصيل المصاريف الشهرية
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Chip
          label={`المجموع الكلي: ${Number(
            monthlyDashboardStats.expenseBoxesGrandTotal || 0
          ).toLocaleString()} د.ع`}
          variant="outlined"
          size="small"
          sx={{ fontWeight: "bold" }}
        />
      </Box>
    </Box>

    <Grid container spacing={1.5}>
      {monthlyDashboardStats.expenseItems.map((item) => (
        <Grid item xs={12} sm={6} md={3} key={item.key}>
          <Paper
            variant="outlined"
            onClick={() => {
              setSelectedExpenseKey(item.key);
              setExpenseDetailsOpen(true);
            }}
            sx={{
              p: 1.5,
              borderRadius: 2,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              bgcolor: "#fafafa",
              borderColor: item.color,
              cursor: "pointer",
              transition: "0.2s",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: 3,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography
                fontSize={13}
                fontWeight="bold"
                sx={{ color: item.color }}
              >
                {item.label}
              </Typography>

              <Chip
                label={`${Number(item.sharePercent || 0).toFixed(1)}%`}
                size="small"
                title="النسبة من المجموع الكلي (لكل الأشهر)"
                sx={{
                  fontWeight: "bold",
                  color: item.color,
                  borderColor: item.color,
                  flexShrink: 0,
                }}
                variant="outlined"
              />
            </Box>

            <Typography fontSize={18} fontWeight="900" sx={{ mb: 0.3 }}>
              {Number(item.currentMonthTotal || 0).toLocaleString()} د.ع
            </Typography>

            <Typography
              variant="caption"
              sx={{ display: "block", color: "text.secondary", mb: 1 }}
            >
              مصروف هذا الشهر
            </Typography>

            <Box sx={{ borderTop: "1px dashed #ddd", pt: 1, mb: 1 }}>
              {item.monthRows.length > 0 ? (
                item.monthRows.map((month) => (
                  <Box
                    key={month.monthKey}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.6,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {month.label}
                    </Typography>

                    <Typography variant="caption" fontWeight="bold">
                      {Number(month.value || 0).toLocaleString()} د.ع
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  لا توجد حركات مسجلة بعد
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                borderTop: `1px solid ${item.color}`,
                mt: "auto",
                pt: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="caption" fontWeight="bold" sx={{ color: item.color }}>
                المجموع الكلي
              </Typography>
              <Typography fontSize={14} fontWeight="900">
                {Number(item.allMonthsTotal || 0).toLocaleString()} د.ع
              </Typography>
            </Box>
          </Paper>
        </Grid>
      ))}
    </Grid>
  </Paper>
)}
{!isBaneen && (
  <Paper sx={{ p: 2, mb: 4, borderRadius: 3 }}>
    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
      <Typography fontWeight="bold">
        🧾 حسابات كلفة الفواتير
        {invoiceCostHistoryLoading && (
          <Typography component="span" fontSize={12} color="text.secondary" sx={{ mr: 1 }}>
            (جارٍ حساب كل الفواتير...)
          </Typography>
        )}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        disabled={invoiceCostHistoryLoading}
        onClick={fetchInvoiceCostHistory}
      >
        🔄 تحديث
      </Button>
    </Box>

    <Grid container spacing={1.5}>
      <Grid item xs={12} sm={6} md={3}>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: "#ede7f6",
            borderColor: "#5e35b1",
          }}
        >
          <Typography fontSize={12} fontWeight="bold">
            المجموع
          </Typography>

          <Typography fontSize={15} fontWeight="900" sx={{ color: "#5e35b1" }}>
            {Number(
              (invoiceCostAccountStats.balances["مطبعة صدى سومر 2"] || 0) +
                (invoiceCostAccountStats.balances["إطارات توب فريم 2"] || 0)
            ).toLocaleString()}{" "}
            د.ع
          </Typography>
        </Paper>
      </Grid>

      {Object.entries(invoiceCostAccountStats.balances).map(([name, balance]) => (
        <Grid item xs={12} sm={6} md={3} key={name}>
          <Paper
            variant="outlined"
            onClick={() => {
              setSelectedCreditAccount(name);
              setCreditDetailsOpen(true);
            }}
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: balance > 0 ? "#fff8e1" : "#f5f5f5",
              cursor: "pointer",
              transition: "0.2s",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: 3,
                borderColor: "#ef6c00",
              },
            }}
          >
            <Typography fontSize={12} fontWeight="bold">
              {getInvoiceCostAccountLabel(name)}
            </Typography>

            <Typography
              fontSize={15}
              fontWeight="900"
              sx={{
                color: balance > 0 ? "#ef6c00" : "text.secondary",
              }}
            >
              {Number(balance || 0).toLocaleString()} د.ع
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  </Paper>
)}
{!isBaneen && (
  <Paper sx={{ p: 2, mb: 4, borderRadius: 3 }}>
    <Typography fontWeight="bold" sx={{ mb: 2 }}>
      🟣 تفاصيل حسابات الموظفين
    </Typography>

    <Grid container spacing={1.5}>
      {Object.entries(employeeAccountStats.balances).map(([name, balance]) => (
        <Grid item xs={12} sm={6} md={3} key={name}>
          <Paper
            variant="outlined"
            onClick={() => {
              setSelectedCreditAccount(name);
              setCreditDetailsOpen(true);
            }}
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: balance > 0 ? "#f3e5f5" : "#f5f5f5",
              cursor: "pointer",
              transition: "0.2s",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: 3,
                borderColor: "#6a1b9a",
              },
            }}
          >
            <Typography fontSize={12} fontWeight="bold">
              {name}
            </Typography>

            <Typography
              fontSize={15}
              fontWeight="900"
              sx={{
                color: balance > 0 ? "#6a1b9a" : "text.secondary",
              }}
            >
              {Number(balance || 0).toLocaleString()} د.ع
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  </Paper>
)}
  <Dialog
    open={formDialogOpen}
    onClose={cancelEdit}
    maxWidth="sm"
    fullWidth
    dir="rtl"
  >
    <DialogContent sx={{ p: 0 }}>
      <Paper
  elevation={0}
  sx={{
    p: 3,
    borderRadius: 0,
    width: "100%",
   
    borderTop: `8px solid ${
  editingId
    ? "#ff9800"
    : isEmployeeAccountMode
    ? "#6a1b9a"
    : isCreditAccountMode
    ? "#6a1b9a"
    : isIncome
    ? "#2e7d32"
    : "#d32f2f"
}`,
  }}
>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
               <Typography variant="h6" fontWeight="bold">{editingId ? "✏️ تعديل القيد" : "إدخال جديد"}</Typography>
            <FormControlLabel
  control={
    <Switch
      checked={isIncome}
      onChange={(e) => {
        setIsIncome(e.target.checked);
        resetFormFields();
      }}
      color="success"
     disabled={!!editingId || isCreditAccountMode || isEmployeeAccountMode}
    />
  }
  label={
  isEmployeeAccountMode
  ? "حساب موظف 🟣"
  : isCreditAccountMode
  ? "حساب آجل 🧾"
  : isIncome
  ? "قبض 🟢"
  : "صرف 🔴"
  }
/>
            </Box>
{!isEmployeeAccountMode && !isIncome && !(editingId && isInternalTransfer) && (
  <Box mb={2}>
    <FormControlLabel
      control={
        <Switch
          checked={isCreditAccountMode}
          onChange={(e) => {
            const checked = e.target.checked;

            setIsCreditAccountMode(checked);
            setIsIncome(false);
            setIsInternalTransfer(false);

            if (checked) {
              // أثناء التعديل نحوله إلى تسديد حساب آجل، مو إضافة فاتورة
              setCreditAction(editingId ? "payment" : "add_invoice");
              setManualCategory(
                editingId
                  ? (manualCategory?.trim() || "كلفة البضاعة المباعة")
                  : "إضافة فاتورة آجلة"
              );
            } else {
              // يرجع صرف عادي
              setCreditAction("add_invoice");
              setCreditAccountName("");
              setManualCategory(manualCategory?.trim() || suggestedCategory);
            }
          }}
          color="secondary"
        />
      }
      label={
        isCreditAccountMode
          ? "🧾 الحركة حالياً حساب آجل"
          : "🧾 تحويل الحركة إلى حساب آجل"
      }
    />
  </Box>
)}

{!editingId && !isBaneen && (
  <Box mb={2}>
    <FormControlLabel
      control={
        <Switch
          checked={isEmployeeAccountMode}
          onChange={(e) => {
            const checked = e.target.checked;

            setIsEmployeeAccountMode(checked);
            setIsCreditAccountMode(false);
            setIsIncome(false);
            setIsInternalTransfer(false);
            setEmployeeAccountName("");
            setCreditAction("add_invoice");
            setCreditAccountName("");
            setManualCategory("");
            setWallet("baghdad");
          }}
          color="secondary"
        />
      }
      label="🟣 حساب موظف"
    />
  </Box>
)}
            {/* سويتش ميزة التحويل الداخلي، يظهر فقط في حالة الـ "صرف" */}
           {!isIncome && !isCreditAccountMode && !isEmployeeAccountMode && (
              <Box mb={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isInternalTransfer}
                      onChange={(e) => setIsInternalTransfer(e.target.checked)}
                      color="secondary"
                    />
                  }
                  label="⚙️ تحويل داخلي بين الصناديق"
                />
              </Box>
            )}

            <TextField fullWidth type="date" label="التاريخ" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} sx={{ mb: 2 }} InputLabelProps={{ shrink: true }} />
            <TextField fullWidth label="الوصف" value={processName} onChange={(e) => setProcessName(e.target.value)} sx={{ mb: 2 }} />
            
            {/* في حالة التحويل الداخلي نخفي التبويب المحاسبي المعتاد ونعرض بدله حقل "إلى" */}
  {isEmployeeAccountMode ? (
  <>
    <FormControl fullWidth sx={{ mb: 2 }}>
      <InputLabel>اسم الموظف - الحساب</InputLabel>
      <Select
        value={employeeAccountName}
        label="اسم الموظف - الحساب"
        onChange={(e) => setEmployeeAccountName(e.target.value)}
      >
        {EMPLOYEE_ACCOUNTS.map((name) => (
          <MenuItem key={name} value={name}>
            {name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    <Alert severity="info" sx={{ mb: 2 }}>
      هذا القيد خاص بحساب الموظف فقط، ولا يؤثر على الصناديق أو الإكسل أو الإيميل.
    </Alert>
  </>
) : isCreditAccountMode ? (
  <>
    <FormControl fullWidth sx={{ mb: 2 }}>
      <InputLabel>نوع حركة الحساب الآجل</InputLabel>
      <Select
        value={creditAction}
        label="نوع حركة الحساب الآجل"
        onChange={(e) => {
          const value = e.target.value;
          setCreditAction(value);

          if (value === "add_invoice") {
            setManualCategory("إضافة فاتورة آجلة");
          } else if (value === "discount") {
            setManualCategory("خصم على حساب آجل");
          } else {
            setManualCategory("كلفة البضاعة المباعة");
            setWallet((prev) => prev || "baghdad");
          }
        }}
      >
        <MenuItem value="add_invoice">إضافة فاتورة</MenuItem>
        <MenuItem value="payment">تسديد حساب / دفعة على الحساب</MenuItem>
        <MenuItem value="discount">خصم على الحساب</MenuItem>
      </Select>
    </FormControl>

    <FormControl fullWidth sx={{ mb: 2 }}>
      <InputLabel>اسم الحساب</InputLabel>
      <Select
        value={creditAccountName}
        label="اسم الحساب"
        onChange={(e) => setCreditAccountName(e.target.value)}
      >
        {CREDIT_ACCOUNTS.map((name) => (
          <MenuItem key={name} value={name}>
            {name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>

{creditAction === "payment" && (
  <>
    <FormControl fullWidth sx={{ mb: 2 }}>
      <InputLabel>التبويب المحاسبي</InputLabel>
      <Select
        value={manualCategory}
        label="التبويب المحاسبي"
        onChange={(e) => setManualCategory(e.target.value)}
      >
        {EXPENSE_CATEGORIES.map((cat) => (
          <MenuItem key={cat} value={cat}>
            {cat}
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    <FormControl fullWidth sx={{ mb: 2 }}>
      <InputLabel>المصدر</InputLabel>
      <Select
        value={wallet}
        label="المصدر"
        onChange={(e) => setWallet(e.target.value)}
      >
        <MenuItem value="baghdad">كاش بغداد</MenuItem>
        <MenuItem value="erbil">كاش أربيل</MenuItem>
        <MenuItem value="zain">زين كاش</MenuItem>
        <MenuItem value="fib">FIB</MenuItem>
        <MenuItem value="qi">كي كارد</MenuItem>
      </Select>
    </FormControl>
  </>
)}

    {creditAction === "add_invoice" && (
      <Alert severity="info" sx={{ mb: 2 }}>
        إضافة الفاتورة تزيد رصيد الحساب الآجل ولا تؤثر على أي صندوق.
      </Alert>
    )}

    {creditAction === "discount" && (
      <Alert severity="info" sx={{ mb: 2 }}>
        الخصم ينقص رصيد الحساب الآجل (المبلغ المطلوب) ولا يؤثر على أي صندوق - لا تُعتبر هذه الحركة صرف فلوس.
      </Alert>
    )}
  </>
) : (!isIncome && isInternalTransfer) ? (
  <>
    <FormControl fullWidth sx={{ mb: 2 }}>
      <InputLabel>من المصدر</InputLabel>
      <Select
        value={wallet}
        label="من المصدر"
        onChange={(e) => setWallet(e.target.value)}
      >
        <MenuItem value="baghdad">كاش بغداد</MenuItem>
        <MenuItem value="erbil">كاش أربيل</MenuItem>
        <MenuItem value="zain">زين كاش</MenuItem>
        <MenuItem value="fib">FIB</MenuItem>
        <MenuItem value="qi">كي كارد</MenuItem>
      </Select>
    </FormControl>

    <FormControl fullWidth sx={{ mb: 2 }}>
      <InputLabel>إلى المصدر</InputLabel>
      <Select
        value={transferTo}
        label="إلى المصدر"
        onChange={(e) => setTransferTo(e.target.value)}
      >
        <MenuItem value="baghdad">كاش بغداد</MenuItem>
        <MenuItem value="erbil">كاش أربيل</MenuItem>
        <MenuItem value="zain">زين كاش</MenuItem>
        <MenuItem value="fib">FIB</MenuItem>
        <MenuItem value="qi">كي كارد</MenuItem>
      </Select>
    </FormControl>
  </>
) : (
  <>
    <FormControl fullWidth sx={{ mb: 2 }}>
      <InputLabel>التبويب المحاسبي</InputLabel>
      <Select
        value={manualCategory}
        label="التبويب المحاسبي"
        onChange={(e) => setManualCategory(e.target.value)}
      >
        {(isIncome ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map((cat) => (
          <MenuItem key={cat} value={cat}>
            {cat}
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    <FormControl fullWidth sx={{ mb: 2 }}>
      <InputLabel>المصدر</InputLabel>
      <Select
        value={wallet}
        label="المصدر"
        onChange={(e) => setWallet(e.target.value)}
      >
        <MenuItem value="baghdad">كاش بغداد</MenuItem>
        <MenuItem value="erbil">كاش أربيل</MenuItem>
        <MenuItem value="zain">زين كاش</MenuItem>
        <MenuItem value="fib">FIB</MenuItem>
        <MenuItem value="qi">كي كارد</MenuItem>
      </Select>
    </FormControl>
  </>
)}

{!editingId && isIncome && !isCreditAccountMode && !isEmployeeAccountMode && (
  <Box mb={2}>
    <FormControlLabel
      control={
        <Switch
          checked={showOrderLinking}
          onChange={(e) => {
            const checked = e.target.checked;
            setShowOrderLinking(checked);
            if (!checked) setLinkedOrderIds([]);
          }}
          color="secondary"
        />
      }
      label="🔗 ربط هذا القبض بطلب أو أكثر (باقي دفعة مقدمة / تحصيل شركة توصيل)"
    />

    {showOrderLinking && (
      <Box sx={{ border: "1px solid #ddd", borderRadius: 2, p: 1.5, mt: 1, maxHeight: 260, overflowY: "auto" }}>
        {outstandingOrders.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            لا توجد طلبات فيها مبلغ متبقي غير محصَّل حالياً.
          </Typography>
        ) : (
          outstandingOrders.map((o) => (
            <FormControlLabel
              key={o.id}
              sx={{ display: "flex", width: "100%", ml: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={linkedOrderIds.includes(o.id)}
                  onChange={(e) => {
                    setLinkedOrderIds((prev) =>
                      e.target.checked
                        ? [...prev, o.id]
                        : prev.filter((id) => id !== o.id)
                    );
                  }}
                />
              }
              label={
                <Typography variant="caption">
                  {o.customerName || "-"} — {getOrderRemainingAmount(o).toLocaleString()} د.ع
                  {" "}({PAYMENT_METHOD_LABELS[o.payment?.method] || o.payment?.method || "-"})
                </Typography>
              }
            />
          ))
        )}
      </Box>
    )}

    {showOrderLinking && linkedOrderIds.length > 0 && (
      <Alert
        severity={
          Number(amount) === linkedOrdersRemainingSum ? "success" : "warning"
        }
        sx={{ mt: 1 }}
      >
        مجموع الطلبات المختارة: {linkedOrdersRemainingSum.toLocaleString()} د.ع
        {Number(amount) !== linkedOrdersRemainingSum &&
          " — يختلف عن المبلغ المدخل (طبيعي إذا كانت هناك عمولة مقتطعة)."}
      </Alert>
    )}
  </Box>
)}

{isEmployeeAccountMode ? (
  <TextField
    fullWidth
    label="المبلغ"
    type="number"
    value={amount}
    onChange={(e) => setAmount(e.target.value)}
    onWheel={(e) => e.target.blur()}
    sx={{ mb: 2 }}
  />
) : (
  <Grid container spacing={1} mb={2}>
    <Grid item xs={4} sm={3}>
      <TextField
        select
        fullWidth
        label="العملة"
        value={currency}
        onChange={(e) => setCurrency(e.target.value)}
      >
        <MenuItem value="IQD">IQD</MenuItem>
        <MenuItem value="USD">$</MenuItem>
      </TextField>
    </Grid>

    <Grid item xs={8} sm={5}>
      <TextField
        fullWidth
        label="المبلغ"
        type="number"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        onWheel={(e) => e.target.blur()}
      />
    </Grid>

    <Grid item xs={12} sm={4}>
      <TextField
        fullWidth
        label="صرف"
        type="number"
        disabled={currency === "IQD"}
        value={exchangeRate}
        onChange={(e) => setExchangeRate(e.target.value)}
      />
    </Grid>
  </Grid>
)}

            {currency === "USD" && amount && <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>الإجمالي: {finalAmountIQD.toLocaleString()} IQD</Alert>}

   {!isEmployeeAccountMode && (
  <>
    <TextField
      fullWidth
      label="رقم الوصل (اختياري)"
      value={receiptNumber}
      onChange={(e) => setReceiptNumber(e.target.value)}
      sx={{ mb: 2 }}
    />

  </>
)}

            <Box display="flex" gap={1} mb={2}>
                {["بغداد", "أربيل", "عام"].map(loc => (
                  <Chip key={loc} label={loc} onClick={() => setLocation(loc)} color={location === loc ? "secondary" : "default"} />
                ))}
            </Box>

      {editingId && existingInvoiceUrls.length > 0 && (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" display="block" sx={{ mb: 1, fontWeight: "bold" }}>
      الملفات المرفوعة حاليًا:
    </Typography>

    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {existingInvoiceUrls.map((url, index) => (
        <Chip
          key={url}
          label={`ملف ${index + 1}`}
          color="info"
          variant="outlined"
          onClick={() => window.open(url, "_blank")}
          onDelete={() => removeExistingAttachment(url)}
          sx={{ mb: 1 }}
        />
      ))}
    </Stack>

    <Typography variant="caption" color="error" display="block">
      اضغطي على X لحذف الملف من هذا القيد، أو اضغطي على اسم الملف لفتحه.
    </Typography>
  </Box>
)}

<Button
  variant="outlined"
  component="label"
  fullWidth
  sx={{ mb: 1, borderStyle: 'dashed' }}
>
  {files.length > 0
    ? `✅ تم اختيار ${files.length} ملف جديد`
    : (editingId ? "إضافة صور / ملفات وصل جديدة" : "📎 ارفع صور أو ملفات الوصل (اختياري)")}
  <input
    type="file"
    hidden
    multiple
    onChange={(e) => appendPickedFiles(e, setFiles)}
  />
</Button>

{files.length > 0 && (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" display="block" sx={{ mb: 1, fontWeight: "bold" }}>
      الملفات الجديدة المختارة:
    </Typography>

    <UploadPreview files={files} onChange={setFiles} />
  </Box>
)}

{!isEmployeeAccountMode && (
  <Box
    sx={{
      mb: 2,
      p: 1.5,
      borderRadius: 2,
      bgcolor: showForBaneen ? "#e8f5e9" : "#fff3e0",
      border: "1px solid",
      borderColor: showForBaneen ? "#a5d6a7" : "#ffcc80",
    }}
  >
    <FormControlLabel
      control={
        <Checkbox
          checked={showForBaneen}
          onChange={(e) => setShowForBaneen(e.target.checked)}
          color="success"
        />
      }
      label={
        showForBaneen
          ? "إظهار هذه الحركة في جدول بنين"
          : "إخفاء هذه الحركة عن جدول بنين"
      }
    />
  </Box>
)}
            <Stack direction="row" spacing={1}>
                <Button
  fullWidth
  variant="contained"
  size="large"
  color={editingId ? "warning" : isIncome ? "success" : "error"}
  onClick={handleSave}
  disabled={loading}
  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
  sx={{
    fontWeight: "bold",
  bgcolor: !editingId && (isCreditAccountMode || isEmployeeAccountMode) ? "#6a1b9a" : undefined,
    "&:hover": {
     bgcolor: !editingId && (isCreditAccountMode || isEmployeeAccountMode) ? "#4a148c" : undefined,
    },
  }}
>
{loading
  ? "جاري الحفظ..."
  : editingId
  ? "تحديث القيد"
  : isEmployeeAccountMode
  ? "ترحيل قيد موظف"
  : isCreditAccountMode
  ? creditAction === "add_invoice"
    ? "ترحيل فاتورة آجلة"
    : creditAction === "discount"
    ? "تسجيل الخصم"
    : "تسديد حساب آجل"
  : !isIncome && isInternalTransfer
  ? "ترحيل تحويل داخلي"
  : `ترحيل ${isIncome ? "قبض" : "صرف"}`}
                </Button>
                <Button variant="outlined" onClick={cancelEdit}>إلغاء</Button>
            </Stack>
          </Paper>
    </DialogContent>
  </Dialog>

  {!isBaneen && (
  <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
    <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={2}>
      <Typography fontWeight="bold" display="flex" alignItems="center" gap={0.7}>
        🔍 فلترة سجل القيود
      </Typography>
    </Box>

    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} sm={6} md={2.5}>
        <TextField
          fullWidth
          size="small"
          label="🔍 بحث بالوصف"
          placeholder="ابحث عن كلمة بعمود الوصف..."
          value={filterDescriptionSearch}
          onChange={(e) => setFilterDescriptionSearch(e.target.value)}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={2.5}>
        <FormControl fullWidth size="small">
          <InputLabel>الحركة</InputLabel>
          <Select
            multiple
            label="الحركة"
            value={filterEntryTab}
            onChange={(e) => {
              const { value } = e.target;
              const arr = typeof value === "string" ? value.split(",") : value;
              if (arr.includes("__ALL__")) {
                setFilterEntryTab(
                  filterEntryTab.length === ENTRY_TAB_OPTIONS.length ? [] : ENTRY_TAB_OPTIONS
                );
              } else {
                setFilterEntryTab(arr);
              }
            }}
            renderValue={(selected) =>
              selected.length === 0 || selected.length === ENTRY_TAB_OPTIONS.length
                ? "الكل"
                : selected.join("، ")
            }
          >
            <MenuItem value="__ALL__">
              <Checkbox
                size="small"
                checked={filterEntryTab.length === ENTRY_TAB_OPTIONS.length}
              />
              <ListItemText primary="الكل" />
            </MenuItem>
            {ENTRY_TAB_OPTIONS.map((tab) => (
              <MenuItem key={tab} value={tab}>
                <Checkbox size="small" checked={filterEntryTab.includes(tab)} />
                <ListItemText primary={tab} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6} md={2.5}>
        <FormControl fullWidth size="small">
          <InputLabel>التبويب</InputLabel>
          <Select
            multiple
            label="التبويب"
            value={filterAccountingCategory}
            onChange={(e) => {
              const { value } = e.target;
              const arr = typeof value === "string" ? value.split(",") : value;
              if (arr.includes("__ALL__")) {
                setFilterAccountingCategory(
                  filterAccountingCategory.length === ALL_ACCOUNTING_CATEGORIES.length
                    ? []
                    : ALL_ACCOUNTING_CATEGORIES
                );
              } else {
                setFilterAccountingCategory(arr);
              }
            }}
            renderValue={(selected) =>
              selected.length === 0 || selected.length === ALL_ACCOUNTING_CATEGORIES.length
                ? "الكل"
                : selected.join("، ")
            }
          >
            <MenuItem value="__ALL__">
              <Checkbox
                size="small"
                checked={filterAccountingCategory.length === ALL_ACCOUNTING_CATEGORIES.length}
              />
              <ListItemText primary="الكل" />
            </MenuItem>
            {ALL_ACCOUNTING_CATEGORIES.map((cat) => (
              <MenuItem key={cat} value={cat}>
                <Checkbox size="small" checked={filterAccountingCategory.includes(cat)} />
                <ListItemText primary={cat} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6} md={2.5}>
        <FormControl fullWidth size="small">
          <InputLabel>الصندوق</InputLabel>
          <Select
            multiple
            label="الصندوق"
            value={filterWallet}
            onChange={(e) => {
              const { value } = e.target;
              const arr = typeof value === "string" ? value.split(",") : value;
              if (arr.includes("__ALL__")) {
                setFilterWallet(
                  filterWallet.length === WALLET_OPTIONS.length ? [] : WALLET_OPTIONS
                );
              } else {
                setFilterWallet(arr);
              }
            }}
            renderValue={(selected) =>
              selected.length === 0 || selected.length === WALLET_OPTIONS.length
                ? "الكل"
                : selected.map((w) => getWalletName(w)).join("، ")
            }
          >
            <MenuItem value="__ALL__">
              <Checkbox
                size="small"
                checked={filterWallet.length === WALLET_OPTIONS.length}
              />
              <ListItemText primary="الكل" />
            </MenuItem>
            {WALLET_OPTIONS.map((w) => (
              <MenuItem key={w} value={w}>
                <Checkbox size="small" checked={filterWallet.includes(w)} />
                <ListItemText primary={getWalletName(w)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6} md={2.5}>
        <TextField
          fullWidth
          size="small"
          type="date"
          label="من تاريخ"
          value={filterFromDate}
          onChange={(e) => setFilterFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={2.5}>
        <TextField
          fullWidth
          size="small"
          type="date"
          label="إلى تاريخ"
          value={filterToDate}
          onChange={(e) => setFilterToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={2}>
        <Button
          fullWidth
          variant="outlined"
          color="secondary"
          size="small"
          sx={{ height: 40, borderRadius: 2 }}
          onClick={() => {
            setFilterEntryTab([]);
            setFilterAccountingCategory([]);
            setFilterWallet([]);
            setFilterDescriptionSearch("");
            setFilterFromDate("");
            setFilterToDate("");
          }}
        >
          إلغاء الفلتر
        </Button>
      </Grid>
    </Grid>
  </Paper>
  )}

  <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
    {isAdmin && (
      <Box display="flex" justifyContent="flex-start" mb={2}>
        <Box sx={{ width: 200 }}>
          <StatCard
            label="الصندوق الكلي"
            value={walletStats.totalBox}
            color="#1a237e"
            onClick={() => openWalletDetails("totalBox")}
          />
        </Box>
      </Box>
    )}
    {isAdmin && (
      <Box display="flex" justifyContent="flex-start" alignItems="stretch" flexWrap="wrap" gap={2} mb={2}>
        <Box sx={{ width: 200 }}>
          <StatCard label="المبيعات الكلية" value={totalSalesAllTime} color="#2e7d32" />
        </Box>
        <Box sx={{ width: 200 }}>
          <StatCard label="الإيرادات الكلية" value={totalRevenueAll} color="#1565c0" />
        </Box>
        <Box sx={{ width: 200 }}>
          <StatCard label="المصاريف الكلية" value={totalExpenseAll} color="#c62828" />
        </Box>
      </Box>
    )}
    <Box display="flex" justifyContent="flex-start" alignItems="stretch" flexWrap="wrap" gap={2}>
      <Button
        variant="contained"
        color="primary"
        size="large"
        startIcon={<AddCircleIcon />}
        onClick={openNewEntryDialog}
        sx={{ borderRadius: 3, fontWeight: "bold" }}
      >
        إدخال جديد
      </Button>
      {isBaneen && (
        <Box>
          <Button
            variant="contained"
            size="large"
            onClick={openReconcileDialog}
            disabled={isReconcileLocked}
            sx={{ borderRadius: 3, fontWeight: "bold", background: "#6a1b9a", "&:hover": { background: "#4a148c" } }}
          >
            🔄 مطابقة الحسابات
          </Button>
          {isReconcileLocked && (
            <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.5 }}>
              🔒 مقفل لحد الساعة {reconcileUnlockAt.toLocaleTimeString("ar-IQ", { hour: "2-digit", minute: "2-digit" })} يوم {reconcileUnlockAt.toLocaleDateString("en-GB")}
            </Typography>
          )}
        </Box>
      )}
      {!isBaneen && (
        <Box sx={{ width: 200, marginInlineStart: "auto" }}>
          <Button
            fullWidth
            variant="contained"
            color="success"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportExcel}
            sx={{ height: "100%", borderRadius: 2 }}
          >
            سحب الإكسل
          </Button>
        </Box>
      )}
    </Box>
  </Paper>


  <Paper
    sx={{
      p: 2,
      borderRadius: 4,
      width: "100%",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}
  >
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
      <Typography fontWeight="bold" display="flex" alignItems="center" gap={0.7}>
        🧾 سجل القيود
      </Typography>
      <Chip size="small" label={`${filteredTableRecords.length} حركة`} />
    </Box>
 <TableContainer
  sx={{
    flex: 1,
    maxHeight: "75vh",
    minHeight: 0,
    overflowX: "auto",
    overflowY: "auto",
    width: "100%",
  }}
>
<Table
  stickyHeader
  size="small"
  sx={{
    tableLayout: "fixed",
    width: "100%",
    minWidth: 920,
      "& th, & td": {
        px: 1,
        py: 1.1,
        fontSize: "12px",
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
      },
      "& th": {
        fontWeight: 800,
        bgcolor: "#f5f6fa",
      },
    }}
  >
    <TableHead>
      <TableRow>
       <TableCell align="right" sx={{ width: "20%" }}>الوصف</TableCell>
<TableCell align="right" sx={{ width: "15%" }}>التبويب</TableCell>
<TableCell align="center" sx={{ width: "9%" }}>التاريخ</TableCell>
<TableCell align="right" sx={{ width: "11%" }}>المبلغ</TableCell>
<TableCell align="center" sx={{ width: "10%" }}>الصندوق</TableCell>
<TableCell align="right" sx={{ width: "10%" }}>رصيد الصندوق</TableCell>
<TableCell align="right" sx={{ width: "11%" }}>رصيد الصندوق الكلي</TableCell>
<TableCell align="center" sx={{ width: "7%" }}>الموقع</TableCell>
<TableCell align="center" sx={{ width: "7%" }}>الإجراءات</TableCell>
      </TableRow>
    </TableHead>

<TableBody>
 {filteredTableRecords.map((r) => {
    const walletName =
      r.wallet === "baghdad" ? "كاش بغداد" :
      r.wallet === "erbil" ? "كاش أربيل" :
      r.wallet === "zain" ? "زين كاش" :
      r.wallet === "fib" ? "FIB" :
      r.wallet === "qi" ? "كي كارد" :
      r.wallet || "-";

    const transferToName =
      r.transferTo === "baghdad" ? "كاش بغداد" :
      r.transferTo === "erbil" ? "كاش أربيل" :
      r.transferTo === "zain" ? "زين كاش" :
      r.transferTo === "fib" ? "FIB" :
      r.transferTo === "qi" ? "كي كارد" :
      r.transferTo || "-";
const creditLabel =
  r.creditAction === "add_invoice"
    ? `إضافة فاتورة آجلة - ${r.creditAccountName || r.accountName || ""}`
    : r.creditAction === "discount"
    ? `خصم - ${r.creditAccountName || r.accountName || ""}`
    : r.creditAction === "payment"
    ? `${r.accountingCategory || " كلفة البضاعة المباعة "} - ${r.creditAccountName || r.accountName || ""}`
    : "";

    // 🔄 حركة مطابقة الحسابات - تنعرض كشريط ملون يمتد على كل الصف بدل الأعمدة العادية
    if (r.isReconciliation) {
      const isMatched = r.reconciliationStatus === "matched";
      return (
        <TableRow key={r.id}>
          <TableCell colSpan={9} sx={{ p: 0 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                px: 2,
                py: 1.2,
                bgcolor: isMatched ? "#2e7d32" : "#c62828",
                color: "#fff",
              }}
            >
              <Typography fontWeight="bold" fontSize={13}>
                {r.processName}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography fontWeight="bold" fontSize={12}>
                  {r.date?.toDate ? r.date.toDate().toLocaleDateString("en-GB") : "-"}
                </Typography>
                <Typography fontWeight="bold" fontSize={12}>
                  {r.timestamp?.toDate
                    ? r.timestamp.toDate().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
                    : "-"}
                </Typography>
                {isAdmin && (
                  <IconButton
                    size="small"
                    title="حذف حركة المطابقة"
                    onClick={() => handleDelete(r)}
                    sx={{ p: 0.3, color: "#fff" }}
                  >
                    <DeleteIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    const isBaneenEntry = r.user === "Baneen@123" || r.createdBy === "Baneen@123";
    const wasEditedByAdmin = isBaneenEntry && r.lastEditedBy === "admin";

    return (
      <TableRow key={r.id} hover>
        <TableCell align="right">
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, flexWrap: "wrap" }}>
            {isBaneenEntry && (
              <Chip
                label={wasEditedByAdmin ? "B - N" : "B"}
                size="small"
                title={wasEditedByAdmin ? "أضافتها بنين - عدّلها المدير" : "أضافتها بنين"}
                sx={{
                  height: 18,
                  minWidth: 18,
                  fontSize: 10,
                  fontWeight: "bold",
                  bgcolor: "#8e24aa",
                  color: "#fff",
                  "& .MuiChip-label": { px: 0.6 },
                }}
              />
            )}
            <Typography variant="body2" fontWeight="bold">
              {r.processName}
            </Typography>
          </Box>
        </TableCell>

        <TableCell align="right">
          <Typography variant="caption" color="textSecondary">
           {r.creditAction
  ? creditLabel
  : r.isInternalTransfer
  ? `تحويل داخلي من ${walletName} إلى ${transferToName}`
  : r.accountingCategory}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="caption" fontWeight="bold">
            {r.date?.toDate
              ? r.date.toDate().toLocaleDateString("en-GB")
              : "-"}
          </Typography>
        </TableCell>

<TableCell
  align="right"
  sx={{
    fontWeight: "bold",
    color:
      r.creditAction === "add_invoice"
        ? "#6a1b9a"
        : r.creditAction === "discount"
        ? "#ef6c00"
        : r.creditAction === "payment"
        ? "red"
        : r.isInternalTransfer
        ? "#7b1fa2"
        : r.type === "قبض"
        ? "green"
        : "red",
  }}
>
  {r.amountIQD?.toLocaleString()}
</TableCell>

<TableCell align="center">
  <Chip
    label={
  r.creditAction === "add_invoice"
    ? "بدون صندوق"
    : r.creditAction === "discount"
    ? "بدون صندوق (خصم)"
    : r.creditAction === "payment"
    ? `${walletName} → ${r.creditAccountName || r.accountName}`
    : r.isInternalTransfer
    ? `${walletName} ← ${transferToName}`
    : walletName
}
    size="small"
    color={r.isInternalTransfer ? "secondary" : "default"}
    variant="outlined"
    sx={{
      maxWidth: "100%",
      fontSize: "10px",
      height: 24,
      "& .MuiChip-label": {
        px: 0.7,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    }}
  />
</TableCell>
<TableCell
  align="right"
  sx={{
    fontWeight: "bold",
    color:
      Number(r.walletBalanceAfter || 0) < 0
        ? "error.main"
        : "text.primary",
  }}
>
  {r.walletBalanceAfter === null
    ? "-"
    : Number(r.walletBalanceAfter || 0).toLocaleString()}
</TableCell>

<TableCell
  align="right"
  sx={{
    fontWeight: "bold",
    color:
      Number(r.totalBoxAfter || 0) < 0
        ? "error.main"
        : "#3f51b5",
  }}
>
  {r.totalBoxAfter === null
    ? "-"
    : Number(r.totalBoxAfter || 0).toLocaleString()}
</TableCell>
<TableCell align="center">
  <Chip
    label={r.location}
    size="small"
    variant="outlined"
    sx={{
      maxWidth: "100%",
      fontSize: "10px",
      height: 24,
      "& .MuiChip-label": {
        px: 0.7,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    }}
  />
</TableCell>

       <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.4,
      flexWrap: "nowrap",
    }}
  >
    {!isBaneen && (
      <>
        <IconButton
          size="small"
          color="primary"
          disabled={!!r.autoFromOrder}
          title={r.autoFromOrder ? "قيد تلقائي من طلب - غير قابل للتعديل" : undefined}
          onClick={() => handleEdit(r)}
          sx={{ p: 0.3 }}
        >
          <EditIcon sx={{ fontSize: 17 }} />
        </IconButton>

        <IconButton
          size="small"
          color="error"
          disabled={!!r.autoFromOrder}
          title={r.autoFromOrder ? "قيد تلقائي من طلب - غير قابل للحذف" : undefined}
          onClick={() => handleDelete(r)}
          sx={{ p: 0.3 }}
        >
          <DeleteIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </>
    )}

    {(() => {
      const attachments = r.invoiceUrls?.length
        ? r.invoiceUrls
        : r.invoiceUrl
        ? [r.invoiceUrl]
        : [];

      if (attachments.length === 0) {
        return (
          <IconButton size="small" disabled sx={{ p: 0.3 }}>
            <ImageIcon sx={{ fontSize: 17 }} />
          </IconButton>
        );
      }

      return attachments.map((url, index) => (
        <IconButton
          key={index}
          size="small"
          title={`فتح المرفق ${index + 1}`}
          onClick={() => window.open(url, "_blank")}
          sx={{ p: 0.3 }}
        >
          <ImageIcon sx={{ fontSize: 17 }} />
        </IconButton>
      ));
    })()}
  </Box>
</TableCell>
      </TableRow>
    );
  })}
</TableBody>
              </Table>
            </TableContainer>
          </Paper>

      <Dialog
  open={voucherDialogOpen}
  onClose={() => setVoucherDialogOpen(false)}
  maxWidth="sm"
  fullWidth
  dir="rtl"
>
  <DialogTitle fontWeight="bold">
    {voucherType === "قبض" ? "إنشاء سند قبض" : "إنشاء سند صرف"}
  </DialogTitle>

  <DialogContent dividers>
    <Grid container spacing={2} sx={{ mt: 0.5 }}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="رقم السند"
          type="number"
          value={voucherData.voucherNumber}
          onChange={(e) =>
            setVoucherData((prev) => ({
              ...prev,
              voucherNumber: e.target.value,
            }))
          }
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="التاريخ"
          type="date"
          value={voucherData.date}
          InputLabelProps={{ shrink: true }}
          onChange={(e) =>
            setVoucherData((prev) => ({
              ...prev,
              date: e.target.value,
            }))
          }
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label={voucherType === "قبض" ? "استلمنا من السيد / السادة" : "اسم المستفيد"}
          value={voucherData.personName}
          onChange={(e) =>
            setVoucherData((prev) => ({
              ...prev,
              personName: e.target.value,
            }))
          }
        />
      </Grid>

      <Grid item xs={12}>
      <TextField
  fullWidth
  label="المبلغ"
  value={`${Number(voucherData.amount || 0).toLocaleString()} د.ع`}
  disabled
/>
      </Grid>

      <Grid item xs={12}>
    <TextField
  fullWidth
  label="المبلغ كتابة"
  value={voucherData.amountWords}
  disabled
/>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label={voucherType === "قبض" ? "وذلك عن / سبب القبض" : "سبب الصرف / البيان"}
          value={voucherData.reason}
          onChange={(e) =>
            setVoucherData((prev) => ({
              ...prev,
              reason: e.target.value,
            }))
          }
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>طريقة الدفع</InputLabel>
          <Select
            label="طريقة الدفع"
            value={voucherData.paymentMethod}
            onChange={(e) =>
              setVoucherData((prev) => ({
                ...prev,
                paymentMethod: e.target.value,
              }))
            }
          >
            <MenuItem value="كاش">كاش</MenuItem>
            <MenuItem value="كي كارد">كي كارد</MenuItem>
            <MenuItem value="زين كاش">زين كاش</MenuItem>
            <MenuItem value="FIB">FIB</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="رقم المرجع / رقم الفاتورة"
          value={voucherData.invoiceNumber}
          onChange={(e) =>
            setVoucherData((prev) => ({
              ...prev,
              invoiceNumber: e.target.value,
            }))
          }
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label={voucherType === "قبض" ? "استلمت بواسطة" : "اسم مستلم الفلوس"}
          value={voucherData.receivedBy}
          onChange={(e) =>
            setVoucherData((prev) => ({
              ...prev,
              receivedBy: e.target.value,
            }))
          }
          helperText={
            voucherType === "قبض"
              ? "الديفولت بنين، ويمكن تغييره"
              : "في سند الصرف يجب إدخال اسم الشخص الذي استلم المبلغ"
          }
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="ملاحظات"
          value={voucherData.notes}
          onChange={(e) =>
            setVoucherData((prev) => ({
              ...prev,
              notes: e.target.value,
            }))
          }
        />
      </Grid>
    </Grid>
  </DialogContent>

  <DialogActions>
    <Button onClick={() => setVoucherDialogOpen(false)}>
      إلغاء
    </Button>

    <Button
      variant="contained"
      color={voucherType === "قبض" ? "success" : "error"}
      onClick={printVoucher}
    >
      طباعة السند
    </Button>
  </DialogActions>
</Dialog>

<Dialog
  open={creditDetailsOpen}
  onClose={() => setCreditDetailsOpen(false)}
  maxWidth="lg"
  fullWidth
  dir="rtl"
>
<DialogTitle fontWeight="bold">
  {selectedIsEmployeeAccount
    ? `تفاصيل حساب الموظف: ${selectedCreditAccount}`
    : selectedIsInvoiceCostAccount
    ? `تفاصيل حساب كلفة الفاتورة: ${getInvoiceCostAccountLabel(selectedCreditAccount)}`
    : `تفاصيل الحساب الآجل: ${selectedCreditAccount}`}
</DialogTitle>

  <DialogContent dividers>
    <Box
      sx={{
        mb: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Paper
        sx={{
          p: 1.5,
          minWidth: 220,
      bgcolor: selectedDetailsBalance > 0
  ? selectedIsInvoiceCostAccount
    ? "#fff8e1"
    : "#f3e5f5"
  : "#f5f5f5",
          borderRadius: 2,
        }}
        variant="outlined"
      >
        <Typography fontSize={13} fontWeight="bold">
          الرصيد الحالي
        </Typography>

        <Typography
          fontSize={20}
          fontWeight="900"
          sx={{
            color: selectedDetailsBalance > 0
  ? selectedIsInvoiceCostAccount
    ? "#ef6c00"
    : "#6a1b9a"
  : "text.secondary",
          }}
        >
          {Number(selectedDetailsBalance || 0).toLocaleString()} د.ع
        </Typography>
      </Paper>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        {showSelectedDetailsAmountTotal && (
          <Paper
            sx={{ p: 1.5, minWidth: 220, bgcolor: "#f3e5f5", borderRadius: 2 }}
            variant="outlined"
          >
            <Typography fontSize={13} fontWeight="bold">
              مجموع مبلغ إضافة فاتورة آجلة
            </Typography>

            <Typography
              fontSize={20}
              fontWeight="900"
              sx={{ color: "#6a1b9a" }}
            >
              {selectedDetailsAmountTotal.toLocaleString()} د.ع
            </Typography>
          </Paper>
        )}

        <Button
          variant="contained"
          color="success"
          startIcon={<FileDownloadIcon />}
          onClick={exportSelectedCreditAccountExcel}
        >
          سحب Excel
        </Button>
      </Box>
    </Box>

    <TableContainer sx={{ maxHeight: 500, overflowX: "auto" }}>
      <Table
        stickyHeader
        size="small"
        sx={{
          tableLayout: "fixed",
          width: "100%",
          minWidth: 700,
          "& th, & td": {
            px: 0.8,
            py: 1,
            fontSize: "12px",
            whiteSpace: "normal",
            wordBreak: "break-word",
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell align="right" sx={{ width: "22%" }}>الوصف</TableCell>
            <TableCell align="right" sx={{ width: "20%" }}>التبويب</TableCell>
            <TableCell align="center" sx={{ width: "11%" }}>التاريخ</TableCell>
            <TableCell align="right" sx={{ width: "12%" }}>المبلغ</TableCell>
            <TableCell align="center" sx={{ width: "14%" }}>الصندوق</TableCell>
            <TableCell align="center" sx={{ width: "8%" }}>الموقع</TableCell>
            <TableCell align="center" sx={{ width: "13%" }}>الملفات</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {selectedDetailsRecords.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center">
                لا توجد قيود لهذا الحساب
              </TableCell>
            </TableRow>
          ) : (
            selectedDetailsRecords.map((r) => {
              const walletName = getWalletName(r.wallet);

              const attachments = r.invoiceUrls?.length
                ? r.invoiceUrls
                : r.invoiceUrl
                ? [r.invoiceUrl]
                : [];

              return (
                <TableRow key={r.id} hover>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {r.processName || "-"}
                    </Typography>

                  <Typography variant="caption" color="text.secondary">
  {r.isEmployeeAccount
    ? "إضافة فاتورة موظف"
    : r.isInvoiceCostAccount
    ? "كلفة فاتورة"
    : getCreditRecordLabel(r)}
</Typography>
                  </TableCell>

             <TableCell align="right">
  <Typography variant="caption" color="text.secondary">
 {r.isEmployeeAccount
  ? ""
  : r.isInvoiceCostAccount
  ? ""
  : r.creditAction === "add_invoice"
  ? "إضافة فاتورة آجلة"
  : r.accountingCategory || "كلفة البضاعة المباعة"}
  </Typography>
</TableCell>

                  <TableCell align="center">
                    <Typography variant="caption" fontWeight="bold">
                      {getRecordDateText(r)}
                    </Typography>
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                color: r.isEmployeeAccount
  ? "#6a1b9a"
  : r.isInvoiceCostAccount
  ? "#ef6c00"
  : r.creditAction === "add_invoice"
  ? "#6a1b9a"
  : r.creditAction === "discount"
  ? "#ef6c00"
  : "#d32f2f",
                    }}
                  >
                    {Number(r.amountIQD || 0).toLocaleString()}
                  </TableCell>

                  <TableCell align="center">
                    <Chip
      label={
  r.isEmployeeAccount || r.isInvoiceCostAccount
    ? "-"
    : r.creditAction === "add_invoice" || r.creditAction === "discount"
    ? "بدون صندوق"
    : `${walletName} → ${r.creditAccountName || r.accountName}`
}
                      size="small"
                      variant="outlined"
                      sx={{
                        maxWidth: "100%",
                        fontSize: "10px",
                        height: 24,
                        "& .MuiChip-label": {
                          px: 0.7,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                    />
                  </TableCell>

                  <TableCell align="center">
                    <Chip
                     label={r.isEmployeeAccount || r.isInvoiceCostAccount ? "-" : r.location || "-"}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "10px",
                        height: 24,
                      }}
                    />
                  </TableCell>

                  <TableCell align="center">
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 0.4,
                        flexWrap: "wrap",
                      }}
                    >
                      {!isBaneen && !r.isInvoiceCostAccount && (
                        <IconButton
                          size="small"
                          color="error"
                          disabled={!!r.autoFromOrder}
                          title={r.autoFromOrder ? "قيد تلقائي من طلب - غير قابل للحذف" : "حذف هذا القيد"}
                          onClick={() => handleDelete(r)}
                          sx={{ p: 0.3 }}
                        >
                          <DeleteIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      )}

                      {attachments.length === 0 ? (
                        <IconButton size="small" disabled>
                          <ImageIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      ) : (
                        attachments.map((url, index) => (
                          <IconButton
                            key={index}
                            size="small"
                            title={`فتح المرفق ${index + 1}`}
                            onClick={() => window.open(url, "_blank")}
                            sx={{ p: 0.3 }}
                          >
                            <ImageIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        ))
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </DialogContent>

  <DialogActions>
    <Button onClick={() => setCreditDetailsOpen(false)}>
      إغلاق
    </Button>
  </DialogActions>
</Dialog>
<Dialog
  open={expenseDetailsOpen}
  onClose={() => setExpenseDetailsOpen(false)}
  maxWidth="lg"
  fullWidth
  dir="rtl"
>
  <DialogTitle fontWeight="bold">
    تفاصيل المصروف: {selectedExpenseBox?.label || "-"}
  </DialogTitle>

  <DialogContent dividers>
    <Box
      sx={{
        mb: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          minWidth: 220,
          bgcolor: "#f3e5f5",
          borderRadius: 2,
        }}
      >
        <Typography fontSize={13} fontWeight="bold">
          إجمالي المصروف
        </Typography>

        <Typography
          fontSize={20}
          fontWeight="900"
          sx={{ color: "#6a1b9a" }}
        >
          {Number(selectedExpenseTotal || 0).toLocaleString()} د.ع
        </Typography>
      </Paper>

      <Button
        variant="contained"
        color="success"
        startIcon={<FileDownloadIcon />}
        onClick={exportSelectedExpenseExcel}
      >
        سحب Excel
      </Button>
    </Box>

    <TableContainer sx={{ maxHeight: 500, overflowX: "auto" }}>
      <Table
        stickyHeader
        size="small"
        sx={{
          tableLayout: "fixed",
          width: "100%",
          minWidth: 700,
          "& th, & td": {
            px: 0.8,
            py: 1,
            fontSize: "12px",
            whiteSpace: "normal",
            wordBreak: "break-word",
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell align="right" sx={{ width: "24%" }}>الوصف</TableCell>
            <TableCell align="right" sx={{ width: "20%" }}>التبويب</TableCell>
            <TableCell align="center" sx={{ width: "11%" }}>التاريخ</TableCell>
            <TableCell align="right" sx={{ width: "12%" }}>المبلغ</TableCell>
            <TableCell align="center" sx={{ width: "14%" }}>الصندوق</TableCell>
            <TableCell align="center" sx={{ width: "8%" }}>الموقع</TableCell>
            <TableCell align="center" sx={{ width: "11%" }}>الملفات</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {selectedExpenseRecords.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center">
                لا توجد قيود لهذا البند
              </TableCell>
            </TableRow>
          ) : (
            selectedExpenseRecords.map((r) => {
              const walletName = getWalletName(r.wallet);
              const attachments = getRecordAttachments(r);

              return (
                <TableRow key={r.id} hover>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {r.processName || "-"}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography variant="caption" color="text.secondary">
                      {r.accountingCategory || "-"}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Typography variant="caption" fontWeight="bold">
                      {getRecordDateText(r)}
                    </Typography>
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{ fontWeight: "bold", color: "#d32f2f" }}
                  >
                    {Number(r.amountIQD || 0).toLocaleString()}
                  </TableCell>

                  <TableCell align="center">
                    <Chip
                      label={walletName}
                      size="small"
                      variant="outlined"
                      sx={{
                        maxWidth: "100%",
                        fontSize: "10px",
                        height: 24,
                        "& .MuiChip-label": {
                          px: 0.7,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                    />
                  </TableCell>

                  <TableCell align="center">
                    <Chip
                      label={r.location || "-"}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "10px", height: 24 }}
                    />
                  </TableCell>

                  <TableCell align="center">
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 0.4,
                        flexWrap: "wrap",
                      }}
                    >
                      {attachments.length === 0 ? (
                        <IconButton size="small" disabled>
                          <ImageIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      ) : (
                        attachments.map((url, index) => (
                          <IconButton
                            key={index}
                            size="small"
                            title={`فتح المرفق ${index + 1}`}
                            onClick={() => window.open(url, "_blank")}
                            sx={{ p: 0.3 }}
                          >
                            <ImageIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        ))
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </DialogContent>

  <DialogActions>
    <Button onClick={() => setExpenseDetailsOpen(false)}>
      إغلاق
    </Button>
  </DialogActions>
</Dialog>
<Dialog
  open={walletDetailsOpen}
  onClose={() => setWalletDetailsOpen(false)}
  maxWidth="lg"
  fullWidth
  dir="rtl"
>
  <DialogTitle fontWeight="bold">
    تفاصيل الصندوق: {WALLET_LABELS[selectedWalletKey] || "-"}
  </DialogTitle>

  <DialogContent dividers>
    <Box
      sx={{
        mb: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          minWidth: 220,
          borderRadius: 2,
          bgcolor: "#eef2ff",
        }}
      >
        <Typography fontSize={13} fontWeight="bold">
          الرصيد الحالي
        </Typography>

        <Typography
          fontSize={20}
          fontWeight="900"
          sx={{ color: "#3f51b5" }}
        >
          {Number(walletStats[selectedWalletKey] || 0).toLocaleString()} د.ع
        </Typography>
      </Paper>

      <Button
        variant="contained"
        color="success"
        startIcon={<FileDownloadIcon />}
        onClick={exportSelectedWalletExcel}
      >
        سحب Excel
      </Button>
    </Box>

    <TableContainer sx={{ maxHeight: 520, overflowX: "auto" }}>
      <Table
        stickyHeader
        size="small"
        sx={{
          tableLayout: "fixed",
          width: "100%",
          minWidth: 700,
          "& th, & td": {
            px: 0.8,
            py: 1,
            fontSize: "12px",
            whiteSpace: "normal",
            wordBreak: "break-word",
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell align="right" sx={{ width: "23%" }}>الوصف</TableCell>
            <TableCell align="right" sx={{ width: "20%" }}>التبويب</TableCell>
            <TableCell align="center" sx={{ width: "11%" }}>التاريخ</TableCell>
            <TableCell align="right" sx={{ width: "12%" }}>المبلغ</TableCell>
            <TableCell align="center" sx={{ width: "10%" }}>الحركة</TableCell>
            <TableCell align="center" sx={{ width: "12%" }}>الصندوق</TableCell>
            <TableCell align="center" sx={{ width: "7%" }}>الموقع</TableCell>
            <TableCell align="center" sx={{ width: "5%" }}>ملف</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {selectedWalletDetailsRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                لا توجد حركات لهذا الصندوق
              </TableCell>
            </TableRow>
          ) : (
            selectedWalletDetailsRows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    {r.description || "-"}
                  </Typography>
                </TableCell>

                <TableCell align="right">
                  <Typography variant="caption" color="text.secondary">
                    {r.category || "-"}
                  </Typography>
                </TableCell>

                <TableCell align="center">
                  <Typography variant="caption" fontWeight="bold">
                    {r.date ? r.date.toLocaleDateString("en-GB") : "-"}
                  </Typography>
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    fontWeight: "bold",
                    color: Number(r.amount || 0) >= 0 ? "green" : "red",
                  }}
                >
                  {Math.abs(Number(r.amount || 0)).toLocaleString()}
                </TableCell>

                <TableCell align="center">
                  <Chip
                    label={Number(r.amount || 0) >= 0 ? "قبض" : "صرف"}
                    color={Number(r.amount || 0) >= 0 ? "success" : "error"}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>

                <TableCell align="center">
                  <Chip
                    label={r.walletLabel || "-"}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "10px", height: 24 }}
                  />
                </TableCell>

                <TableCell align="center">
                  <Chip
                    label={r.location || "-"}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "10px", height: 24 }}
                  />
                </TableCell>

                <TableCell align="center">
                  {r.fileUrls?.length > 0 ? (
                    r.fileUrls.map((url, index) => (
                      <IconButton
                        key={index}
                        size="small"
                        onClick={() => window.open(url, "_blank")}
                        sx={{ p: 0.3 }}
                      >
                        <ImageIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    ))
                  ) : (
                    <IconButton size="small" disabled sx={{ p: 0.3 }}>
                      <ImageIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </DialogContent>

  <DialogActions>
    <Button onClick={() => setWalletDetailsOpen(false)}>
      إغلاق
    </Button>
  </DialogActions>
</Dialog>

<Dialog
  open={reconcileDialogOpen}
  onClose={() => setReconcileDialogOpen(false)}
  maxWidth="xs"
  fullWidth
  dir="rtl"
>
  <DialogTitle fontWeight="bold">🔄 مطابقة الحسابات</DialogTitle>

  <DialogContent>
    {reconcileMode !== "unmatched" ? (
      <Box sx={{ display: "flex", gap: 2, justifyContent: "center", py: 2 }}>
        <Button
          variant="contained"
          color="success"
          size="large"
          disabled={reconcileSaving}
          onClick={() => saveReconciliation("matched")}
        >
          ✅ مطابق
        </Button>

        <Button
          variant="contained"
          color="error"
          size="large"
          onClick={() => setReconcileMode("unmatched")}
        >
          ❌ غير مطابق
        </Button>
      </Box>
    ) : (
      <Box sx={{ py: 1 }}>
        <Typography fontSize={13} color="text.secondary" sx={{ mb: 1.5 }}>
          اكتبي السبب اللي خلى الحساب غير مطابق:
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          placeholder="السبب..."
          value={reconcileReason}
          onChange={(e) => setReconcileReason(e.target.value)}
        />
      </Box>
    )}
  </DialogContent>

  <DialogActions>
    <Button onClick={() => setReconcileDialogOpen(false)} disabled={reconcileSaving}>
      إلغاء
    </Button>

    {reconcileMode === "unmatched" && (
      <Button
        variant="contained"
        color="error"
        disabled={!reconcileReason.trim() || reconcileSaving}
        onClick={() => saveReconciliation("unmatched", reconcileReason.trim())}
      >
        حفظ
      </Button>
    )}
  </DialogActions>
</Dialog>
    </Container>
  );
};

export default SmartAccountingSystem;